<template>
    <div ref="workspace" class="kp-variant-sifter-workspace">
        <header
            ref="vksHeader"
            class="vks-header"
            :class="{ 'is-pinned': chromePinned }"
            :style="pinnedChromeStyle.header"
        >
            <div class="vks-header-start">
                <div class="vks-brand">
                    <span class="vks-mark">GP2</span>
                    <span class="vks-title">Browser</span>
                </div>
                <GP2BrowserMenuBar
                    :recent-searches="recentSearches"
                    @action="onMenuAction"
                />
            </div>
            <GP2BrowserSessionParamBubbles
                v-if="searchSession"
                :search-session="searchSession"
                :project-id="projectId"
                :phenotypes-in-use="phenotypes"
                :selected-phenotypes="associationsState.selectedPhenotypes || []"
                :utils="utilsBox"
                :bio-index-host="bioIndexHostFor('gene')"
                @apply-search="onSessionParamApply"
                @remove-phenotype="onRemoveAssociationPhenotype"
            />
            <GP2BrowserViewportControls
                v-if="canvasActive"
                class="vks-header-controls"
                :viewing-region-width-bp="viewingRegionWidthBp"
                :data-table-open="dataTableOpen"
                :ai-assistant-open="aiAssistantOpen"
                :settings-open="settingsOpen"
                @update:viewingRegionWidthBp="onViewingRegionWidthUpdate"
                @update:dataTableOpen="dataTableOpen = $event"
                @toggle-assistant="aiAssistantOpen = !aiAssistantOpen"
                @toggle-settings="settingsOpen = !settingsOpen"
            />
        </header>
        <div
            v-show="chromePinned"
            class="vks-header-spacer"
            :style="{ height: `${headerHeightPx}px` }"
            aria-hidden="true"
        ></div>

        <VariantSifterWorkspaceGuide
            v-if="canvasActive"
            :open="workspaceGuideOpen"
            @close="workspaceGuideOpen = false"
        />

        <input
            ref="sessionImportInput"
            type="file"
            accept=".json,application/json"
            class="vks-session-import-input"
            @change="onSessionFileSelected"
        />

        <div class="vks-main">
            <div class="vks-stage">
                <GP2BrowserCanvas
                    :sections="sections"
                    :visible-section-ids="visibleSectionIds"
                    :canvas-active="canvasActive"
                    :welcome-open="welcomeOpen"
                    :welcome-title="'Welcome to GP2 Browser'"
                    :phenotypes="phenotypes"
                    :utils="utilsBox"
                    :welcome-initial-values="welcomeInitialValues"
                    :project-id="projectId"
                    :gene-lookup-bio-index-host="bioIndexHostFor('gene')"
                    :tissue-regions-host="bioIndexHostFor('tissue-regions')"
                    :search-session="searchSession"
                    :region-zoom="regionZoom"
                    :region-shift-bp="regionShiftBp"
                    :region-view-area="regionViewArea"
                    :view-region="viewRegion"
                    :active-search-region="activeRegion"
                    :viewing-center-bp="effectiveViewingCenterBp"
                    :viewing-region-half-width-bp="viewingRegionHalfWidthBp"
                    :data-table-open="dataTableOpen"
                    :mapping-state="mappingState"
                    :workspace-mapping-filter="workspaceMappingFilter"
                    :associations-state="associationsState"
                    :genes-state="genesState"
                    :qtl-state="qtlState"
                    :plot-overlays-state="plotOverlaysState"
                    :plot-markers="plotMarkersState"
                    :credible-sets-state="credibleSetsState"
                    :credible-set-colors="credibleSetDotColors"
                    :credible-set-pill-colors="credibleSetPillColors"
                    :global-enrichment-state="globalEnrichmentState"
                    :v2g-state="v2gState"
                    :s2g-state="s2gState"
                    :region-load-progress-active="regionLoadProgress.active"
                    @update:regionShiftBp="onRegionShiftBpUpdate"
                    @update:regionViewArea="onRegionViewAreaUpdate"
                    @update:viewingCenterBp="onViewingCenterBpUpdate"
                    @commit-viewing-region="onCommitViewingRegion"
                    @pan-end="onRegionPanEnd"
                    @toggle-position-marker="onTogglePositionMarker"
                    @toggle-star-variant="onToggleStarVariant"
                    @toggle-qtl-star-variant="onToggleQtlStarVariant"
                    @set-reference-variant="onSetReferenceVariant"
                    @update:associationsFiltersIndex="onAssociationsFiltersIndexUpdate"
                    @update:geSelectedBiosamples="onGeSelectedBiosamplesUpdate"
                    @update:geActiveAnnotation="onGeActiveAnnotationUpdate"
                    @update:geSelectedTissues="onGeSelectedTissuesUpdate"
                    @update:geSelectedAnnotations="onGeSelectedAnnotationsUpdate"
                    @select-ge-plot-tissue="onSelectGePlotTissue"
                    @update:geBiosampleFilterOptions="onGeBiosampleFilterOptionsUpdate"
                    @update:geBiosampleTissueRegions="onGeBiosampleTissueRegionsUpdate"
                    @update:geBiosampleLoading="onGeBiosampleLoadingUpdate"
                    @update:mappingState="onMappingStateUpdate"
                    @update:workspaceFilterActive="onWorkspaceFilterActiveUpdate"
                    @update:v2gSelectedLinks="onV2gSelectedLinksUpdate"
                    @update:s2gSelectedLinks="onS2gSelectedLinksUpdate"
                    @add-credible-set="onAddCredibleSet"
                    @remove-credible-set="onRemoveCredibleSet"
                    @remove-mapping-category="onRemoveMappingCategory"
                    @close-data-table="dataTableOpen = false"
                    @start-search="onStartSearch"
                    @import-session="openSessionImport"
                />
                <GP2BrowserAiAssistantPanel
                    :open="aiAssistantOpen"
                    :active-tab="assistantState.activeTab"
                    :thread-entries="assistantState.threadEntries"
                    :executing="assistantState.executing"
                    :execution-progress-label="assistantState.executionProgressLabel"
                    :llm-available="assistantState.llmAvailable"
                    :can-run-actions="assistantCanRunActions"
                    :plan="assistantState.plan"
                    :step-states="assistantState.stepStates"
                    :cs2ct-star-prompt="assistantState.cs2ctStarPrompt"
                    :understudied-star-prompt="assistantState.understudiedStarPrompt"
                    :panel-style="assistantPanelStyle"
                    @close="aiAssistantOpen = false"
                    @update:activeTab="onAssistantActiveTabUpdate"
                    @execute-all="onAssistantExecuteAll"
                    @execute-step="onAssistantExecuteStep"
                    @plan-request="onAssistantPlanRequest"
                    @execute-catalog-action="onExecuteCatalogAction"
                    @confirm-cs2ct-star="onConfirmCs2ctStar"
                    @dismiss-cs2ct-star="onDismissCs2ctStar"
                    @confirm-understudied-star="onConfirmUnderstudiedStar"
                    @dismiss-understudied-star="onDismissUnderstudiedStar"
                    @open-correlated-phenotype="onOpenCorrelatedPhenotype"
                />
            </div>
            <div v-if="canvasActive" class="vks-drawer-rail-slot">
                <GP2BrowserSectionDrawers
                    :sections="visibleSections"
                    :open-drawer-id="openDrawerId"
                    :search-session="searchSession"
                    :project-id="projectId"
                    :associations-state="associationsState"
                    :plot-overlays-state="plotOverlaysState"
                    :plot-markers="plotMarkersState"
                    :credible-sets-state="credibleSetsState"
                    :credible-set-pill-colors="credibleSetPillColors"
                    :genes-state="genesState"
                    :qtl-state="qtlState"
                    :global-enrichment-state="globalEnrichmentState"
                    :v2g-state="v2gState"
                    :s2g-state="s2gState"
                    :mapping-state="mappingState"
                    :workspace-mapping-filter="workspaceMappingFilter"
                    :view-region="viewRegion"
                    :utils="utilsBox"
                    :region-load-progress-active="regionLoadProgress.active"
                    :rail-pinned="chromePinned"
                    :rail-pin-style="pinnedChromeStyle.drawer"
                    @toggle-drawer="onToggleDrawer"
                    @update:associationsFiltersIndex="onAssociationsFiltersIndexUpdate"
                    @toggle-star-variant="onToggleStarVariant"
                    @set-reference-variant="onSetReferenceVariant"
                    @toggle-association-ancestry="onToggleAssociationAncestry"
                    @add-credible-set="onAddCredibleSet"
                    @remove-credible-set="onRemoveCredibleSet"
                    @update:credibleSetsPanelFilters="onCredibleSetsPanelFiltersUpdate"
                    @update:genesSelectedTypes="onGenesSelectedTypesUpdate"
                    @update:qtlSelectedTypes="onQtlSelectedTypesUpdate"
                    @update:qtlSelectedTissues="onQtlSelectedTissuesUpdate"
                    @update:qtlSelectedGenes="onQtlSelectedGenesUpdate"
                    @update:qtlPValueMax="onQtlPValueMaxUpdate"
                    @toggle-qtl-star-variant="onToggleQtlStarVariant"
                    @update:geEnabledMutedAnnotations="onGeEnabledMutedAnnotationsUpdate"
                    @update:geEnabledMutedAnnotationTissues="
                        onGeEnabledMutedAnnotationTissuesUpdate
                    "
                    @update:geDisabledAnnotationTissues="
                        onGeDisabledAnnotationTissuesUpdate
                    "
                    @update:geSelectedAnnotations="onGeSelectedAnnotationsUpdate"
                    @update:geTissueTrackSort="onGeTissueTrackSortUpdate"
                    @update:geTrackPValueMax="onGeTrackPValueMaxUpdate"
                    @update:geSelectedMethods="onGeSelectedMethodsUpdate"
                    @update:geSelectedSources="onGeSelectedSourcesUpdate"
                    @update:mappingState="onMappingStateUpdate"
                    @update:workspaceFilterActive="onWorkspaceFilterActiveUpdate"
                    @remove-mapping-category="onRemoveMappingCategory"
                    @update:v2gSelectedTissues="onV2gSelectedTissuesUpdate"
                    @update:v2gDeselectedMethods="onV2gDeselectedMethodsUpdate"
                    @update:v2gDeselectedGenes="onV2gDeselectedGenesUpdate"
                    @update:v2gDeselectedTissues="onV2gDeselectedTissuesUpdate"
                    @update:v2gDeselectedBiosamples="onV2gDeselectedBiosamplesUpdate"
                    @update:v2gSelectedLinks="onV2gSelectedLinksUpdate"
                    @update:v2gViewMode="onV2gViewModeUpdate"
                    @load-s2g="onS2gLoad"
                    @clear-s2g="onS2gClear"
                    @update:s2gDeselectedMethods="onS2gDeselectedMethodsUpdate"
                    @update:s2gDeselectedGenes="onS2gDeselectedGenesUpdate"
                    @update:s2gSelectedLinks="onS2gSelectedLinksUpdate"
                />
            </div>
        </div>

        <VariantSifterRegionLoadBubble
            :progress="regionLoadProgress"
            @dismiss="dismissRegionLoadProgress"
        />

        <VariantSifterExportSessionModal
            :open="exportSessionOpen"
            :default-filename="exportSessionDefaultFilename"
            :summary="exportSessionSummary"
            :exporting="exportSessionBusy"
            @close="exportSessionOpen = false"
            @export="onExportSessionConfirm"
        />

        <GP2BrowserSettingsPanel
            :open="settingsOpen"
            :sections="sections"
            :visible-section-ids="visibleSectionIds"
            :search-session="searchSession"
            :bio-index-host="bioIndexHost"
            :default-bio-index-host="defaultBioIndexHost"
            :project-id="projectId"
            :resolve-host-for-index="bioIndexHostFor"
            :tool-name="'GP2 Browser'"
            :suggest-actions-after-user-action="suggestActionsAfterUserAction"
            :refetch-associations-on-zoom="refetchAssociationsOnZoom"
            @close="settingsOpen = false"
            @update:visibleSectionIds="onVisibleSectionIdsUpdate"
            @update:projectId="onProjectIdUpdate"
            @update:suggestActionsAfterUserAction="onSuggestActionsAfterUserActionUpdate"
            @update:refetchAssociationsOnZoom="onRefetchAssociationsOnZoomUpdate"
        />

        <GP2BrowserResearchModal
            :panel-id="activeResearchPanelId"
            @close="activeResearchPanelId = null"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";

import GP2BrowserMenuBar from "./gp2Browser/GP2BrowserMenuBar.vue";
import GP2BrowserResearchModal from "./gp2Browser/GP2BrowserResearchModal.vue";
import GP2BrowserSectionDrawers from "./gp2Browser/GP2BrowserSectionDrawers.vue";
import GP2BrowserCanvas from "./gp2Browser/GP2BrowserCanvas.vue";
import GP2BrowserViewportControls from "./gp2Browser/GP2BrowserViewportControls.vue";
import GP2BrowserAiAssistantPanel from "./gp2Browser/GP2BrowserAiAssistantPanel.vue";
import GP2BrowserSettingsPanel from "./gp2Browser/GP2BrowserSettingsPanel.vue";
import {
    GP2_ASSOCIATIONS_BIOINDEX_HOST,
    GP2_BROWSER_PHENOTYPES,
    GP2_BROWSER_SECTIONS,
    gp2DefaultVisibleSectionIds,
    gp2NormalizeVisibleSectionIds,
} from "./gp2Browser/gp2BrowserSections.js";
import {
    fetchGp2QtlAssociations,
    gp2QtlLoadKey,
} from "./gp2Browser/gp2BrowserQtlApi.js";
import GP2BrowserSessionParamBubbles from "./gp2Browser/GP2BrowserSessionParamBubbles.vue";
import VariantSifterWorkspaceGuide from "./kpVariantSifter/VariantSifterWorkspaceGuide.vue";
import VariantSifterExportSessionModal from "./kpVariantSifter/VariantSifterExportSessionModal.vue";
import VariantSifterRegionLoadBubble from "./kpVariantSifter/VariantSifterRegionLoadBubble.vue";
import {
    isSectionVisible,
} from "./kpVariantSifter/variantSifterToolSettings.js";
import { parseRegionParam, formatRegion, formatSearchSessionLabel, formatSubAncestriesParam, parseSubAncestriesParam, formatPhenotypesParam, parsePhenotypesParam } from "./kpVariantSifter/variantSifterSearchUtils.js";
import {
    associationRowAncestry,
    associationRowPhenotype,
    ancestrySeriesLoadingKey,
    fetchAssociations,
    fetchGwasCeAssociations,
    filterAssociationRowsByProject,
    primaryAssociationAncestry,
    probeAncestryAssociationAvailability,
    fetchGlobalAssociations,
} from "./kpVariantSifter/variantSifterAssociationsApi.js";
import { formatAssociationRows } from "./kpVariantSifter/variantSifterAssociationsTable.js";
import {
    downsampleAssociationRowsOnLoad,
    mergeZoomedAssociationRows,
    stripZoomRefetchedRows,
} from "./gp2Browser/gp2BrowserAssociationsDownsample.js";
import { createFiltersIndex, applyAssociationsFilters } from "./kpVariantSifter/variantSifterAssociationsFilters.js";
import {
    GP2_GENOME_BUILD,
    applyLatestGp2LdScoreMap,
    enrichAssociationRowsWithLdScores,
    enrichAssociationRowsWithLdScoresForRef,
    fetchLdScoreMapForRefRow,
    pickLeadVariantRow,
    resolveLdReferenceRow,
    rowToLdVariant,
} from "./gp2Browser/gp2BrowserLdServer.js";
import {
    GP2_VIEWING_REGION_WIDTH_BP,
    clampViewingCenterBp,
    computeGp2ViewingRegion,
    extractVariantPositionBp,
} from "./gp2Browser/gp2BrowserViewingRegion.js";
import {
    emptyPlotMarkersState,
    createStarredVariant,
    isVariantStarred,
    togglePositionMarker,
    toggleStarredVariant,
} from "./kpVariantSifter/variantSifterPlotMarkers.js";
import {
    buildSessionExportFilename,
    countActiveAssociationFilters,
    exportVariantSifterSession,
    importVariantSifterSession,
    readSessionFile,
    saveJsonBundle,
} from "./kpVariantSifter/variantSifterSession.js";
import {
    clampRegionZoom,
    clampRegionViewArea,
    sliderValueFromZoom,
    zoomFromSliderValue,
    VKS_REGION_ZOOM_SLIDER_MAX,
    VKS_REGION_ZOOM_SLIDER_MIN,
} from "./kpVariantSifter/variantSifterRegionZoom.js";
import {
    cloneGenomicRegion,
    computeActiveRegion,
    computeFetchGaps,
    computeViewRegion,
    clampRegionViewportForDataLimit,
    filterAssociationRowsInRegion,
    filterGenesInRegion,
    genomicRegionsEqual,
    isRegionZoomOutBlocked,
    mergeAssociationRowsByVariantAndAncestry,
    mergeGenesByName,
    mergeRecombData,
    resolveActiveDataRegion,
    resolveZoomOutLimitRegion,
    trimRecombData,
    unionGenomicRegions,
    activeRegionTrimmedMessage,
    ensureRegionWithinActiveDataLimit,
} from "./kpVariantSifter/variantSifterRegionPan.js";
import {
    fetchGp2GenesTrackData as fetchGenesTrackData,
    fetchGp2RecombinationRate as fetchRecombinationRate,
} from "./gp2Browser/gp2BrowserGenesAndRecomb.js";
import {
    fetchGlobalEnrichment,
    fetchLocusAnnotations,
    mergeGeRowsByPhenotype,
    removeGeRowsForPhenotype,
} from "./kpVariantSifter/variantSifterGlobalEnrichmentApi.js";
import { fetchGeneLinks } from "./kpVariantSifter/variantSifterV2gApi.js";
import { fetchVariantLinks } from "./kpVariantSifter/variantSifterS2gApi.js";
import {
    collectGenesFromTissueData,
    collectMethodsFromTissueData,
    emptyV2gState,
    normalizeV2gViewMode,
} from "./kpVariantSifter/variantSifterV2gData.js";
import {
    buildS2gTissueData,
    emptyS2gState,
    hasS2gTrackData,
    VKS_S2G_TISSUE_LABEL,
} from "./kpVariantSifter/variantSifterS2gData.js";
import {
    applyGlobalEnrichmentAnnoRows,
    buildAnnoDataFromRows,
    buildGeLlmLoadingState,
    buildGeLlmRelevanceShowAllState,
    emptyGeLlmRelevanceState,
    extractGeCatalog,
    filterAnnoRowsInRegion,
    mergeAnnoRows,
    normalizeGeFilterStringList,
    normalizeGeSelectedBiosamples,
    normalizeGeTissueTrackSort,
    normalizeGeTrackPValueMax,
    resolveSelectedTissuesByAnnotation,
    selectedTissuesForAnnotation,
    setSelectedTissuesForAnnotation,
    upsertGeBiosampleTissueRegions,
} from "./kpVariantSifter/variantSifterGlobalEnrichmentData.js";
import {
    emptyMappingState,
    normalizeMappingState,
    emptyWorkspaceMappingFilter,
    normalizeWorkspaceMappingFilter,
    buildWorkspaceMappingFilter,
    collectMappingCategories,
    parseMappingCategoryId,
} from "./kpVariantSifter/variantSifterMappingData.js";
import {
    isGwasCeProject,
    normalizeProjectId,
    projectAssociationsOnly,
    projectPhenotypes,
    resolveGwasCeToken,
    resolveProjectBioIndexHost,
    resolveProjectPrimaryBioIndexHost,
    VKS_ASSOCIATION_PROJECT_GWAS_CE,
    VKS_ASSOCIATION_PROJECT_KP,
    VKS_PROJECT_DEFAULT_ID,
} from "./kpVariantSifter/variantSifterProjects.js";
import {
    loadRecentSearches,
    pushRecentSearch,
} from "./kpVariantSifter/variantSifterRecentSearches.js";
import { exportVariantSifterHtmlReport } from "./kpVariantSifter/variantSifterHtmlReport.js";
import { normalizeV2gSelectedLinks } from "./kpVariantSifter/variantSifterV2gData.js";
import { fetchInteractiveLlmHealth } from "./kpVariantSifter/variantSifterGeRelevanceLlm.js";
import {
    buildCs2ctStarPromptMessage,
    buildGeRelevanceIntroMessage,
    buildGeRelevanceOfferMessage,
    buildGeRelevanceReportMessage,
    buildGeRelevanceRunningMessage,
} from "./kpVariantSifter/variantSifterAssistantGeRelevance.js";
import {
    buildUnderstudiedIntroMessage,
    buildUnderstudiedNoneFoundMessage,
    buildUnderstudiedOfferMessage,
    buildUnderstudiedReportMessage,
    buildUnderstudiedRunningMessage,
    buildUnderstudiedStarPrompt,
    filterUnderstudiedBottomLineInRegion,
} from "./kpVariantSifter/variantSifterAssistantUnderstudied.js";
import {
    buildGeneticCorrelationIntroMessage,
    buildGeneticCorrelationPhenotypeGroups,
    buildGeneticCorrelationResultEntry,
    buildGeneticCorrelationRunningMessage,
    fetchGeneticCorrelation,
    filterSignificantGeneticCorrelations,
} from "./kpVariantSifter/variantSifterAssistantGeneticCorrelation.js";
import { runCs2ctTissueClassification } from "./kpVariantSifter/variantSifterCs2ctClassify.js";
import {
    appendAssistantEntries,
    createAssistantMessage,
    createAssistantPlan,
    createAssistantStatusMessage,
    createAssistantStepMessage,
    createUserMessage,
    emptyAssistantState,
    replacePendingAssistantEntry,
    clearAssistantResultEntries,
} from "./kpVariantSifter/variantSifterAssistantConversation.js";
import {
    findAssistantAction,
    matchVksAssistantRequest,
} from "./kpVariantSifter/variantSifterAssistantActionCatalog.js";
import {
    fetchCredibleSetsList,
    fetchCredibleSetsListForAncestries,
    fetchCredibleSetVariants,
    fetchGwasCeCredibleSetsList,
    fetchGwasCeCredibleSetVariants,
    isGwasCeCredibleSetEntry,
    mergeCredibleSetAvailableLists,
    tagCredibleSetEntries,
} from "./kpVariantSifter/variantSifterCredibleSetsApi.js";
import {
    credibleSetOptionLabel,
    credibleSetShortLabel,
    formatCredibleVariantPhenotypeDisplay,
    formatCredibleVariantRows,
    makeCredibleSetSelectionKey,
    parseCredibleSetSelectionKey,
} from "./kpVariantSifter/variantSifterCredibleSetsFormat.js";
import { buildCredibleSetColorMap } from "./kpVariantSifter/variantSifterCredibleSetsColors.js";
import { pruneCredibleSetsForRegion } from "./kpVariantSifter/variantSifterCredibleSetsRegion.js";
import {
    cloneCredibleSetsPanelFilters,
    createCredibleSetsPanelFilters,
} from "./kpVariantSifter/variantSifterCredibleSetsFilters.js";
import {
    normalizeSelectedGeneTypes,
    resolveSelectedGeneTypesForData,
    VKS_ALL_GENE_TYPES,
    VKS_DEFAULT_GENE_TYPES,
} from "./kpVariantSifter/variantSifterGenesFilter.js";
import {
    completeDeferredPhenotypeSeriesSteps,
    emptyRegionLoadProgress,
    finishRegionLoadProgress,
    patchRegionLoadStep,
    regionLoadProgressSettled,
    regionLoadProgressCanAutoDismiss,
    startRegionLoadProgress,
    VKS_REGION_LOAD_STATUS,
} from "./kpVariantSifter/variantSifterRegionLoadProgress.js";
import "./kpVariantSifter/vksSharedStyles.css";

const GP2_REGION_LOAD_STEP_CATALOG = [
    { id: "associations", label: "Association data" },
    { id: "genes", label: "Genes track" },
    { id: "recomb", label: "Recombination overlay" },
    { id: "credibleSets", label: "Credible sets list" },
    { id: "globalEnrichment", label: "Global enrichment" },
    { id: "geneLinks", label: "Gene links" },
    { id: "snp2geneLinks", label: "SNP 2 gene links" },
    { id: "ld", label: "LD scores" },
];

function startGp2RegionLoadProgress(stepIds = null) {
    const hasFilter = Array.isArray(stepIds);
    const allowed = hasFilter ? new Set(stepIds) : null;
    const steps = GP2_REGION_LOAD_STEP_CATALOG.filter((step) =>
        allowed ? allowed.has(step.id) : true
    ).map((step) => ({
        ...step,
        status: VKS_REGION_LOAD_STATUS.PENDING,
    }));
    if (!steps.length) {
        return emptyRegionLoadProgress();
    }
    return {
        active: true,
        steps,
    };
}

function emptyAssociationsState() {
    return {
        loading: false,
        ldLoading: false,
        error: null,
        ldError: null,
        rows: [],
        index: null,
        query: null,
        filtersIndex: createFiltersIndex(),
        ancestryAvailability: [],
        ancestryAvailabilityByPhenotype: {},
        ancestryAvailabilityLoading: false,
        ancestryAvailabilityLoadingByPhenotype: {},
        ancestryAvailabilityError: null,
        ancestryAvailabilityErrorByPhenotype: {},
        selectedAncestries: [],
        ancestrySeriesLoading: {},
        ancestrySeriesErrors: {},
        selectedPhenotypes: [],
        phenotypeSeriesLoading: {},
        phenotypeSeriesErrors: {},
    };
}

function emptyGenesState(selectedTypes = [VKS_ALL_GENE_TYPES]) {
    return {
        ready: false,
        loading: false,
        error: null,
        data: null,
        selectedTypes: normalizeSelectedGeneTypes(selectedTypes),
    };
}

function emptyQtlState(previous = {}) {
    return {
        loading: false,
        error: null,
        rawRows: [],
        downsampledRows: [],
        selectedTypes: Array.isArray(previous.selectedTypes)
            ? previous.selectedTypes.slice()
            : [],
        selectedTissues: Array.isArray(previous.selectedTissues)
            ? previous.selectedTissues.slice()
            : [],
        selectedGenes: Array.isArray(previous.selectedGenes)
            ? previous.selectedGenes.slice()
            : [],
        pValueMax: previous.pValueMax != null ? String(previous.pValueMax) : "",
        loadedRegionKey: null,
    };
}

function emptyPlotOverlaysState() {
    return {
        ready: false,
        loading: false,
        error: null,
        recombData: null,
        refVariant: null,
        refVariantUserSet: false,
    };
}

function emptyCredibleSetsState() {
    return {
        listLoading: false,
        listError: null,
        available: [],
        selectedIds: [],
        variantsBySet: {},
        variantsLoading: false,
        variantsError: null,
        panelFilters: createCredibleSetsPanelFilters(),
    };
}

function emptyGlobalEnrichmentState() {
    return {
        loading: false,
        error: null,
        geRows: [],
        annoRows: [],
        annoData: {},
        catalog: { annotations: [], tissues: [], pairCount: 0 },
        llmRelevance: emptyGeLlmRelevanceState(),
        enabledMutedAnnotations: [],
        enabledMutedAnnotationTissues: {},
        disabledAnnotationTissues: {},
        selectedAnnotations: [],
        tissueTrackSort: "alphabetical",
        geTrackPValueMax: 0.5,
        selectedMethods: null,
        selectedSources: null,
        activeAnnotation: null,
        selectedTissues: [],
        selectedTissuesByAnnotation: {},
        selectedBiosamples: [],
        biosampleMethodOptions: [],
        biosampleSourceOptions: [],
        biosampleRegionsByAnnotation: {},
        biosampleLoading: false,
    };
}

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

export default Vue.component("gp2-browser", {
    props: ["sectionConfigs", "phenotypesInUse", "utilsBox"],
    components: {
        GP2BrowserMenuBar,
        GP2BrowserResearchModal,
        GP2BrowserSectionDrawers,
        GP2BrowserCanvas,
        GP2BrowserViewportControls,
        GP2BrowserAiAssistantPanel,
        GP2BrowserSettingsPanel,
        GP2BrowserSessionParamBubbles,
        VariantSifterWorkspaceGuide,
        VariantSifterExportSessionModal,
        VariantSifterRegionLoadBubble,
    },
    data() {
        return {
            sections: GP2_BROWSER_SECTIONS,
            visibleSectionIds: gp2DefaultVisibleSectionIds(),
            settingsOpen: false,
            suggestActionsAfterUserAction: false,
            refetchAssociationsOnZoom: true,
            canvasActive: false,
            welcomeOpen: true,
            searchSession: null,
            welcomeInitialValues: null,
            projectId: VKS_PROJECT_DEFAULT_ID,
            recentSearches: loadRecentSearches(),
            regionZoom: 0,
            regionZoomOut: 0,
            regionViewArea: 0,
            regionShiftBp: 0,
            viewingCenterBp: null,
            viewingRegionWidthBp: GP2_VIEWING_REGION_WIDTH_BP,
            dataRegion: null,
            regionLoadProgress: emptyRegionLoadProgress(),
            regionLoadDismissTimer: null,
            regionExtendToken: 0,
            regionPanSyncTimer: null,
            zoomViewAreaRefetchTimer: null,
            pendingPanSliderReset: false,
            dataTableOpen: false,
            mappingState: emptyMappingState(),
            workspaceMappingFilter: emptyWorkspaceMappingFilter(),
            aiAssistantOpen: false,
            assistantState: emptyAssistantState(),
            assistantActionToken: 0,
            openDrawerId: null,
            associationsState: emptyAssociationsState(),
            genesState: emptyGenesState(),
            qtlState: emptyQtlState(),
            qtlRequestToken: 0,
            plotOverlaysState: emptyPlotOverlaysState(),
            plotMarkersState: emptyPlotMarkersState(),
            credibleSetsState: emptyCredibleSetsState(),
            globalEnrichmentState: emptyGlobalEnrichmentState(),
            v2gState: emptyV2gState(),
            s2gState: emptyS2gState(),
            associationsRequestToken: 0,
            zoomAssociationsRequestToken: 0,
            genesRequestToken: 0,
            plotOverlaysRequestToken: 0,
            credibleSetsRequestToken: 0,
            globalEnrichmentRequestToken: 0,
            v2gRequestToken: 0,
            s2gRequestToken: 0,
            pendingSubAncestries: [],
            pendingPhenotypes: [],
            lastAssociationsRegion: null,
            lastZoomedAssociationsRegion: null,
            lastGenesRegion: null,
            lastQtlRegion: null,
            lastCredibleSetsListRegion: null,
            lastGlobalEnrichmentRegion: null,
            lastV2gCatalogRegion: null,
            lastS2gRegion: null,
            exportSessionOpen: false,
            exportSessionBusy: false,
            workspaceGuideOpen: false,
            activeResearchPanelId: null,
            chromePinned: false,
            headerHeightPx: 53,
            assistantPanelTopPx: 53,
            pinnedChromeStyle: {
                header: {},
                drawer: {},
            },
        };
    },
    computed: {
        assistantPanelStyle() {
            return {
                top: `${this.assistantPanelTopPx}px`,
                bottom: "30px",
                height: "auto",
            };
        },
        phenotypes() {
            return projectPhenotypes(this.projectId, GP2_BROWSER_PHENOTYPES);
        },
        defaultBioIndexHost() {
            return this.utilsBox?.uiUtils?.biDomain?.() || "";
        },
        bioIndexHost() {
            if (!this.projectId || this.projectId === VKS_PROJECT_DEFAULT_ID) {
                return GP2_ASSOCIATIONS_BIOINDEX_HOST;
            }
            return resolveProjectPrimaryBioIndexHost(
                this.projectId,
                this.defaultBioIndexHost
            );
        },
        visibleSections() {
            return this.sections.filter((section) =>
                isSectionVisible(this.visibleSectionIds, section.id)
            );
        },
        exportSessionDefaultFilename() {
            if (!this.searchSession) {
                return "vks-session-export.json";
            }
            return buildSessionExportFilename(this.searchSession, {
                projectId: this.projectId,
            });
        },
        exportSessionSummary() {
            const rows = this.associationsState?.rows?.length || 0;
            if (!rows) {
                return "";
            }
            const filterCount = countActiveAssociationFilters(
                this.associationsState.filtersIndex
            );
            const geneCount = this.genesState?.data?.length || 0;
            const parts = [`${rows.toLocaleString()} association rows`];
            const extraPhenotypes =
                (this.associationsState?.selectedPhenotypes || []).length;
            if (extraPhenotypes > 0) {
                parts.push(
                    extraPhenotypes === 1
                        ? "1 additional phenotype"
                        : `${extraPhenotypes} additional phenotypes`
                );
            }
            if (geneCount > 0) {
                parts.push(`${geneCount.toLocaleString()} genes`);
            }
            if (filterCount) {
                parts.push(
                    filterCount === 1 ? "1 active filter" : `${filterCount} active filters`
                );
            }
            const gePairs = this.globalEnrichmentState?.catalog?.pairCount || 0;
            if (gePairs > 0) {
                parts.push(`${gePairs.toLocaleString()} GE pairs`);
            }
            const v2gTissues = (this.v2gState?.selectedTissues || []).length;
            if (v2gTissues > 0) {
                parts.push(
                    v2gTissues === 1 ? "1 V2G tissue" : `${v2gTissues} V2G tissues`
                );
            }
            if (hasS2gTrackData(this.s2gState)) {
                parts.push("SNP 2 gene links");
            }
            return parts.join(" · ");
        },
        searchSessionLabel() {
            return formatSearchSessionLabel(this.searchSession, {
                projectId: this.projectId,
            });
        },
        // kept for export/report titles; header uses SessionParamBubbles
        assistantCanRunActions() {
            return Boolean(this.searchSession || this.canvasActive);
        },
        activeRegion() {
            if (!this.searchSession?.region) {
                return null;
            }
            return resolveActiveDataRegion(
                this.searchSession.region,
                this.regionShiftBp,
                this.regionZoomOut,
                this.zoomOutLimitRegion
            );
        },
        zoomOutLimitRegion() {
            if (!this.searchSession?.region) {
                return null;
            }
            return resolveZoomOutLimitRegion(
                this.searchSession.region,
                this.searchSession.regionExpandBp
            );
        },
        zoomOutAtLimit() {
            if (!this.searchSession?.region) {
                return true;
            }
            return isRegionZoomOutBlocked(
                this.searchSession.region,
                this.regionShiftBp,
                this.regionZoomOut,
                this.zoomOutLimitRegion
            );
        },
        viewingRegionHalfWidthBp() {
            return Math.round((Number(this.viewingRegionWidthBp) || GP2_VIEWING_REGION_WIDTH_BP) / 2);
        },
        effectiveViewingCenterBp() {
            const searchRegion = this.activeRegion || this.searchSession?.region;
            if (!searchRegion) {
                return null;
            }
            if (this.viewingCenterBp != null) {
                return clampViewingCenterBp(
                    this.viewingCenterBp,
                    searchRegion,
                    this.viewingRegionHalfWidthBp
                );
            }
            const refPos =
                extractVariantPositionBp(this.plotOverlaysState?.refVariant) ??
                extractVariantPositionBp(
                    pickLeadVariantRow(this.associationsState?.rows)
                );
            return clampViewingCenterBp(refPos, searchRegion, this.viewingRegionHalfWidthBp);
        },
        viewRegion() {
            const searchRegion = this.activeRegion || this.searchSession?.region;
            if (!searchRegion) {
                return null;
            }
            return computeGp2ViewingRegion(
                searchRegion,
                this.effectiveViewingCenterBp,
                this.viewingRegionHalfWidthBp
            );
        },
        credibleSetDotColors() {
            return buildCredibleSetColorMap(
                this.credibleSetsState.available,
                this.credibleSetsState.selectedIds
            ).dotMap;
        },
        credibleSetPillColors() {
            return buildCredibleSetColorMap(
                this.credibleSetsState.available,
                this.credibleSetsState.selectedIds
            ).pillMap;
        },
    },
    mounted() {
        this.applyProjectFromUrl();
        this.applyUrlSearchParams();
        this.$nextTick(() => this.setupChromePin());
        this.refreshAssistantLlmHealth();
    },
    beforeDestroy() {
        if (this.regionPanSyncTimer) {
            clearTimeout(this.regionPanSyncTimer);
            this.regionPanSyncTimer = null;
        }
        if (this.zoomViewAreaRefetchTimer) {
            clearTimeout(this.zoomViewAreaRefetchTimer);
            this.zoomViewAreaRefetchTimer = null;
        }
        this.teardownChromePin();
        if (this.regionLoadDismissTimer) {
            clearTimeout(this.regionLoadDismissTimer);
            this.regionLoadDismissTimer = null;
        }
    },
    watch: {
        phenotypes() {
            this.applyProjectFromUrl();
            this.applyUrlSearchParams();
        },
        canvasActive() {
            this.$nextTick(() => {
                this.refreshChromePinListeners();
                this.updateChromePin();
            });
        },
    },
    methods: {
        bioIndexHostFor(index) {
            if (
                (!this.projectId || this.projectId === VKS_PROJECT_DEFAULT_ID) &&
                String(index || "") === "associations"
            ) {
                return GP2_ASSOCIATIONS_BIOINDEX_HOST;
            }
            return resolveProjectBioIndexHost(
                index,
                this.projectId,
                this.defaultBioIndexHost
            );
        },
        /**
         * Fetch GWAS-CE token associations as an additive overlay (Project=GWAS-CE).
         * Soft-fails to [] when token/project is missing or the request errors.
         */
        async fetchFormattedGwasCeAssociationRows(
            session,
            region = null,
            { downsample = true } = {}
        ) {
            if (!isGwasCeProject(this.projectId) || !resolveGwasCeToken(session)) {
                return [];
            }
            try {
                const result = await fetchGwasCeAssociations(
                    region ? { ...session, region } : session,
                    { downsample }
                );
                return formatAssociationRows(result.rows, session, {
                    project: VKS_ASSOCIATION_PROJECT_GWAS_CE,
                });
            } catch (error) {
                console.warn("GP2 Browser GWAS-CE associations load failed", error);
                return [];
            }
        },
        /**
         * Enrich KP and GWAS-CE association rows separately so CE indels do not
         * block LD for the KP series (and vice versa).
         */
        async enrichAssociationRowsByProject(rows, session, region = null) {
            const list = Array.isArray(rows) ? rows : [];
            if (!list.length || !session) {
                return { rows: list, refVariant: null };
            }
            const kpRows = filterAssociationRowsByProject(
                list,
                VKS_ASSOCIATION_PROJECT_KP
            );
            const ceRows = filterAssociationRowsByProject(
                list,
                VKS_ASSOCIATION_PROJECT_GWAS_CE
            );
            const activeRegion = region || session.region;
            let enriched = [];
            let refVariant = null;

            if (kpRows.length) {
                const kpResult = await enrichAssociationRowsWithLdScores(
                    kpRows,
                    session,
                    activeRegion
                );
                enriched = enriched.concat(kpResult.rows);
                refVariant = kpResult.refVariant;
            }
            if (ceRows.length) {
                const ceResult = await enrichAssociationRowsWithLdScores(
                    ceRows,
                    session,
                    activeRegion
                );
                enriched = enriched.concat(ceResult.rows);
                if (!refVariant) {
                    refVariant = ceResult.refVariant;
                }
            }

            // Preserve any rows that lack Project (legacy snapshots).
            if (!kpRows.length && !ceRows.length) {
                return enrichAssociationRowsWithLdScores(list, session, activeRegion);
            }

            return { rows: enriched, refVariant };
        },
        applyProjectFromUrl() {
            const params = this.utilsBox?.keyParams;
            if (!params) {
                return;
            }
            const next = normalizeProjectId(params.project);
            if (next === this.projectId) {
                return;
            }
            if (this.searchSession || this.canvasActive) {
                return;
            }
            this.projectId = next;
        },
        syncUrlProjectParam() {
            if (!this.utilsBox?.keyParams) {
                return;
            }
            this.utilsBox.keyParams.set({
                project: this.projectId || undefined,
            });
        },
        onProjectIdUpdate(projectId) {
            const next = normalizeProjectId(projectId);
            if (next === this.projectId) {
                return;
            }
            const hadSession = Boolean(this.searchSession || this.canvasActive);
            this.projectId = next;
            this.syncUrlProjectParam();
            if (hadSession) {
                this.resetSearch();
            }
        },
        scheduleChromePinUpdate() {
            if (this.chromePinFrame) {
                return;
            }
            this.chromePinFrame = requestAnimationFrame(() => {
                this.chromePinFrame = null;
                this.updateChromePin();
            });
        },
        collectChromePinScrollTargets() {
            const targets = new Set([window]);
            const workspace = this.$refs.workspace;
            let node = workspace?.parentElement;

            while (node) {
                const style = window.getComputedStyle(node);
                const overflow = `${style.overflow} ${style.overflowY} ${style.overflowX}`;
                if (/(auto|scroll|overlay)/.test(overflow)) {
                    targets.add(node);
                }
                if (node === document.body || node === document.documentElement) {
                    break;
                }
                node = node.parentElement;
            }

            return Array.from(targets);
        },
        refreshChromePinListeners() {
            this.teardownChromePinListeners();
            this.onChromePinScroll = () => this.scheduleChromePinUpdate();
            this.chromePinScrollTargets = this.collectChromePinScrollTargets();
            this.chromePinScrollTargets.forEach((target) => {
                target.addEventListener("scroll", this.onChromePinScroll, { passive: true });
            });
            window.addEventListener("resize", this.onChromePinScroll, { passive: true });
        },
        setupChromePin() {
            const workspace = this.$refs.workspace;
            if (!workspace) {
                return;
            }

            this.refreshChromePinListeners();

            if (typeof IntersectionObserver !== "undefined") {
                this.chromePinObserver = new IntersectionObserver(
                    () => this.scheduleChromePinUpdate(),
                    { threshold: [0, 1] }
                );
                this.chromePinObserver.observe(workspace);
            }

            this.updateChromePin();
        },
        teardownChromePinListeners() {
            if (this.chromePinScrollTargets?.length && this.onChromePinScroll) {
                this.chromePinScrollTargets.forEach((target) => {
                    target.removeEventListener("scroll", this.onChromePinScroll);
                });
            }
            if (this.onChromePinScroll) {
                window.removeEventListener("resize", this.onChromePinScroll);
            }
            this.chromePinScrollTargets = [];
            this.onChromePinScroll = null;
            if (this.chromePinFrame) {
                cancelAnimationFrame(this.chromePinFrame);
                this.chromePinFrame = null;
            }
        },
        teardownChromePin() {
            this.teardownChromePinListeners();
            if (this.chromePinObserver) {
                this.chromePinObserver.disconnect();
                this.chromePinObserver = null;
            }
        },
        buildPinnedChromeStyle(workspaceRect, headerHeight) {
            const tabWidth = 30;
            return {
                header: {
                    position: "fixed",
                    top: "0px",
                    left: `${workspaceRect.left}px`,
                    width: `${workspaceRect.width}px`,
                    zIndex: 1000,
                },
                drawer: {
                    position: "fixed",
                    top: `${headerHeight}px`,
                    left: `${workspaceRect.right - tabWidth}px`,
                    width: `${tabWidth}px`,
                    height: `calc(100vh - ${headerHeight}px)`,
                    zIndex: 999,
                },
            };
        },
        updateChromePin() {
            const workspace = this.$refs.workspace;
            const header = this.$refs.vksHeader;
            if (!header) {
                return;
            }

            const headerHeight = header.offsetHeight || 53;
            this.headerHeightPx = headerHeight;
            const headerRect = header.getBoundingClientRect();
            this.assistantPanelTopPx = Math.max(
                0,
                Math.round(headerRect.bottom)
            );

            if (!workspace) {
                return;
            }

            const workspaceRect = workspace.getBoundingClientRect();

            const shouldPin =
                this.canvasActive &&
                workspaceRect.top <= 0 &&
                workspaceRect.bottom > headerHeight + 48;

            if (shouldPin) {
                this.chromePinned = true;
                this.pinnedChromeStyle = this.buildPinnedChromeStyle(
                    workspaceRect,
                    headerHeight
                );
                return;
            }

            this.chromePinned = false;
            this.pinnedChromeStyle = {
                header: {},
                drawer: {},
            };
        },
        onToggleDrawer(sectionId) {
            this.openDrawerId = this.openDrawerId === sectionId ? null : sectionId;
        },
        onQtlSelectedTypesUpdate(types) {
            this.qtlState = {
                ...this.qtlState,
                selectedTypes: Array.isArray(types) ? types.slice() : [],
            };
            this.maybeLoadQtlEvidences();
        },
        onQtlSelectedTissuesUpdate(tissues) {
            this.qtlState = {
                ...this.qtlState,
                selectedTissues: Array.isArray(tissues) ? tissues.slice() : [],
            };
            this.maybeLoadQtlEvidences();
        },
        onQtlPValueMaxUpdate(pValueMax) {
            this.qtlState = {
                ...this.qtlState,
                pValueMax: pValueMax != null ? String(pValueMax) : "",
            };
        },
        onQtlSelectedGenesUpdate(genes) {
            this.qtlState = {
                ...this.qtlState,
                selectedGenes: Array.isArray(genes) ? genes.slice() : [],
            };
        },
        /**
         * QTL data is deliberately NOT preloaded with the rest of the
         * searching-region sections — fetching `qtl-associations` for every
         * type/tissue combination up front doesn't scale as more types and
         * tissues are added. Instead, loading only starts once the user has
         * checked at least one type and one tissue in the QTL evidences
         * drawer, and only if the current searching region isn't already
         * loaded or loading.
         */
        maybeLoadQtlEvidences() {
            if (!this.searchSession?.region) {
                return;
            }
            const hasSelection =
                (this.qtlState?.selectedTypes || []).length &&
                (this.qtlState?.selectedTissues || []).length;
            if (!hasSelection || this.qtlState.loading) {
                return;
            }
            const targetRegion = cloneGenomicRegion(
                this.dataRegion || this.searchSession.region
            );
            if (genomicRegionsEqual(this.lastQtlRegion, targetRegion)) {
                return;
            }
            this.loadQtlEvidencesForSession({
                ...this.searchSession,
                region: targetRegion,
            });
        },
        /**
         * Load raw `qtl-associations` rows for the whole searching region
         * (unfiltered by type/tissue/p-value — the drawer and plot filter
         * client-side). Mirrors `loadGenesTrack`.
         */
        async loadQtlEvidencesForSession(session) {
            const region = session?.region;
            if (!region?.chr || region.start == null || region.end == null) {
                return false;
            }
            const key = gp2QtlLoadKey(region);
            const token = ++this.qtlRequestToken;
            this.qtlState = {
                ...this.qtlState,
                loading: true,
                error: null,
            };
            try {
                const rawRows = await fetchGp2QtlAssociations(region);
                if (token !== this.qtlRequestToken) {
                    return false;
                }
                this.qtlState = {
                    ...this.qtlState,
                    loading: false,
                    error: null,
                    rawRows,
                    downsampledRows: downsampleAssociationRowsOnLoad(rawRows),
                    loadedRegionKey: key,
                };
                this.lastQtlRegion = cloneGenomicRegion(region);
                return true;
            } catch (error) {
                if (token !== this.qtlRequestToken) {
                    return false;
                }
                console.warn("GP2 Browser QTL associations load failed", error);
                this.qtlState = {
                    ...emptyQtlState(this.qtlState),
                    error: "QTL associations could not be loaded.",
                };
                return false;
            }
        },
        onRegionZoomUpdate(nextZoom) {
            this.pendingPanSliderReset = false;
            this.regionZoom = clampRegionZoom(nextZoom);
            if (this.regionZoom > 0) {
                this.regionZoomOut = 0;
            } else {
                this.restoreZoom0AssociationRows();
            }
        },
        onRegionZoomOutUpdate(nextZoomOut) {
            this.pendingPanSliderReset = false;
            const viewport = clampRegionViewportForDataLimit(
                this.searchSession?.region,
                {
                    regionShiftBp: this.regionShiftBp,
                    regionZoomOut: nextZoomOut,
                },
                this.zoomOutLimitRegion
            );
            this.regionZoomOut = viewport.regionZoomOut;
            this.regionShiftBp = viewport.regionShiftBp;
            if (this.regionZoomOut > 0) {
                this.regionZoom = 0;
                this.regionViewArea = 0;
                this.restoreZoom0AssociationRows();
            }
        },
        onRegionZoomSliderCommit() {
            if (this.regionZoom > 0) {
                this.refetchAssociationsForZoomedView();
                return;
            }
            this.restoreZoom0AssociationRows();
            this.scheduleRegionDataSync(0);
        },
        onViewingCenterBpUpdate(nextCenterBp) {
            const searchRegion = this.activeRegion || this.searchSession?.region;
            const clamped = clampViewingCenterBp(
                nextCenterBp,
                searchRegion,
                this.viewingRegionHalfWidthBp
            );
            if (clamped == null || clamped === this.viewingCenterBp) {
                return;
            }
            this.viewingCenterBp = clamped;
            if (this.refetchAssociationsOnZoom) {
                this.scheduleZoomedAssociationsRefetch(180);
            }
        },
        onCommitViewingRegion() {
            if (this.refetchAssociationsOnZoom) {
                this.scheduleZoomedAssociationsRefetch(0);
            }
        },
        onViewingRegionWidthUpdate(nextWidthBp) {
            const width = Number(nextWidthBp);
            if (!Number.isFinite(width) || width <= 0) {
                return;
            }
            this.viewingRegionWidthBp = width;
        },
        onRegionViewAreaUpdate(nextViewArea) {
            const clamped = clampRegionViewArea(nextViewArea);
            const changed = clamped !== this.regionViewArea;
            this.regionViewArea = clamped;
            if (changed && this.refetchAssociationsOnZoom) {
                this.scheduleZoomedAssociationsRefetch(180);
            }
        },
        scheduleZoomedAssociationsRefetch(delayMs = 180) {
            if (this.zoomViewAreaRefetchTimer) {
                clearTimeout(this.zoomViewAreaRefetchTimer);
                this.zoomViewAreaRefetchTimer = null;
            }
            if (delayMs <= 0) {
                this.refetchAssociationsForZoomedView();
                return;
            }
            this.zoomViewAreaRefetchTimer = setTimeout(() => {
                this.zoomViewAreaRefetchTimer = null;
                this.refetchAssociationsForZoomedView();
            }, delayMs);
        },
        resetRegionViewport() {
            this.regionZoom = 0;
            this.regionZoomOut = 0;
            this.regionViewArea = 0;
            this.regionShiftBp = 0;
            this.viewingCenterBp = null;
            this.restoreZoom0AssociationRows();
            if (this.searchSession?.region) {
                this.applyDataRegion(this.activeRegion);
            }
        },
        onRegionShiftBpUpdate(nextShiftBp) {
            // Locus translate + gap fetch only when not zoomed in.
            if (this.regionZoom > 0) {
                return;
            }
            const viewport = clampRegionViewportForDataLimit(
                this.searchSession?.region,
                {
                    regionShiftBp: nextShiftBp,
                    regionZoomOut: this.regionZoomOut,
                },
                this.zoomOutLimitRegion
            );
            if (viewport.regionShiftBp !== this.regionShiftBp) {
                this.pendingPanSliderReset = true;
            }
            this.regionShiftBp = viewport.regionShiftBp;
            this.regionZoomOut = viewport.regionZoomOut;
        },
        finishRegionExtendSync() {
            // Region zoom-out/pan can extend the searching region well beyond
            // what QTL has loaded; unlike Associations/Genes this isn't
            // gap-filled incrementally — just refetch the (now wider) region
            // in one shot if a type+tissue selection is already active.
            this.maybeLoadQtlEvidences();

            const hadZoomOut = this.regionZoomOut > 0;
            const hadPanPendingReset = this.pendingPanSliderReset;

            if (!hadZoomOut && !hadPanPendingReset) {
                return;
            }

            const activeRegion = this.activeRegion;
            if (hadZoomOut && activeRegion && this.searchSession?.region) {
                this.searchSession = {
                    ...this.searchSession,
                    region: cloneGenomicRegion(activeRegion),
                    regionLabel: formatRegion(activeRegion),
                };
                this.regionShiftBp = 0;
                this.syncUrlSearchParams(this.searchSession);
            }

            // Zoom-out commit / unzoomed locus pan: collapse slider to identity.
            // Do not clear zoom-in; Hand pan while zoomed never reaches here.
            this.regionZoom = 0;
            this.regionZoomOut = 0;
            this.regionViewArea = 0;
            this.pendingPanSliderReset = false;
        },
        onRegionPanEnd() {
            if (this.regionZoom > 0) {
                this.scheduleZoomedAssociationsRefetch(0);
                return;
            }
            this.scheduleRegionDataSync(0);
        },
        restoreZoom0AssociationRows() {
            if (this.zoomViewAreaRefetchTimer) {
                clearTimeout(this.zoomViewAreaRefetchTimer);
                this.zoomViewAreaRefetchTimer = null;
            }
            this.zoomAssociationsRequestToken += 1;
            this.lastZoomedAssociationsRegion = null;
            const cleaned = stripZoomRefetchedRows(this.associationsState.rows);
            if (cleaned !== this.associationsState.rows) {
                this.associationsState = {
                    ...this.associationsState,
                    rows: cleaned,
                };
            }
        },
        onRefetchAssociationsOnZoomUpdate(enabled) {
            this.refetchAssociationsOnZoom = Boolean(enabled);
            if (!this.refetchAssociationsOnZoom) {
                this.restoreZoom0AssociationRows();
            } else {
                this.refetchAssociationsForZoomedView();
            }
        },
        async refetchAssociationsForZoomedView() {
            if (
                !this.refetchAssociationsOnZoom ||
                !this.searchSession?.region ||
                !this.viewRegion ||
                !this.isSectionChecked("associations")
            ) {
                if (!this.refetchAssociationsOnZoom) {
                    this.restoreZoom0AssociationRows();
                }
                return;
            }
            const zoomRegion = cloneGenomicRegion(this.viewRegion);
            if (genomicRegionsEqual(this.lastZoomedAssociationsRegion, zoomRegion)) {
                return;
            }
            const token = ++this.zoomAssociationsRequestToken;
            const associationsHost = this.bioIndexHostFor("associations");
            const zoomSession = {
                ...this.searchSession,
                region: zoomRegion,
            };
            const primaryAncestry = primaryAssociationAncestry(this.searchSession);
            let zoomedRows = [];

            try {
                if (this.searchSession?.phenotype?.name) {
                    const primaryResult = await fetchAssociations(
                        zoomSession,
                        associationsHost,
                        this.projectId,
                        { downsample: false }
                    );
                    if (token !== this.zoomAssociationsRequestToken) {
                        return;
                    }
                    zoomedRows = formatAssociationRows(primaryResult.rows, zoomSession, {
                        project: VKS_ASSOCIATION_PROJECT_KP,
                    });
                }

                const ceRows = await this.fetchFormattedGwasCeAssociationRows(
                    this.searchSession,
                    zoomRegion,
                    { downsample: false }
                );
                if (token !== this.zoomAssociationsRequestToken) {
                    return;
                }
                if (ceRows.length) {
                    zoomedRows = mergeAssociationRowsByVariantAndAncestry(
                        zoomedRows,
                        ceRows,
                        primaryAncestry
                    );
                }

                for (const ancestry of this.associationsState.selectedAncestries || []) {
                    if (token !== this.zoomAssociationsRequestToken) {
                        return;
                    }
                    if (!ancestry || ancestry === primaryAncestry) {
                        continue;
                    }
                    try {
                        const ancestrySession = {
                            ...zoomSession,
                            ancestry,
                        };
                        const ancestryResult = await fetchAssociations(
                            ancestrySession,
                            associationsHost,
                            this.projectId,
                            { downsample: false }
                        );
                        const ancestryRows = formatAssociationRows(
                            ancestryResult.rows,
                            ancestrySession,
                            { project: VKS_ASSOCIATION_PROJECT_KP }
                        );
                        zoomedRows = mergeAssociationRowsByVariantAndAncestry(
                            zoomedRows,
                            ancestryRows,
                            primaryAncestry
                        );
                    } catch (ancestryErr) {
                        console.warn(
                            `GP2 Browser ${ancestry} zoomed association refetch failed`,
                            ancestryErr
                        );
                    }
                }

                for (const entry of this.associationsState.selectedPhenotypes || []) {
                    if (token !== this.zoomAssociationsRequestToken) {
                        return;
                    }
                    const phenotypeName =
                        typeof entry === "string" ? entry : entry?.name;
                    if (
                        !phenotypeName ||
                        phenotypeName === this.searchSession?.phenotype?.name
                    ) {
                        continue;
                    }
                    try {
                        const phenotypeSession = {
                            ...zoomSession,
                            phenotype:
                                typeof entry === "string" ? { name: entry } : entry,
                            ancestry: primaryAncestry,
                        };
                        const phenotypeResult = await fetchAssociations(
                            phenotypeSession,
                            associationsHost,
                            this.projectId,
                            { downsample: false }
                        );
                        const phenotypeRows = formatAssociationRows(
                            phenotypeResult.rows,
                            phenotypeSession,
                            { project: VKS_ASSOCIATION_PROJECT_KP }
                        );
                        zoomedRows = mergeAssociationRowsByVariantAndAncestry(
                            zoomedRows,
                            phenotypeRows,
                            primaryAncestry
                        );
                    } catch (phenotypeErr) {
                        console.warn(
                            `GP2 Browser ${phenotypeName} zoomed association refetch failed`,
                            phenotypeErr
                        );
                    }

                    for (const ancestry of entry?.selectedAncestries || []) {
                        if (token !== this.zoomAssociationsRequestToken) {
                            return;
                        }
                        if (!ancestry || ancestry === primaryAncestry) {
                            continue;
                        }
                        try {
                            const ancestrySession = {
                                ...zoomSession,
                                phenotype:
                                    typeof entry === "string"
                                        ? { name: entry }
                                        : entry,
                                ancestry,
                            };
                            const ancestryResult = await fetchAssociations(
                                ancestrySession,
                                associationsHost,
                                this.projectId,
                                { downsample: false }
                            );
                            const ancestryRows = formatAssociationRows(
                                ancestryResult.rows,
                                ancestrySession,
                                { project: VKS_ASSOCIATION_PROJECT_KP }
                            );
                            zoomedRows = mergeAssociationRowsByVariantAndAncestry(
                                zoomedRows,
                                ancestryRows,
                                primaryAncestry
                            );
                        } catch (ancestryErr) {
                            console.warn(
                                `GP2 Browser ${phenotypeName}/${ancestry} zoomed association refetch failed`,
                                ancestryErr
                            );
                        }
                    }
                }

                if (
                    token !== this.zoomAssociationsRequestToken ||
                    !this.refetchAssociationsOnZoom
                ) {
                    return;
                }

                this.lastZoomedAssociationsRegion = zoomRegion;
                const zoomedWithLd = applyLatestGp2LdScoreMap(zoomedRows);
                this.associationsState = {
                    ...this.associationsState,
                    rows: mergeZoomedAssociationRows(
                        this.associationsState.rows,
                        zoomedWithLd,
                        primaryAncestry
                    ),
                };
            } catch (error) {
                console.warn(
                    "GP2 Browser zoomed association refetch failed",
                    error
                );
            }
        },
        onTogglePositionMarker(position) {
            const viewRegion = this.viewRegion || this.searchSession?.region;
            if (!viewRegion) {
                return;
            }
            this.plotMarkersState = {
                ...this.plotMarkersState,
                positionMarkers: togglePositionMarker(
                    this.plotMarkersState.positionMarkers,
                    position,
                    viewRegion
                ),
            };
        },
        onToggleStarVariant(row) {
            this.plotMarkersState = {
                ...this.plotMarkersState,
                starredVariants: toggleStarredVariant(
                    this.plotMarkersState.starredVariants,
                    row
                ),
            };
        },
        /**
         * Starring from a QTL dot. The row carries the full raw QTL row
         * (plus an injected "Variant ID") so the drawer table and plots can
         * reconstruct it later even after it falls out of the downsampled
         * set — see GP2BrowserQtlDrawer.vue / GP2BrowserAssociationsPlot.vue.
         */
        onToggleQtlStarVariant(row) {
            this.plotMarkersState = {
                ...this.plotMarkersState,
                starredVariants: toggleStarredVariant(
                    this.plotMarkersState.starredVariants,
                    row,
                    "qtl"
                ),
            };
        },
        async onSetReferenceVariant(row) {
            if (!row || !this.searchSession) {
                return;
            }

            const refVariant = rowToLdVariant(row);
            const region = this.dataRegion || this.searchSession.region;
            const refPos = extractVariantPositionBp(row);
            if (refPos != null) {
                this.viewingCenterBp = clampViewingCenterBp(
                    refPos,
                    region,
                    this.viewingRegionHalfWidthBp
                );
            }
            this.associationsState = {
                ...this.associationsState,
                ldLoading: true,
                ldError: null,
            };

            try {
                const rowsWithLd = await enrichAssociationRowsWithLdScoresForRef(
                    this.associationsState.rows,
                    this.searchSession,
                    row,
                    region
                );
                const ldAvailable = rowsWithLd.some(
                    (entry) => entry.LDS != null && !Number.isNaN(entry.LDS)
                );
                this.associationsState = {
                    ...this.associationsState,
                    ldLoading: false,
                    ldError: ldAvailable
                        ? null
                        : "LD scores could not be loaded for the selected reference variant.",
                    rows: rowsWithLd,
                };
                if (ldAvailable) {
                    this.plotOverlaysState = {
                        ...this.plotOverlaysState,
                        refVariant,
                        refVariantUserSet: true,
                    };
                }
                this.lastZoomedAssociationsRegion = null;
                this.refetchAssociationsForZoomedView();
            } catch (ldError) {
                console.warn("GP2 Browser reference LD fetch failed", ldError);
                this.associationsState = {
                    ...this.associationsState,
                    ldLoading: false,
                    ldError: "LD scores could not be loaded for the selected reference variant.",
                };
            }
        },
        scheduleRegionDataSync(delayMs = 300) {
            if (this.regionPanSyncTimer) {
                clearTimeout(this.regionPanSyncTimer);
                this.regionPanSyncTimer = null;
            }
            if (delayMs <= 0) {
                this.$nextTick(() => {
                    this.syncRegionDataToView();
                });
                return;
            }
            this.regionPanSyncTimer = setTimeout(() => {
                this.regionPanSyncTimer = null;
                this.syncRegionDataToView();
            }, delayMs);
        },
        applyDataRegion(targetDataRegion) {
            if (!targetDataRegion) {
                return;
            }

            const nextDataRegion = cloneGenomicRegion(targetDataRegion);
            if (genomicRegionsEqual(this.dataRegion, nextDataRegion)) {
                return;
            }

            this.dataRegion = nextDataRegion;
            const rows = filterAssociationRowsInRegion(
                this.associationsState.rows,
                nextDataRegion
            );
            const genes = filterGenesInRegion(this.genesState.data, nextDataRegion);
            const recombData = trimRecombData(
                this.plotOverlaysState.recombData,
                nextDataRegion
            );

            this.associationsState = {
                ...this.associationsState,
                rows,
            };
            this.genesState = {
                ...this.genesState,
                data: genes.length ? genes : null,
            };
            this.plotOverlaysState = {
                ...this.plotOverlaysState,
                recombData,
            };
            this.applyGlobalEnrichmentDataRegion(nextDataRegion);
        },
        applyGlobalEnrichmentDataRegion(targetDataRegion) {
            const state = this.globalEnrichmentState;
            if (!state?.annoRows?.length || !targetDataRegion) {
                return;
            }

            const annoRows = filterAnnoRowsInRegion(state.annoRows, targetDataRegion);
            this.globalEnrichmentState = applyGlobalEnrichmentAnnoRows(state, annoRows);
        },
        flushStreamedAnnoRows(mergedAnnoRows, activeRegion) {
            const annoRows = filterAnnoRowsInRegion(mergedAnnoRows, activeRegion);
            this.globalEnrichmentState = {
                ...applyGlobalEnrichmentAnnoRows(this.globalEnrichmentState, annoRows),
                loading: false,
            };
        },
        isSectionChecked(sectionId) {
            return isSectionVisible(this.visibleSectionIds, sectionId);
        },
        setRegionLoadStep(stepId, status) {
            this.regionLoadProgress = patchRegionLoadStep(
                this.regionLoadProgress,
                stepId,
                status
            );
        },
        beginAdditivePhenotypeLoadProgress() {
            if (this.regionLoadDismissTimer) {
                clearTimeout(this.regionLoadDismissTimer);
                this.regionLoadDismissTimer = null;
            }
            const stepIds = ["associations", "ld"];
            if (!projectAssociationsOnly(this.projectId)) {
                if (this.isSectionChecked("credible-sets")) {
                    stepIds.push("credibleSets");
                }
                if (
                    this.isSectionChecked("global-enrichment") ||
                    this.isSectionChecked("variant-to-gene-links")
                ) {
                    stepIds.push("globalEnrichment");
                }
            }
            this.regionLoadProgress = startGp2RegionLoadProgress(stepIds);
            this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.LOADING);
        },
        finishAdditivePhenotypeLoadProgress({
            associationsOk = true,
            ldOk = true,
            credibleSetsOk = true,
            globalEnrichmentOk = true,
        } = {}) {
            if (!this.regionLoadProgress?.active) {
                return;
            }
            const associationsStep = this.regionLoadProgress.steps.find(
                (step) => step.id === "associations"
            );
            if (
                associationsStep &&
                associationsStep.status !== VKS_REGION_LOAD_STATUS.DONE &&
                associationsStep.status !== VKS_REGION_LOAD_STATUS.FAILED
            ) {
                this.setRegionLoadStep(
                    "associations",
                    associationsOk
                        ? VKS_REGION_LOAD_STATUS.DONE
                        : VKS_REGION_LOAD_STATUS.FAILED
                );
            }
            const ldStep = this.regionLoadProgress.steps.find(
                (step) => step.id === "ld"
            );
            if (
                ldStep &&
                ldStep.status !== VKS_REGION_LOAD_STATUS.DONE &&
                ldStep.status !== VKS_REGION_LOAD_STATUS.FAILED
            ) {
                this.setRegionLoadStep(
                    "ld",
                    ldOk ? VKS_REGION_LOAD_STATUS.DONE : VKS_REGION_LOAD_STATUS.FAILED
                );
            }
            const credibleSetsStep = this.regionLoadProgress.steps.find(
                (step) => step.id === "credibleSets"
            );
            if (
                credibleSetsStep &&
                credibleSetsStep.status !== VKS_REGION_LOAD_STATUS.DONE &&
                credibleSetsStep.status !== VKS_REGION_LOAD_STATUS.FAILED
            ) {
                this.setRegionLoadStep(
                    "credibleSets",
                    credibleSetsOk
                        ? VKS_REGION_LOAD_STATUS.DONE
                        : VKS_REGION_LOAD_STATUS.FAILED
                );
            }
            const globalEnrichmentStep = this.regionLoadProgress.steps.find(
                (step) => step.id === "globalEnrichment"
            );
            if (
                globalEnrichmentStep &&
                globalEnrichmentStep.status !== VKS_REGION_LOAD_STATUS.DONE &&
                globalEnrichmentStep.status !== VKS_REGION_LOAD_STATUS.FAILED
            ) {
                this.setRegionLoadStep(
                    "globalEnrichment",
                    globalEnrichmentOk
                        ? VKS_REGION_LOAD_STATUS.DONE
                        : VKS_REGION_LOAD_STATUS.FAILED
                );
            }
            this.regionLoadProgress = completeDeferredPhenotypeSeriesSteps(
                this.regionLoadProgress
            );
            this.closeRegionLoadProgressIfSettled();
        },
        closeRegionLoadProgressIfSettled() {
            if (
                !this.regionLoadProgress.active ||
                !regionLoadProgressSettled(this.regionLoadProgress) ||
                !regionLoadProgressCanAutoDismiss(this.regionLoadProgress)
            ) {
                return;
            }
            if (this.regionLoadDismissTimer) {
                return;
            }
            this.regionLoadDismissTimer = setTimeout(() => {
                this.regionLoadProgress = finishRegionLoadProgress(this.regionLoadProgress);
                this.regionLoadDismissTimer = null;
            }, 700);
        },
        dismissRegionLoadProgress() {
            if (this.regionLoadDismissTimer) {
                clearTimeout(this.regionLoadDismissTimer);
                this.regionLoadDismissTimer = null;
            }
            this.regionLoadProgress = finishRegionLoadProgress(this.regionLoadProgress);
        },
        flushStreamedAssociationRows(mergedRows, activeRegion, gapRegion = null) {
            const rowsForRegion = filterAssociationRowsInRegion(mergedRows, activeRegion);
            if (gapRegion) {
                this.dataRegion = unionGenomicRegions(this.dataRegion, gapRegion);
            }
            this.associationsState = {
                ...this.associationsState,
                rows: rowsForRegion,
                ldLoading: false,
                ldError: null,
            };
        },
        flushStreamedGenes(mergedGenes, activeRegion) {
            const genesForRegion = filterGenesInRegion(mergedGenes, activeRegion);
            this.genesState = {
                ...this.genesState,
                ready: true,
                loading: false,
                data: genesForRegion.length ? genesForRegion : null,
                error: genesForRegion.length ? null : this.genesState.error,
            };
        },
        flushStreamedRecomb(mergedRecomb, activeRegion) {
            this.plotOverlaysState = {
                ...this.plotOverlaysState,
                ready: true,
                loading: false,
                recombData: trimRecombData(mergedRecomb, activeRegion),
            };
        },
        async syncRegionDataToView() {
            if (!this.searchSession?.region || !this.dataRegion) {
                return;
            }

            const activeRegion = this.activeRegion;
            if (!activeRegion) {
                return;
            }

            const gaps = computeFetchGaps(this.dataRegion, activeRegion);

            if (!gaps.length) {
                this.applyDataRegion(activeRegion);
                this.syncCredibleSetsToActiveRegion(activeRegion);
                this.finishRegionExtendSync();
                return;
            }

            const token = ++this.regionExtendToken;
            if (this.regionLoadDismissTimer) {
                clearTimeout(this.regionLoadDismissTimer);
                this.regionLoadDismissTimer = null;
            }
            const associationsOnly = projectAssociationsOnly(this.projectId);
            const extendAssoc = this.isSectionChecked("associations");
            const extendGenes = !associationsOnly && this.isSectionChecked("genes");
            const extendGe =
                !associationsOnly && this.isSectionChecked("global-enrichment");
            const extendCs =
                !associationsOnly && this.isSectionChecked("credible-sets");
            const extendStepIds = [
                ...(extendAssoc ? ["associations", "recomb", "ld"] : []),
                ...(extendGenes ? ["genes"] : []),
                ...(extendCs ? ["credibleSets"] : []),
                ...(extendGe ? ["globalEnrichment"] : []),
            ];
            this.regionLoadProgress = startGp2RegionLoadProgress(extendStepIds);
            const associationsHost = this.bioIndexHostFor("associations");
            const genesHost = this.bioIndexHostFor("genes");
            const regionsHost = this.bioIndexHostFor("regions");
            if (!associationsHost) {
                this.regionLoadProgress = finishRegionLoadProgress(this.regionLoadProgress);
                return;
            }

            let mergedRows = this.associationsState.rows;
            let mergedGenes = this.genesState.data || [];
            let mergedRecomb = this.plotOverlaysState.recombData;
            let mergedAnnoRows = this.globalEnrichmentState?.annoRows || [];
            let extendedAssociationRows = false;
            let associationFetchFailed = false;

            if (extendAssoc) {
                this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.LOADING);
            }

            try {
                for (const gap of extendAssoc ? gaps : []) {
                    if (token !== this.regionExtendToken) {
                        return;
                    }

                    const gapRegion = {
                        chr: activeRegion.chr,
                        start: gap.start,
                        end: gap.end,
                    };
                    const gapSession = {
                        ...this.searchSession,
                        region: gapRegion,
                    };

                    try {
                        const result = await fetchAssociations(gapSession, associationsHost, this.projectId);
                        const formattedRows = formatAssociationRows(result.rows, gapSession, {
                            project: VKS_ASSOCIATION_PROJECT_KP,
                        });
                        if (formattedRows.length) {
                            extendedAssociationRows = true;
                        }
                        mergedRows = mergeAssociationRowsByVariantAndAncestry(
                            mergedRows,
                            formattedRows,
                            primaryAssociationAncestry(this.searchSession)
                        );

                        const ceGapRows = await this.fetchFormattedGwasCeAssociationRows(
                            this.searchSession,
                            gapRegion
                        );
                        if (ceGapRows.length) {
                            extendedAssociationRows = true;
                            mergedRows = mergeAssociationRowsByVariantAndAncestry(
                                mergedRows,
                                ceGapRows,
                                primaryAssociationAncestry(this.searchSession)
                            );
                        }
                        this.flushStreamedAssociationRows(mergedRows, activeRegion, gapRegion);

                        for (const ancestry of this.associationsState.selectedAncestries || []) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            if (!ancestry || ancestry === primaryAssociationAncestry(this.searchSession)) {
                                continue;
                            }
                            try {
                                const ancestrySession = {
                                    ...gapSession,
                                    ancestry,
                                };
                                const ancestryResult = await fetchAssociations(
                                    ancestrySession,
                                    associationsHost,
                                    this.projectId
                                );
                                const ancestryRows = formatAssociationRows(
                                    ancestryResult.rows,
                                    ancestrySession,
                                    { project: VKS_ASSOCIATION_PROJECT_KP }
                                );
                                if (ancestryRows.length) {
                                    extendedAssociationRows = true;
                                }
                                mergedRows = mergeAssociationRowsByVariantAndAncestry(
                                    mergedRows,
                                    ancestryRows,
                                    primaryAssociationAncestry(this.searchSession)
                                );
                                this.flushStreamedAssociationRows(
                                    mergedRows,
                                    activeRegion,
                                    gapRegion
                                );
                            } catch (ancestryError) {
                                console.warn(
                                    `GP2 Browser ${ancestry} association gap fetch failed`,
                                    ancestryError
                                );
                            }
                        }

                        for (const entry of this.associationsState.selectedPhenotypes || []) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            const phenotypeName =
                                typeof entry === "string" ? entry : entry?.name;
                            if (
                                !phenotypeName ||
                                phenotypeName === this.searchSession?.phenotype?.name
                            ) {
                                continue;
                            }
                            try {
                                const phenotypeSession = {
                                    ...gapSession,
                                    phenotype:
                                        typeof entry === "string"
                                            ? { name: entry }
                                            : entry,
                                    ancestry: primaryAssociationAncestry(
                                        this.searchSession
                                    ),
                                };
                                const phenotypeResult = await fetchAssociations(
                                    phenotypeSession,
                                    associationsHost,
                                    this.projectId
                                );
                                const phenotypeRows = formatAssociationRows(
                                    phenotypeResult.rows,
                                    phenotypeSession,
                                    { project: VKS_ASSOCIATION_PROJECT_KP }
                                );
                                if (phenotypeRows.length) {
                                    extendedAssociationRows = true;
                                }
                                mergedRows = mergeAssociationRowsByVariantAndAncestry(
                                    mergedRows,
                                    phenotypeRows,
                                    primaryAssociationAncestry(this.searchSession)
                                );
                                this.flushStreamedAssociationRows(
                                    mergedRows,
                                    activeRegion,
                                    gapRegion
                                );
                            } catch (phenotypeError) {
                                console.warn(
                                    `GP2 Browser ${phenotypeName} association gap fetch failed`,
                                    phenotypeError
                                );
                            }

                            for (const ancestry of entry?.selectedAncestries || []) {
                                if (token !== this.regionExtendToken) {
                                    return;
                                }
                                if (
                                    !ancestry ||
                                    ancestry ===
                                        primaryAssociationAncestry(this.searchSession)
                                ) {
                                    continue;
                                }
                                try {
                                    const ancestrySession = {
                                        ...gapSession,
                                        phenotype:
                                            typeof entry === "string"
                                                ? { name: entry }
                                                : entry,
                                        ancestry,
                                    };
                                    const ancestryResult = await fetchAssociations(
                                        ancestrySession,
                                        associationsHost,
                                        this.projectId
                                    );
                                    const ancestryRows = formatAssociationRows(
                                        ancestryResult.rows,
                                        ancestrySession,
                                        { project: VKS_ASSOCIATION_PROJECT_KP }
                                    );
                                    if (ancestryRows.length) {
                                        extendedAssociationRows = true;
                                    }
                                    mergedRows = mergeAssociationRowsByVariantAndAncestry(
                                        mergedRows,
                                        ancestryRows,
                                        primaryAssociationAncestry(this.searchSession)
                                    );
                                    this.flushStreamedAssociationRows(
                                        mergedRows,
                                        activeRegion,
                                        gapRegion
                                    );
                                } catch (ancestryError) {
                                    console.warn(
                                        `GP2 Browser ${phenotypeName}/${ancestry} association gap fetch failed`,
                                        ancestryError
                                    );
                                }
                            }
                        }
                    } catch (assocError) {
                        associationFetchFailed = true;
                        console.warn("GP2 Browser association gap fetch failed", assocError);
                    }
                }

                if (token !== this.regionExtendToken) {
                    return;
                }

                mergedRows = filterAssociationRowsInRegion(mergedRows, activeRegion);
                this.dataRegion = cloneGenomicRegion(activeRegion);
                if (extendAssoc) {
                    this.lastAssociationsRegion = cloneGenomicRegion(activeRegion);
                    this.flushStreamedAssociationRows(mergedRows, activeRegion);
                    this.setRegionLoadStep(
                        "associations",
                        associationFetchFailed && !extendedAssociationRows
                            ? VKS_REGION_LOAD_STATUS.FAILED
                            : VKS_REGION_LOAD_STATUS.DONE
                    );
                }

                if (projectAssociationsOnly(this.projectId)) {
                    if (extendAssoc) {
                        let recombFetchFailed = false;
                        this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.LOADING);
                        for (const gap of gaps) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            const gapRegion = {
                                chr: activeRegion.chr,
                                start: gap.start,
                                end: gap.end,
                            };
                            try {
                                const newRecomb = await fetchRecombinationRate(gapRegion);
                                if (newRecomb) {
                                    mergedRecomb = mergeRecombData(mergedRecomb, newRecomb);
                                    this.flushStreamedRecomb(mergedRecomb, activeRegion);
                                }
                            } catch (recombError) {
                                recombFetchFailed = true;
                                console.warn("GP2 Browser recomb gap fetch failed", recombError);
                            }
                        }
                        if (token !== this.regionExtendToken) {
                            return;
                        }
                        this.setRegionLoadStep(
                            "recomb",
                            recombFetchFailed && !mergedRecomb?.position?.length
                                ? VKS_REGION_LOAD_STATUS.FAILED
                                : VKS_REGION_LOAD_STATUS.DONE
                        );

                        if (extendedAssociationRows) {
                            this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                            const ldOk = await this.refreshRegionLdScores(
                                mergedRows,
                                activeRegion,
                                () => token !== this.regionExtendToken
                            );
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            this.setRegionLoadStep(
                                "ld",
                                ldOk ? VKS_REGION_LOAD_STATUS.DONE : VKS_REGION_LOAD_STATUS.FAILED
                            );
                        } else {
                            this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.DONE);
                        }
                    }

                    this.finishRegionExtendSync();
                    return;
                }

                let genesFetchFailed = false;
                let recombFetchFailed = false;

                if (extendGenes) {
                    this.setRegionLoadStep("genes", VKS_REGION_LOAD_STATUS.LOADING);
                }
                if (extendAssoc) {
                    this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.LOADING);
                }
                if (extendGe) {
                    this.setRegionLoadStep("globalEnrichment", VKS_REGION_LOAD_STATUS.LOADING);
                }

                await Promise.all([
                    (async () => {
                        if (!extendGenes) {
                            return;
                        }
                        for (const gap of gaps) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            const gapRegion = {
                                chr: activeRegion.chr,
                                start: gap.start,
                                end: gap.end,
                            };
                            try {
                                const newGenes = await fetchGenesTrackData(
                                    gapRegion,
                                    GP2_GENOME_BUILD,
                                    genesHost
                                );
                                mergedGenes = mergeGenesByName(mergedGenes, newGenes);
                                this.flushStreamedGenes(mergedGenes, activeRegion);
                            } catch (genesError) {
                                genesFetchFailed = true;
                                console.warn("GP2 Browser genes gap fetch failed", genesError);
                            }
                        }
                        if (token !== this.regionExtendToken) {
                            return;
                        }
                        this.lastGenesRegion = cloneGenomicRegion(activeRegion);
                        this.setRegionLoadStep(
                            "genes",
                            genesFetchFailed && !mergedGenes.length
                                ? VKS_REGION_LOAD_STATUS.FAILED
                                : VKS_REGION_LOAD_STATUS.DONE
                        );
                    })(),
                    (async () => {
                        if (!extendAssoc) {
                            return;
                        }
                        for (const gap of gaps) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            const gapRegion = {
                                chr: activeRegion.chr,
                                start: gap.start,
                                end: gap.end,
                            };
                            try {
                                const newRecomb = await fetchRecombinationRate(gapRegion);
                                if (newRecomb) {
                                    mergedRecomb = mergeRecombData(mergedRecomb, newRecomb);
                                    this.flushStreamedRecomb(mergedRecomb, activeRegion);
                                }
                            } catch (recombError) {
                                recombFetchFailed = true;
                                console.warn("GP2 Browser recomb gap fetch failed", recombError);
                            }
                        }
                        if (token !== this.regionExtendToken) {
                            return;
                        }
                        this.setRegionLoadStep(
                            "recomb",
                            recombFetchFailed && !mergedRecomb?.position?.length
                                ? VKS_REGION_LOAD_STATUS.FAILED
                                : VKS_REGION_LOAD_STATUS.DONE
                        );
                    })(),
                    (async () => {
                        if (!extendGe) {
                            return;
                        }
                        let annoFetchFailed = false;
                        for (const gap of gaps) {
                            if (token !== this.regionExtendToken) {
                                return;
                            }
                            const gapRegion = {
                                chr: activeRegion.chr,
                                start: gap.start,
                                end: gap.end,
                            };
                            try {
                                const newAnnoRows = await fetchLocusAnnotations(gapRegion, regionsHost);
                                if (newAnnoRows?.length) {
                                    mergedAnnoRows = mergeAnnoRows(mergedAnnoRows, newAnnoRows);
                                    this.flushStreamedAnnoRows(mergedAnnoRows, activeRegion);
                                }
                            } catch (annoError) {
                                annoFetchFailed = true;
                                console.warn(
                                    "GP2 Browser annotation gap fetch failed",
                                    annoError
                                );
                            }
                        }
                        if (token !== this.regionExtendToken) {
                            return;
                        }
                        this.lastGlobalEnrichmentRegion = cloneGenomicRegion(activeRegion);
                        this.flushStreamedAnnoRows(mergedAnnoRows, activeRegion);
                        this.setRegionLoadStep(
                            "globalEnrichment",
                            annoFetchFailed && !mergedAnnoRows.length
                                ? VKS_REGION_LOAD_STATUS.FAILED
                                : VKS_REGION_LOAD_STATUS.DONE
                        );
                    })(),
                ]);

                if (token !== this.regionExtendToken) {
                    return;
                }

                if (extendAssoc) {
                    if (extendedAssociationRows) {
                        this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                        const ldOk = await this.refreshRegionLdScores(
                            mergedRows,
                            activeRegion,
                            () => token !== this.regionExtendToken
                        );
                        if (token !== this.regionExtendToken) {
                            return;
                        }
                        this.setRegionLoadStep(
                            "ld",
                            ldOk ? VKS_REGION_LOAD_STATUS.DONE : VKS_REGION_LOAD_STATUS.FAILED
                        );
                    } else {
                        this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.DONE);
                    }
                }

                if (extendCs) {
                    this.setRegionLoadStep("credibleSets", VKS_REGION_LOAD_STATUS.LOADING);
                    const credibleSetsOk = await this.loadCredibleSetsList(
                        {
                            ...this.searchSession,
                            region: activeRegion,
                        },
                        { preserveSelection: true }
                    );
                    if (token !== this.regionExtendToken) {
                        return;
                    }
                    this.setRegionLoadStep(
                        "credibleSets",
                        credibleSetsOk
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                }

                this.finishRegionExtendSync();
            } catch (error) {
                if (token !== this.regionExtendToken) {
                    return;
                }
                console.warn("GP2 Browser region extension failed", error);
                this.regionLoadProgress = {
                    ...this.regionLoadProgress,
                    steps: this.regionLoadProgress.steps.map((step) =>
                        step.status === VKS_REGION_LOAD_STATUS.LOADING
                            ? { ...step, status: VKS_REGION_LOAD_STATUS.FAILED }
                            : step
                    ),
                };
                this.finishRegionExtendSync();
                this.closeRegionLoadProgressIfSettled();
            } finally {
                if (token === this.regionExtendToken) {
                    this.closeRegionLoadProgressIfSettled();
                }
            }
        },
        onMenuAction(payload) {
            if (
                payload.action === "resetSearch" ||
                payload.action === "newSession"
            ) {
                this.resetSearch();
                return;
            }
            if (payload.action === "applyRecentSearch") {
                this.applyRecentSearch(payload.recentSearch);
                return;
            }
            if (payload.action === "exportSession") {
                this.exportSession();
                return;
            }
            if (payload.action === "exportHtmlReport") {
                this.exportHtmlReport();
                return;
            }
            if (payload.action === "importSession") {
                this.openSessionImport();
                return;
            }
            if (payload.action === "gettingAround") {
                this.workspaceGuideOpen = true;
                return;
            }
            if (
                payload.action === "genePrioritization" ||
                payload.action === "associatedPhenotypes" ||
                payload.action === "literatureEvidence"
            ) {
                this.activeResearchPanelId = payload.action;
            }
        },
        applyRecentSearch(entry) {
            if (!entry?.phenotypeName || !entry?.region) {
                return;
            }

            const nextProjectId = normalizeProjectId(entry.projectId);
            if (nextProjectId !== this.projectId) {
                this.projectId = nextProjectId;
                this.syncUrlProjectParam();
            }

            const phenotypes = projectPhenotypes(
                this.projectId,
                GP2_BROWSER_PHENOTYPES
            );
            const phenotype = phenotypes.find(
                (item) => item.name === entry.phenotypeName
            );
            if (!phenotype) {
                this.welcomeInitialValues = {
                    phenotype: entry.phenotypeName,
                    ancestry: entry.ancestry || "",
                    geneOrVariantQuery:
                        entry.geneOrVariantQuery || entry.regionLabel || "",
                    regionExpandBp: entry.regionExpandBp ?? null,
                    gwasCeToken: entry.gwasCeToken || "",
                    errorMessage: `Phenotype "${entry.phenotypeName}" is not available in the current project.`,
                };
                this.welcomeOpen = true;
                this.canvasActive = false;
                return;
            }

            if (isGwasCeProject(this.projectId) && !String(entry.gwasCeToken || "").trim()) {
                this.welcomeInitialValues = {
                    phenotype: entry.phenotypeName,
                    ancestry: entry.ancestry || "",
                    geneOrVariantQuery:
                        entry.geneOrVariantQuery || entry.regionLabel || "",
                    regionExpandBp: entry.regionExpandBp ?? null,
                    gwasCeToken: "",
                    errorMessage: "Enter the GWAS-CE access token to continue.",
                };
                this.welcomeOpen = true;
                this.canvasActive = false;
                return;
            }

            this.onStartSearch(
                {
                    phenotype,
                    ancestry: entry.ancestry || null,
                    region: {
                        chr: entry.region.chr,
                        start: Number(entry.region.start),
                        end: Number(entry.region.end),
                    },
                    regionLabel: entry.regionLabel || formatRegion(entry.region),
                    geneOrVariantQuery:
                        entry.geneOrVariantQuery || entry.regionLabel || "",
                    regionExpandBp: entry.regionExpandBp ?? null,
                    gwasCeToken: entry.gwasCeToken || null,
                },
                { subAncestries: entry.subAncestries || [] }
            );
        },
        onVisibleSectionIdsUpdate(ids) {
            const prevSet = new Set(this.visibleSectionIds || []);
            this.visibleSectionIds = gp2NormalizeVisibleSectionIds(ids, this.sections);
            if (
                this.openDrawerId &&
                !isSectionVisible(this.visibleSectionIds, this.openDrawerId)
            ) {
                this.openDrawerId = null;
            }
            const newlyChecked = this.visibleSectionIds.filter(
                (id) => !prevSet.has(id)
            );
            if (newlyChecked.length && this.searchSession?.region) {
                this.loadDataForCheckedSections(newlyChecked);
                if (newlyChecked.includes("qtl-evidences")) {
                    this.maybeLoadQtlEvidences();
                }
            }
        },
        async loadDataForCheckedSections(sectionIds = []) {
            if (!this.searchSession?.region) {
                return;
            }
            const associationsOnly = projectAssociationsOnly(this.projectId);
            const targetRegion = cloneGenomicRegion(
                this.dataRegion || this.searchSession.region
            );
            const targetSession = {
                ...this.searchSession,
                region: targetRegion,
            };
            const checkedSet = new Set(sectionIds);

            const needAssoc =
                checkedSet.has("associations") &&
                !genomicRegionsEqual(this.lastAssociationsRegion, targetRegion) &&
                !this.associationsState.loading;
            const needGenes =
                !associationsOnly &&
                checkedSet.has("genes") &&
                (!this.genesState.ready ||
                    !genomicRegionsEqual(this.lastGenesRegion, targetRegion)) &&
                !this.genesState.loading;
            const needCs =
                !associationsOnly &&
                checkedSet.has("credible-sets") &&
                !genomicRegionsEqual(
                    this.lastCredibleSetsListRegion,
                    targetRegion
                ) &&
                !this.credibleSetsState.listLoading;
            const needGe =
                !associationsOnly &&
                checkedSet.has("global-enrichment") &&
                !genomicRegionsEqual(
                    this.lastGlobalEnrichmentRegion,
                    targetRegion
                ) &&
                !this.globalEnrichmentState.loading;
            const needV2g =
                !associationsOnly &&
                checkedSet.has("variant-to-gene-links") &&
                !needGe &&
                !genomicRegionsEqual(
                    this.lastGlobalEnrichmentRegion,
                    targetRegion
                ) &&
                !genomicRegionsEqual(this.lastV2gCatalogRegion, targetRegion) &&
                !this.globalEnrichmentState.loading;
            const needS2g =
                !associationsOnly &&
                checkedSet.has("snp2gene-links") &&
                (!hasS2gTrackData(this.s2gState) ||
                    !genomicRegionsEqual(this.lastS2gRegion, targetRegion)) &&
                !this.s2gState?.loadingTissue;

            const stepIds = [
                ...(needAssoc ? ["associations", "recomb", "ld"] : []),
                ...(needGenes ? ["genes"] : []),
                ...(needCs ? ["credibleSets"] : []),
                ...(needGe ? ["globalEnrichment"] : []),
                ...(needV2g ? ["geneLinks"] : []),
                ...(needS2g ? ["snp2geneLinks"] : []),
            ];

            if (!stepIds.length) {
                return;
            }

            if (this.regionLoadDismissTimer) {
                clearTimeout(this.regionLoadDismissTimer);
                this.regionLoadDismissTimer = null;
            }
            this.regionLoadProgress = startGp2RegionLoadProgress(stepIds);

            await Promise.all([
                (async () => {
                    if (!needAssoc) {
                        return;
                    }
                    await this.loadAssociationsSectionForSession(targetSession);
                })(),
                (async () => {
                    if (!needGenes) {
                        return;
                    }
                    this.setRegionLoadStep("genes", VKS_REGION_LOAD_STATUS.LOADING);
                    const ok = await this.loadGenesTrack(targetSession);
                    this.setRegionLoadStep(
                        "genes",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
                (async () => {
                    if (!needCs) {
                        return;
                    }
                    this.setRegionLoadStep(
                        "credibleSets",
                        VKS_REGION_LOAD_STATUS.LOADING
                    );
                    const ok = await this.loadCredibleSetsList(targetSession, {
                        preserveSelection: true,
                    });
                    this.setRegionLoadStep(
                        "credibleSets",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
                (async () => {
                    if (!needGe) {
                        return;
                    }
                    await this.loadGlobalEnrichmentForSession(targetSession, {
                        offerClassifyAction: true,
                    });
                })(),
                (async () => {
                    if (!needV2g) {
                        return;
                    }
                    this.setRegionLoadStep(
                        "geneLinks",
                        VKS_REGION_LOAD_STATUS.LOADING
                    );
                    const ok = await this.loadV2gTissueCatalogForSession(
                        targetSession
                    );
                    this.setRegionLoadStep(
                        "geneLinks",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
                (async () => {
                    if (!needS2g) {
                        return;
                    }
                    this.setRegionLoadStep(
                        "snp2geneLinks",
                        VKS_REGION_LOAD_STATUS.LOADING
                    );
                    const ok = await this.onS2gLoad();
                    this.setRegionLoadStep(
                        "snp2geneLinks",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
            ]);

            this.closeRegionLoadProgressIfSettled();
        },
        onSuggestActionsAfterUserActionUpdate(enabled) {
            this.suggestActionsAfterUserAction = Boolean(enabled);
        },
        /** Open Actions only when Settings → Suggest Actions is enabled. */
        openAssistantSuggestions() {
            if (this.suggestActionsAfterUserAction) {
                this.aiAssistantOpen = true;
            }
        },
        exportSession() {
            try {
                exportVariantSifterSession({
                    projectId: this.projectId,
                    searchSession: this.searchSession,
                    associationsState: this.associationsState,
                    genesState: this.genesState,
                    plotOverlaysState: this.plotOverlaysState,
                    plotMarkersState: this.plotMarkersState,
                    credibleSetsState: this.credibleSetsState,
                    globalEnrichmentState: this.globalEnrichmentState,
                    v2gState: this.v2gState,
                    s2gState: this.s2gState,
                    regionZoom: this.regionZoom,
                    regionZoomOut: this.regionZoomOut,
                    regionViewArea: this.regionViewArea,
                    regionShiftBp: this.regionShiftBp,
                    dataRegion: this.dataRegion,
                    openDrawerId: this.openDrawerId,
                    dataTableOpen: this.dataTableOpen,
                    visibleSectionIds: this.visibleSectionIds,
                    mappingState: this.mappingState,
                    workspaceMappingFilter: this.workspaceMappingFilter,
                });
                this.exportSessionOpen = true;
            } catch (error) {
                window.alert(error.message || "Could not export session.");
            }
        },
        async exportHtmlReport() {
            if (!this.canvasActive || !this.searchSession) {
                window.alert("Run a search before exporting an HTML report.");
                return;
            }

            try {
                // Close overlays that can cover tracks; canvases stay painted.
                const wasDataTableOpen = this.dataTableOpen;
                const wasAssistantOpen = this.aiAssistantOpen;
                this.dataTableOpen = false;
                this.aiAssistantOpen = false;
                await this.$nextTick();

                const result = await exportVariantSifterHtmlReport({
                    rootEl: this.$refs.workspace || this.$el,
                    searchSession: this.searchSession,
                    projectId: this.projectId,
                    viewRegion: this.viewRegion || this.searchSession.region,
                    associationRows: applyAssociationsFilters(
                        this.associationsState?.rows || [],
                        this.associationsState?.filtersIndex
                    ),
                    mappingState: this.mappingState,
                    credibleSetsState: this.credibleSetsState,
                    globalEnrichmentState: this.globalEnrichmentState,
                    v2gState: this.v2gState,
                    s2gState: this.s2gState,
                    workspaceMappingFilter: this.workspaceMappingFilter,
                });

                this.dataTableOpen = wasDataTableOpen;
                this.aiAssistantOpen = wasAssistantOpen;

                if (!result?.ok && result?.reason === "cancelled") {
                    return;
                }
                if (!result?.ok) {
                    window.alert("Could not save the HTML report.");
                }
            } catch (error) {
                window.alert(error?.message || "Could not export HTML report.");
            }
        },
        async onExportSessionConfirm({ filename }) {
            if (this.exportSessionBusy) {
                return;
            }

            try {
                const payload = exportVariantSifterSession({
                    projectId: this.projectId,
                    searchSession: this.searchSession,
                    associationsState: this.associationsState,
                    genesState: this.genesState,
                    plotOverlaysState: this.plotOverlaysState,
                    plotMarkersState: this.plotMarkersState,
                    credibleSetsState: this.credibleSetsState,
                    globalEnrichmentState: this.globalEnrichmentState,
                    v2gState: this.v2gState,
                    s2gState: this.s2gState,
                    regionZoom: this.regionZoom,
                    regionZoomOut: this.regionZoomOut,
                    regionViewArea: this.regionViewArea,
                    regionShiftBp: this.regionShiftBp,
                    dataRegion: this.dataRegion,
                    openDrawerId: this.openDrawerId,
                    dataTableOpen: this.dataTableOpen,
                    visibleSectionIds: this.visibleSectionIds,
                    mappingState: this.mappingState,
                    workspaceMappingFilter: this.workspaceMappingFilter,
                });
                this.exportSessionBusy = true;
                const result = await saveJsonBundle(filename, payload);
                if (result.ok) {
                    this.exportSessionOpen = false;
                }
            } catch (error) {
                window.alert(error.message || "Could not export session.");
            } finally {
                this.exportSessionBusy = false;
            }
        },
        openSessionImport() {
            const input = this.$refs.sessionImportInput;
            if (input) {
                input.value = "";
                input.click();
            }
        },
        async onSessionFileSelected(event) {
            const file = event.target.files?.[0];
            if (!file) {
                return;
            }

            try {
                const payload = await readSessionFile(file);
                const projectId = normalizeProjectId(payload.projectId);
                const phenotypes = projectPhenotypes(
                    projectId,
                    GP2_BROWSER_PHENOTYPES
                );
                const restored = importVariantSifterSession(payload, phenotypes);
                restored.visibleSectionIds = Array.isArray(
                    payload?.ui?.visibleSectionIds
                )
                    ? gp2NormalizeVisibleSectionIds(
                          payload.ui.visibleSectionIds,
                          this.sections
                      )
                    : gp2DefaultVisibleSectionIds(this.sections);
                if (typeof payload?.ui?.openDrawerId === "string") {
                    restored.openDrawerId = payload.ui.openDrawerId;
                }
                this.projectId = projectId;
                this.syncUrlProjectParam();
                this.applyImportedSession(restored);
            } catch (error) {
                const message = String(
                    error?.message || "Could not import session."
                ).replace(/Variant Sifter/gi, "GP2 Browser");
                window.alert(message);
            } finally {
                event.target.value = "";
            }
        },
        applyImportedSession(restored) {
            if (restored.searchSession?.region) {
                const ensured = ensureRegionWithinActiveDataLimit(
                    restored.searchSession.region
                );
                if (ensured.trimmed) {
                    window.alert(
                        activeRegionTrimmedMessage(
                            ensured.originalRegion,
                            ensured.region
                        )
                    );
                    restored.searchSession = {
                        ...restored.searchSession,
                        region: ensured.region,
                        regionLabel:
                            formatRegion(ensured.region) ||
                            restored.searchSession.regionLabel,
                        regionTrimmed: true,
                        regionBeforeTrim: ensured.originalRegion,
                    };
                }
            }

            if (restored.projectId != null) {
                this.projectId = normalizeProjectId(restored.projectId);
            }

            const limitRegion = resolveZoomOutLimitRegion(
                restored.searchSession.region,
                restored.searchSession.regionExpandBp
            );
            const viewport = clampRegionViewportForDataLimit(
                restored.searchSession.region,
                {
                    regionShiftBp: restored.regionShiftBp ?? restored.viewOffsetBp ?? 0,
                    regionZoomOut: restored.regionZoomOut ?? 0,
                },
                limitRegion
            );

            this.associationsRequestToken += 1;
            this.genesRequestToken += 1;
            this.plotOverlaysRequestToken += 1;
            this.credibleSetsRequestToken += 1;
            this.searchSession = restored.searchSession;
            this.associationsState = restored.associationsState;
            this.genesState = restored.genesState || emptyGenesState();
            this.plotOverlaysState =
                restored.plotOverlaysState || emptyPlotOverlaysState();
            this.plotMarkersState =
                restored.plotMarkersState || emptyPlotMarkersState();
            this.credibleSetsState =
                restored.credibleSetsState || emptyCredibleSetsState();
            this.globalEnrichmentState =
                restored.globalEnrichmentState || emptyGlobalEnrichmentState();
            this.v2gState = restored.v2gState || emptyV2gState();
            this.s2gState = restored.s2gState || emptyS2gState();
            this.globalEnrichmentRequestToken += 1;
            this.v2gRequestToken += 1;
            this.s2gRequestToken += 1;
            this.regionZoom = restored.regionZoom ?? 0;
            this.regionViewArea = restored.regionViewArea ?? 0;
            this.regionShiftBp = viewport.regionShiftBp;
            this.regionZoomOut = viewport.regionZoomOut;
            this.pendingPanSliderReset = false;
            this.dataRegion = restored.dataRegion
                ? cloneGenomicRegion(restored.dataRegion)
                : cloneGenomicRegion(restored.searchSession.region);
            const restoredActiveRegion = computeActiveRegion(
                restored.searchSession.region,
                restored.regionShiftBp ?? restored.viewOffsetBp ?? 0
            );
            this.lastAssociationsRegion = restored.associationsState?.rows?.length
                ? cloneGenomicRegion(this.dataRegion)
                : null;
            this.lastGenesRegion = restored.genesState?.ready
                ? cloneGenomicRegion(this.dataRegion)
                : null;
            this.qtlRequestToken += 1;
            this.qtlState = emptyQtlState(this.qtlState);
            this.lastQtlRegion = null;
            this.lastCredibleSetsListRegion = restored.credibleSetsState?.available
                ?.length
                ? restoredActiveRegion
                : null;
            this.lastGlobalEnrichmentRegion =
                restored.globalEnrichmentState?.annoRows?.length ||
                Object.keys(restored.globalEnrichmentState?.annoData || {}).length
                    ? cloneGenomicRegion(this.dataRegion)
                    : null;
            this.lastV2gCatalogRegion = restored.globalEnrichmentState?.geRows
                ?.length
                ? cloneGenomicRegion(this.dataRegion)
                : null;
            this.lastS2gRegion = hasS2gTrackData(restored.s2gState)
                ? cloneGenomicRegion(this.dataRegion)
                : null;
            this.openDrawerId = restored.openDrawerId;
            this.visibleSectionIds = gp2NormalizeVisibleSectionIds(
                restored.visibleSectionIds,
                this.sections
            );
            if (
                this.openDrawerId &&
                !isSectionVisible(this.visibleSectionIds, this.openDrawerId)
            ) {
                this.openDrawerId = null;
            }
            this.canvasActive = true;
            this.welcomeOpen = false;
            this.welcomeInitialValues = null;
            this.dataTableOpen = restored.dataTableOpen ?? false;
            this.mappingState = normalizeMappingState(restored.mappingState);
            this.workspaceMappingFilter = normalizeWorkspaceMappingFilter(
                restored.workspaceMappingFilter
            );
            this.assistantState = emptyAssistantState();
            this.aiAssistantOpen = false;
            this.syncUrlSearchParams(restored.searchSession);
            this.afterSessionRestored(restored);
        },
        afterSessionRestored(restored) {
            const selectedPhenotypes =
                restored.associationsState?.selectedPhenotypes || [];
            if (restored.searchSession && this.isSectionChecked("associations")) {
                this.probeAncestryAssociationAvailabilityForSession(
                    restored.searchSession
                );
                selectedPhenotypes.forEach((entry) => {
                    if (!entry?.name) {
                        return;
                    }
                    this.probeAncestryAssociationAvailabilityForPhenotype(
                        entry,
                        restored.searchSession
                    );
                });
            }
            if (restored.globalEnrichmentState) {
                this.setRegionLoadStep(
                    "globalEnrichment",
                    VKS_REGION_LOAD_STATUS.DONE
                );
                this.hydrateCompanionLayersForRestoredPhenotypes(
                    selectedPhenotypes
                );
                return;
            }
            if (this.isSectionChecked("global-enrichment")) {
                Promise.resolve(
                    this.loadGlobalEnrichmentForSession(restored.searchSession)
                ).finally(() => {
                    this.hydrateCompanionLayersForRestoredPhenotypes(
                        selectedPhenotypes
                    );
                });
            } else {
                this.hydrateCompanionLayersForRestoredPhenotypes(
                    selectedPhenotypes
                );
            }
        },
        /**
         * Older or partial session files may restore additive phenotypes without
         * GE / CS companion rows. Fetch missing layers without clearing snapshot data.
         */
        hydrateCompanionLayersForRestoredPhenotypes(selectedPhenotypes) {
            const loadGe =
                this.isSectionChecked("global-enrichment") ||
                this.isSectionChecked("variant-to-gene-links");
            const loadCs = this.isSectionChecked("credible-sets");
            (selectedPhenotypes || []).forEach((entry) => {
                const phenotypeName = String(entry?.name || "").trim();
                if (!phenotypeName) {
                    return;
                }
                const phenotype = {
                    name: phenotypeName,
                    description: entry.description || null,
                };
                if (loadGe) {
                    const geRows = this.globalEnrichmentState?.geRows || [];
                    const hasGe = geRows.some(
                        (row) =>
                            String(row?.phenotype || "").trim() === phenotypeName
                    );
                    if (!hasGe) {
                        this.mergeGlobalEnrichmentForPhenotype(phenotype);
                    }
                }
                if (loadCs) {
                    const available = this.credibleSetsState?.available || [];
                    const hasCs = available.some(
                        (cs) =>
                            String(cs?.phenotype || "").trim() === phenotypeName
                    );
                    if (!hasCs) {
                        this.mergeCredibleSetsForPhenotype(phenotype);
                    }
                }
            });
        },
        offerGeTissueClassifyAction(session, catalog) {
            if (!session || !catalog?.tissues?.length) {
                return;
            }
            const geAction = findAssistantAction("filter_ge_relevance");
            const understudiedAction = findAssistantAction(
                "find_understudied_bottom_line"
            );
            const credibleSetCount = (this.credibleSetsState?.available || []).length;
            const steps = [
                {
                    id: "step-filter-ge-relevance",
                    actionId: "filter_ge_relevance",
                    label:
                        geAction?.label ||
                        "Classify tissues by phenotype relevance",
                },
                {
                    id: "step-find-understudied-bottom-line",
                    actionId: "find_understudied_bottom_line",
                    label:
                        understudiedAction?.label ||
                        "Find understudied bottom-line variants in this locus",
                },
            ];
            const stepStates = {};
            steps.forEach((step) => {
                stepStates[step.id] = "pending";
            });
            this.openAssistantSuggestions();
            this.assistantState = {
                ...this.assistantState,
                activeTab: "request",
                executing: false,
                executionProgressLabel: "",
                cs2ctStarPrompt: null,
                understudiedStarPrompt: null,
                understudiedAncestry: null,
                plan: createAssistantPlan(steps, { executeLabel: "Execute" }),
                stepStates,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantMessage(
                        buildGeRelevanceOfferMessage(session, catalog, {
                            credibleSetCount,
                        })
                    ),
                    createAssistantMessage(buildUnderstudiedOfferMessage(session)),
                ]),
            };
        },
        offerUnderstudiedForAncestry(ancestry) {
            if (!this.searchSession || !ancestry) {
                return;
            }
            const understudiedAction = findAssistantAction(
                "find_understudied_bottom_line"
            );
            if (!understudiedAction) {
                return;
            }

            const stepId = this.researchActionStepId("find_understudied_bottom_line");
            const existingSteps = this.assistantState?.plan?.steps || [];
            const hasUnderstudiedStep = existingSteps.some(
                (step) => step.actionId === "find_understudied_bottom_line"
            );
            const nextSteps = hasUnderstudiedStep
                ? existingSteps
                : [
                      ...existingSteps,
                      {
                          id: stepId,
                          actionId: "find_understudied_bottom_line",
                          label: understudiedAction.label,
                      },
                  ];
            const stepStates = {
                ...(this.assistantState.stepStates || {}),
                [stepId]: "pending",
            };

            this.openAssistantSuggestions();
            this.assistantState = {
                ...this.assistantState,
                activeTab: "request",
                executing: false,
                executionProgressLabel: "",
                understudiedStarPrompt: null,
                understudiedAncestry: null,
                plan: createAssistantPlan(nextSteps, { executeLabel: "Execute" }),
                stepStates,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantMessage(
                        [
                            `${ancestry} association data is loaded.`,
                            buildUnderstudiedOfferMessage({
                                ...this.searchSession,
                                ancestry: primaryAssociationAncestry(this.searchSession),
                            }),
                            `Loaded ancestries: ${this.listUnderstudiedTargetAncestries().join(
                                ", "
                            )}.`,
                        ].join(" ")
                    ),
                ]),
            };
        },
        researchActionStepId(actionId) {
            if (actionId === "filter_ge_relevance") {
                return "step-filter-ge-relevance";
            }
            if (actionId === "find_understudied_bottom_line") {
                return "step-find-understudied-bottom-line";
            }
            if (actionId === "find_genetic_correlations") {
                return "step-find-genetic-correlations";
            }
            return `step-${actionId}`;
        },
        isResearchActionStepDone(actionId) {
            const stepId = this.researchActionStepId(actionId);
            return (this.assistantState?.stepStates || {})[stepId] === "done";
        },
        offerRemainingResearchAction(completedActionId) {
            const remainingId =
                completedActionId === "filter_ge_relevance"
                    ? "find_understudied_bottom_line"
                    : completedActionId === "find_understudied_bottom_line"
                      ? "filter_ge_relevance"
                      : null;
            if (!remainingId || this.isResearchActionStepDone(remainingId)) {
                return;
            }

            if (
                remainingId === "filter_ge_relevance" &&
                !this.globalEnrichmentState?.catalog?.tissues?.length
            ) {
                return;
            }

            const remainingAction = findAssistantAction(remainingId);
            if (!remainingAction || !this.searchSession) {
                return;
            }

            const remainingStepId = this.researchActionStepId(remainingId);
            const existingSteps = this.assistantState?.plan?.steps || [];
            const hasRemainingStep = existingSteps.some(
                (step) => step.actionId === remainingId
            );
            const nextSteps = hasRemainingStep
                ? existingSteps
                : [
                      ...existingSteps,
                      {
                          id: remainingStepId,
                          actionId: remainingId,
                          label: remainingAction.label,
                      },
                  ];
            const stepStates = {
                ...(this.assistantState.stepStates || {}),
            };
            if (stepStates[remainingStepId] !== "done") {
                stepStates[remainingStepId] = "pending";
            }

            const offerText = hasRemainingStep
                ? `Optional next: run “${remainingAction.label}” from the steps above.`
                : remainingId === "find_understudied_bottom_line"
                  ? buildUnderstudiedOfferMessage(this.searchSession)
                  : buildGeRelevanceOfferMessage(
                        this.searchSession,
                        this.globalEnrichmentState?.catalog,
                        {
                            credibleSetCount: (
                                this.credibleSetsState?.available || []
                            ).length,
                        }
                    );

            this.openAssistantSuggestions();
            this.assistantState = {
                ...this.assistantState,
                activeTab: "request",
                plan: createAssistantPlan(nextSteps, { executeLabel: "Execute" }),
                stepStates,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantMessage(
                        hasRemainingStep
                            ? offerText
                            : `Next optional step: ${remainingAction.label}. ${offerText}`
                    ),
                ]),
            };
        },
        async loadGlobalEnrichmentForSession(
            session,
            { offerClassifyAction = false, requestToken } = {}
        ) {
            if (!session?.region) {
                return false;
            }

            const geToken = requestToken ?? ++this.globalEnrichmentRequestToken;
            const geHost = this.bioIndexHostFor("global-enrichment");
            const regionsHost = this.bioIndexHostFor("regions");
            if (!geHost && !regionsHost) {
                this.globalEnrichmentState = {
                    ...emptyGlobalEnrichmentState(),
                    error: "BioIndex host is not available.",
                };
                this.setRegionLoadStep("globalEnrichment", VKS_REGION_LOAD_STATUS.FAILED);
                return false;
            }

            this.globalEnrichmentState = {
                ...emptyGlobalEnrichmentState(),
                loading: true,
            };
            this.setRegionLoadStep("globalEnrichment", VKS_REGION_LOAD_STATUS.LOADING);

            try {
                const phenotypes =
                    this.associationPhenotypesOnCanvas().length > 0
                        ? this.associationPhenotypesOnCanvas()
                        : session.phenotype
                          ? [session.phenotype]
                          : [];
                const [geRowLists, annoRows] = await Promise.all([
                    Promise.all(
                        phenotypes.map((phenotype) =>
                            fetchGlobalEnrichment(
                                { ...session, phenotype },
                                geHost
                            )
                        )
                    ),
                    fetchLocusAnnotations(session.region, regionsHost),
                ]);
                if (geToken !== this.globalEnrichmentRequestToken) {
                    return false;
                }
                let geRows = [];
                geRowLists.forEach((rows, idx) => {
                    const pName = String(phenotypes[idx]?.name || "").trim();
                    geRows = pName
                        ? mergeGeRowsByPhenotype(geRows, rows, pName)
                        : [...geRows, ...(rows || [])];
                });

                const annoData = buildAnnoDataFromRows(annoRows);
                const catalog = extractGeCatalog(annoData);
                this.globalEnrichmentState = {
                    loading: false,
                    error: Object.keys(annoData).length
                        ? null
                        : "No regulatory annotations were found for this locus.",
                    geRows,
                    annoRows,
                    annoData,
                    catalog,
                    llmRelevance: emptyGeLlmRelevanceState(),
                    enabledMutedAnnotations: [],
                    enabledMutedAnnotationTissues: {},
                    disabledAnnotationTissues: {},
                    selectedAnnotations: [...catalog.annotations],
                    tissueTrackSort: normalizeGeTissueTrackSort(
                        this.globalEnrichmentState?.tissueTrackSort
                    ),
                    geTrackPValueMax: normalizeGeTrackPValueMax(
                        this.globalEnrichmentState?.geTrackPValueMax
                    ),
                    selectedMethods: null,
                    selectedSources: null,
                    activeAnnotation: null,
                    selectedTissues: [],
                    selectedTissuesByAnnotation: {},
                    selectedBiosamples: [],
                    biosampleMethodOptions: [],
                    biosampleSourceOptions: [],
                    biosampleRegionsByAnnotation: {},
                    biosampleLoading: false,
                };
                this.lastGlobalEnrichmentRegion = cloneGenomicRegion(
                    session.region
                );
                this.lastV2gCatalogRegion = cloneGenomicRegion(session.region);
                this.setRegionLoadStep(
                    "globalEnrichment",
                    Object.keys(annoData).length
                        ? VKS_REGION_LOAD_STATUS.DONE
                        : VKS_REGION_LOAD_STATUS.FAILED
                );

                if (offerClassifyAction && Object.keys(annoData).length && catalog.tissues?.length) {
                    this.$nextTick(() => {
                        if (geToken !== this.globalEnrichmentRequestToken) {
                            return;
                        }
                        this.offerGeTissueClassifyAction(session, catalog);
                    });
                }
                return true;
            } catch (error) {
                if (geToken !== this.globalEnrichmentRequestToken) {
                    return false;
                }
                console.warn("GP2 Browser global enrichment load failed", error);
                this.globalEnrichmentState = {
                    ...emptyGlobalEnrichmentState(),
                    error: "Failed to load global enrichment for this locus.",
                };
                this.setRegionLoadStep("globalEnrichment", VKS_REGION_LOAD_STATUS.FAILED);
                return false;
            }
        },
        async loadV2gTissueCatalogForSession(session) {
            if (!session?.region) {
                return false;
            }
            if (
                genomicRegionsEqual(
                    this.lastGlobalEnrichmentRegion,
                    session.region
                ) ||
                genomicRegionsEqual(this.lastV2gCatalogRegion, session.region)
            ) {
                return true;
            }
            const geHost = this.bioIndexHostFor("global-enrichment");
            if (!geHost) {
                return false;
            }
            const geToken = ++this.globalEnrichmentRequestToken;
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                loading: true,
            };
            try {
                const phenotypes =
                    this.associationPhenotypesOnCanvas().length > 0
                        ? this.associationPhenotypesOnCanvas()
                        : session.phenotype
                          ? [session.phenotype]
                          : [];
                const geRowLists = await Promise.all(
                    phenotypes.map((phenotype) =>
                        fetchGlobalEnrichment(
                            { ...session, phenotype },
                            geHost
                        )
                    )
                );
                if (geToken !== this.globalEnrichmentRequestToken) {
                    return false;
                }
                let geRows = [];
                geRowLists.forEach((rows, idx) => {
                    const pName = String(phenotypes[idx]?.name || "").trim();
                    geRows = pName
                        ? mergeGeRowsByPhenotype(geRows, rows, pName)
                        : [...geRows, ...(rows || [])];
                });
                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    loading: false,
                    geRows,
                };
                this.lastV2gCatalogRegion = cloneGenomicRegion(session.region);
                return true;
            } catch (error) {
                if (geToken !== this.globalEnrichmentRequestToken) {
                    return false;
                }
                console.warn(
                    "GP2 Browser V2G tissue catalog load failed",
                    error
                );
                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    loading: false,
                };
                return false;
            }
        },
        async mergeGlobalEnrichmentForPhenotype(phenotype) {
            const phenotypeName = String(phenotype?.name || "").trim();
            if (!phenotypeName || !this.searchSession) {
                return false;
            }

            const geHost = this.bioIndexHostFor("global-enrichment");
            if (!geHost) {
                return false;
            }

            const session = {
                ...this.searchSession,
                phenotype,
                region: this.dataRegion || this.searchSession.region,
            };

            try {
                const geRows = await fetchGlobalEnrichment(session, geHost);
                // V2G tissue pickers read the full geRows list (no phenotype filter),
                // so merged additive rows must land here for new tissues to appear.
                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    geRows: mergeGeRowsByPhenotype(
                        this.globalEnrichmentState.geRows,
                        geRows,
                        phenotypeName
                    ),
                };
                return true;
            } catch (error) {
                console.warn(
                    `GP2 Browser ${phenotypeName} global enrichment load failed`,
                    error
                );
                return false;
            }
        },
        removeGlobalEnrichmentForPhenotype(phenotypeName) {
            const target = String(phenotypeName || "").trim();
            if (!target) {
                return;
            }
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                geRows: removeGeRowsForPhenotype(
                    this.globalEnrichmentState.geRows,
                    target
                ),
            };
        },
        resetSearch() {
            this.associationsRequestToken += 1;
            this.genesRequestToken += 1;
            this.plotOverlaysRequestToken += 1;
            this.credibleSetsRequestToken += 1;
            this.globalEnrichmentRequestToken += 1;
            this.v2gRequestToken += 1;
            this.s2gRequestToken += 1;
            this.assistantActionToken += 1;
            this.searchSession = null;
            this.associationsState = emptyAssociationsState();
            this.genesState = emptyGenesState(this.genesState?.selectedTypes);
            this.qtlState = emptyQtlState(this.qtlState);
            this.qtlRequestToken += 1;
            this.plotOverlaysState = emptyPlotOverlaysState();
            this.plotMarkersState = emptyPlotMarkersState();
            this.credibleSetsState = emptyCredibleSetsState();
            this.globalEnrichmentState = emptyGlobalEnrichmentState();
            this.v2gState = emptyV2gState();
            this.s2gState = emptyS2gState();
            this.assistantState = emptyAssistantState();
            this.aiAssistantOpen = false;
            this.lastAssociationsRegion = null;
            this.lastZoomedAssociationsRegion = null;
            this.zoomAssociationsRequestToken += 1;
            this.lastGenesRegion = null;
            this.lastQtlRegion = null;
            this.lastCredibleSetsListRegion = null;
            this.lastGlobalEnrichmentRegion = null;
            this.lastV2gCatalogRegion = null;
            this.lastS2gRegion = null;
            this.pendingSubAncestries = [];
            this.pendingPhenotypes = [];
            this.dataRegion = null;
            this.regionShiftBp = 0;
            this.regionLoadProgress = emptyRegionLoadProgress();
            this.resetRegionViewport();
            this.openDrawerId = null;
            this.dataTableOpen = false;
            this.mappingState = emptyMappingState();
            this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
            this.settingsOpen = false;
            this.visibleSectionIds = gp2DefaultVisibleSectionIds(this.sections);
            this.welcomeInitialValues = null;
            this.welcomeOpen = true;
            this.canvasActive = false;
            this.clearUrlSearchParams();
        },
        clearUrlSearchParams() {
            if (!this.utilsBox?.keyParams) {
                return;
            }
            this.utilsBox.keyParams.set({
                phenotype: undefined,
                region: undefined,
                ancestry: undefined,
                sub_ancestries: undefined,
            });
            this.syncUrlProjectParam();
        },
        onSessionParamApply({ session, projectId, addPhenotype = false, phenotype = null } = {}) {
            if (!session) {
                return;
            }
            const nextProjectId = normalizeProjectId(projectId);
            if (nextProjectId !== this.projectId) {
                this.projectId = nextProjectId;
                this.syncUrlProjectParam();
            }
            if (addPhenotype && phenotype) {
                this.loadAssociationPhenotypeSeries(phenotype);
                return;
            }
            const subAncestries = [
                ...(this.associationsState?.selectedAncestries || []),
            ];
            this.onStartSearch(session, { subAncestries });
        },
        onStartSearch(session, { subAncestries = [], additionalPhenotypes = [] } = {}) {
            let searchSession = session;
            if (session?.region) {
                const ensured = ensureRegionWithinActiveDataLimit(session.region);
                if (ensured.trimmed) {
                    // Welcome already alerts when it trims; only notify here for
                    // other entry points (recent search, URL hydrate, etc.).
                    if (!session.regionTrimmed) {
                        window.alert(
                            activeRegionTrimmedMessage(
                                ensured.originalRegion,
                                ensured.region
                            )
                        );
                    }
                    searchSession = {
                        ...session,
                        region: ensured.region,
                        regionLabel:
                            formatRegion(ensured.region) || session.regionLabel,
                        regionTrimmed: true,
                        regionBeforeTrim:
                            session.regionBeforeTrim || ensured.originalRegion,
                    };
                }
            }

            this.searchSession = searchSession;
            this.pendingSubAncestries = parseSubAncestriesParam(
                subAncestries,
                searchSession.ancestry || "Mixed"
            );
            this.pendingPhenotypes = parsePhenotypesParam(additionalPhenotypes).filter(
                (name) => name !== searchSession?.phenotype?.name
            );
            this.assistantActionToken += 1;
            this.assistantState = emptyAssistantState();
            this.aiAssistantOpen = false;
            this.regionZoom = 0;
            this.regionZoomOut = 0;
            this.regionViewArea = 0;
            this.regionShiftBp = 0;
            this.viewingCenterBp = null;
            this.qtlRequestToken += 1;
            this.qtlState = emptyQtlState(this.qtlState);
            this.dataRegion = cloneGenomicRegion(searchSession.region);
            this.genesState = emptyGenesState(this.genesState?.selectedTypes);
            this.plotOverlaysState = emptyPlotOverlaysState();
            this.plotMarkersState = emptyPlotMarkersState();
            this.credibleSetsState = emptyCredibleSetsState();
            this.globalEnrichmentState = emptyGlobalEnrichmentState();
            this.v2gState = emptyV2gState();
            this.s2gState = emptyS2gState();
            this.mappingState = emptyMappingState();
            this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
            this.v2gRequestToken += 1;
            this.s2gRequestToken += 1;
            this.lastAssociationsRegion = null;
            this.lastZoomedAssociationsRegion = null;
            this.zoomAssociationsRequestToken += 1;
            this.lastGenesRegion = null;
            this.lastQtlRegion = null;
            this.lastCredibleSetsListRegion = null;
            this.lastGlobalEnrichmentRegion = null;
            this.lastV2gCatalogRegion = null;
            this.lastS2gRegion = null;
            if (projectAssociationsOnly(this.projectId)) {
                this.visibleSectionIds = ["associations"];
                this.openDrawerId = null;
            }
            this.canvasActive = true;
            this.welcomeOpen = false;
            this.syncUrlSearchParams(searchSession);
            this.recentSearches = pushRecentSearch(searchSession, {
                projectId: this.projectId,
                subAncestries: this.pendingSubAncestries,
            });
            this.loadInitialSearchData(searchSession);
            this.maybeLoadQtlEvidences();
        },
        syncCredibleSetsToActiveRegion(activeRegion) {
            if (
                projectAssociationsOnly(this.projectId) ||
                !this.isSectionChecked("credible-sets")
            ) {
                return;
            }
            if (!this.searchSession?.phenotype || !activeRegion) {
                return;
            }
            if (genomicRegionsEqual(this.lastCredibleSetsListRegion, activeRegion)) {
                return;
            }

            this.loadCredibleSetsList(
                {
                    ...this.searchSession,
                    region: activeRegion,
                },
                { preserveSelection: true }
            );
        },
        async loadCredibleSetsList(session, options = {}) {
            const { preserveSelection = false } = options;
            const token = ++this.credibleSetsRequestToken;

            if (preserveSelection) {
                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    listLoading: true,
                    listError: null,
                };
            } else {
                this.credibleSetsState = {
                    ...emptyCredibleSetsState(),
                    listLoading: true,
                };
                this.lastCredibleSetsListRegion = null;
            }

            const host = this.bioIndexHostFor("credible-sets");
            if (!host) {
                if (token !== this.credibleSetsRequestToken) {
                    return false;
                }
                this.credibleSetsState = {
                    ...(preserveSelection ? this.credibleSetsState : emptyCredibleSetsState()),
                    listError: "BioIndex host is not available.",
                };
                return false;
            }

            try {
                const phenotypes =
                    this.associationPhenotypesOnCanvas().length > 0
                        ? this.associationPhenotypesOnCanvas()
                        : session.phenotype
                          ? [session.phenotype]
                          : [];
                const tagKpProject = isGwasCeProject(this.projectId);
                const lists = await Promise.all(
                    phenotypes.map(async (phenotype) => {
                        const phenotypeSession = {
                            ...session,
                            phenotype,
                        };
                        return fetchCredibleSetsListForAncestries(
                            phenotypeSession,
                            host,
                            this.selectedAncestriesForPhenotype(phenotype.name),
                            {
                                project: tagKpProject
                                    ? VKS_ASSOCIATION_PROJECT_KP
                                    : null,
                            }
                        );
                    })
                );
                let available = mergeCredibleSetAvailableLists(lists);
                if (tagKpProject && resolveGwasCeToken(session)) {
                    const ceAvailable = await fetchGwasCeCredibleSetsList(session);
                    available = mergeCredibleSetAvailableLists([
                        available,
                        ceAvailable,
                    ]);
                }
                if (token !== this.credibleSetsRequestToken) {
                    return false;
                }

                const region = cloneGenomicRegion(session.region);
                let selectedIds = this.credibleSetsState.selectedIds || [];
                let variantsBySet = this.credibleSetsState.variantsBySet || {};

                if (preserveSelection) {
                    ({ selectedIds, variantsBySet } = pruneCredibleSetsForRegion(
                        { selectedIds, variantsBySet },
                        region
                    ));
                }

                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    listLoading: false,
                    listError: null,
                    available,
                    selectedIds,
                    variantsBySet,
                };
                this.lastCredibleSetsListRegion = region;
                return true;
            } catch (error) {
                if (token !== this.credibleSetsRequestToken) {
                    return false;
                }
                console.warn("GP2 Browser credible sets list failed", error);
                this.credibleSetsState = {
                    ...(preserveSelection ? this.credibleSetsState : emptyCredibleSetsState()),
                    listLoading: false,
                    listError: "Failed to load credible sets for this locus.",
                };
                return false;
            }
        },
        async onAddCredibleSet({ credibleSetId, phenotype, ancestry, project }) {
            if (!credibleSetId || !this.searchSession) {
                return;
            }

            const resolvedAncestry = ancestry || "Mixed";
            const requestedPhenotype = String(phenotype || "").trim();
            const requestedProject = String(project || "").trim();
            const availableEntry =
                this.credibleSetsState.available.find(
                    (entry) =>
                        entry.credibleSetId === credibleSetId &&
                        (entry.ancestry || "Mixed") === resolvedAncestry &&
                        (!requestedPhenotype ||
                            String(entry.phenotype || "").trim() ===
                                requestedPhenotype) &&
                        (!requestedProject ||
                            String(entry.project || "").trim() ===
                                requestedProject)
                ) ||
                this.credibleSetsState.available.find(
                    (entry) =>
                        entry.credibleSetId === credibleSetId &&
                        (!requestedPhenotype ||
                            String(entry.phenotype || "").trim() ===
                                requestedPhenotype) &&
                        (!requestedProject ||
                            String(entry.project || "").trim() ===
                                requestedProject)
                ) ||
                this.credibleSetsState.available.find(
                    (entry) =>
                        entry.credibleSetId === credibleSetId &&
                        (!requestedProject ||
                            String(entry.project || "").trim() ===
                                requestedProject)
                ) ||
                this.credibleSetsState.available.find(
                    (entry) => entry.credibleSetId === credibleSetId
                );
            const resolvedProject =
                requestedProject ||
                availableEntry?.project ||
                (isGwasCeProject(this.projectId)
                    ? VKS_ASSOCIATION_PROJECT_KP
                    : "");
            const isCeSet =
                resolvedProject === VKS_ASSOCIATION_PROJECT_GWAS_CE ||
                isGwasCeCredibleSetEntry(availableEntry);
            const host = isCeSet
                ? null
                : this.bioIndexHostFor("credible-variants");
            if (!isCeSet && !host) {
                return;
            }

            const resolvedPhenotype =
                requestedPhenotype ||
                availableEntry?.phenotype ||
                (isCeSet
                    ? resolveGwasCeToken(this.searchSession)
                    : this.searchSession.phenotype?.name) ||
                null;
            const entryAncestry = availableEntry?.ancestry || resolvedAncestry;
            const selectionKey = makeCredibleSetSelectionKey(
                credibleSetId,
                entryAncestry,
                resolvedPhenotype,
                resolvedProject
            );

            if (this.credibleSetsState.selectedIds.includes(selectionKey)) {
                return;
            }

            const metaEntry = {
                ...(availableEntry || {
                    credibleSetId,
                    phenotype: resolvedPhenotype,
                    ancestry: entryAncestry,
                }),
                project: resolvedProject || availableEntry?.project || "",
            };
            const label = credibleSetShortLabel(metaEntry);
            const phenotypeSession = {
                ...this.searchSession,
                phenotype:
                    this.resolveAssociationPhenotype(resolvedPhenotype) ||
                    this.searchSession.phenotype,
                region: this.dataRegion || this.searchSession.region,
            };

            this.credibleSetsState = {
                ...this.credibleSetsState,
                selectedIds: [...this.credibleSetsState.selectedIds, selectionKey],
                variantsLoading: true,
                variantsError: null,
            };

            try {
                const rawVariants = isCeSet
                    ? await fetchGwasCeCredibleSetVariants(
                          this.searchSession,
                          credibleSetId
                      )
                    : await fetchCredibleSetVariants(
                          phenotypeSession,
                          credibleSetId,
                          host,
                          { ancestry: entryAncestry }
                      );
                const stampedRaw = (rawVariants || []).map((row) => ({
                    ...row,
                    phenotype: row.phenotype || resolvedPhenotype || "",
                    project: resolvedProject || "",
                }));
                const formattedVariants = formatCredibleVariantRows(stampedRaw).map(
                    (row) => ({
                        ...row,
                        Phenotype: formatCredibleVariantPhenotypeDisplay(
                            row.Phenotype ||
                                row.phenotype ||
                                resolvedPhenotype ||
                                "",
                            resolvedProject
                        ),
                        phenotype: row.phenotype || resolvedPhenotype || "",
                        project: resolvedProject || "",
                    })
                );
                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    variantsLoading: false,
                    variantsBySet: {
                        ...this.credibleSetsState.variantsBySet,
                        [selectionKey]: {
                            meta: {
                                selectionKey,
                                credibleSetId,
                                phenotype: resolvedPhenotype,
                                ancestry: entryAncestry,
                                project: resolvedProject || "",
                                label,
                                optionLabel: credibleSetOptionLabel(metaEntry),
                            },
                            rawVariants: stampedRaw,
                            formattedVariants,
                        },
                    },
                };
            } catch (error) {
                console.warn("GP2 Browser credible variants load failed", error);
                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    variantsLoading: false,
                    variantsError: "Failed to load credible variants for this set.",
                    selectedIds: this.credibleSetsState.selectedIds.filter(
                        (id) => id !== selectionKey
                    ),
                };
            }
        },
        async mergeCredibleSetsForPhenotype(phenotype) {
            const phenotypeName = String(phenotype?.name || "").trim();
            if (!phenotypeName || !this.searchSession) {
                return false;
            }

            const host = this.bioIndexHostFor("credible-sets");
            if (!host) {
                return false;
            }

            const session = {
                ...this.searchSession,
                phenotype,
                region: this.dataRegion || this.searchSession.region,
            };

            try {
                const tagged = await fetchCredibleSetsListForAncestries(
                    session,
                    host,
                    this.selectedAncestriesForPhenotype(phenotypeName),
                    {
                        project: isGwasCeProject(this.projectId)
                            ? VKS_ASSOCIATION_PROJECT_KP
                            : null,
                    }
                );
                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    available: mergeCredibleSetAvailableLists([
                        this.credibleSetsState.available,
                        tagged,
                    ]),
                };
                return true;
            } catch (error) {
                console.warn(
                    `GP2 Browser ${phenotypeName} credible sets list failed`,
                    error
                );
                return false;
            }
        },
        async mergeCredibleSetsForPhenotypeAncestry(phenotype, ancestry) {
            const phenotypeName = String(phenotype?.name || "").trim();
            if (
                !phenotypeName ||
                !ancestry ||
                ancestry === "Mixed" ||
                !this.searchSession
            ) {
                return;
            }

            const host = this.bioIndexHostFor("credible-sets");
            if (!host) {
                return;
            }

            const session = {
                ...this.searchSession,
                phenotype,
                region: this.dataRegion || this.searchSession.region,
            };

            try {
                const tagged = tagCredibleSetEntries(
                    await fetchCredibleSetsList(session, host, { ancestry }),
                    ancestry,
                    phenotypeName,
                    isGwasCeProject(this.projectId)
                        ? VKS_ASSOCIATION_PROJECT_KP
                        : null
                );
                const withoutMatching = (
                    this.credibleSetsState.available || []
                ).filter(
                    (entry) =>
                        !(
                            (entry.ancestry || "Mixed") === ancestry &&
                            String(entry.phenotype || "").trim() === phenotypeName &&
                            !isGwasCeCredibleSetEntry(entry)
                        )
                );
                this.credibleSetsState = {
                    ...this.credibleSetsState,
                    available: mergeCredibleSetAvailableLists([
                        withoutMatching,
                        tagged,
                    ]),
                };
            } catch (error) {
                console.warn(
                    `GP2 Browser ${phenotypeName}/${ancestry} credible sets list failed`,
                    error
                );
            }
        },
        async mergeCredibleSetsForAncestry(ancestry) {
            if (!ancestry || ancestry === "Mixed" || !this.searchSession) {
                return;
            }
            await this.mergeCredibleSetsForPhenotypeAncestry(
                this.searchSession.phenotype,
                ancestry
            );
        },
        removeCredibleSetsForAncestry(ancestry, phenotypeName = null) {
            if (!ancestry || ancestry === "Mixed") {
                return;
            }

            const primaryName = String(
                this.searchSession?.phenotype?.name || ""
            ).trim();
            const targetPhenotype = String(
                phenotypeName || primaryName || ""
            ).trim();

            const available = (this.credibleSetsState.available || []).filter(
                (entry) => {
                    if ((entry.ancestry || "Mixed") !== ancestry) {
                        return true;
                    }
                    if (!targetPhenotype) {
                        return false;
                    }
                    return (
                        String(entry.phenotype || "").trim() !== targetPhenotype
                    );
                }
            );
            const nextVariantsBySet = { ...this.credibleSetsState.variantsBySet };
            const selectedIds = (this.credibleSetsState.selectedIds || []).filter(
                (selectionKey) => {
                    const meta = nextVariantsBySet[selectionKey]?.meta;
                    const parsed = parseCredibleSetSelectionKey(selectionKey);
                    const entryAncestry = meta?.ancestry || parsed.ancestry;
                    const entryPhenotype =
                        meta?.phenotype || parsed.phenotype || "";
                    if ((entryAncestry || "Mixed") !== ancestry) {
                        return true;
                    }
                    if (
                        targetPhenotype &&
                        String(entryPhenotype).trim() !== targetPhenotype
                    ) {
                        return true;
                    }
                    delete nextVariantsBySet[selectionKey];
                    return false;
                }
            );

            this.credibleSetsState = {
                ...this.credibleSetsState,
                available,
                selectedIds,
                variantsBySet: nextVariantsBySet,
            };
        },
        removeCredibleSetsForPhenotype(phenotypeName) {
            const target = String(phenotypeName || "").trim();
            if (!target) {
                return;
            }

            const available = (this.credibleSetsState.available || []).filter(
                (entry) => String(entry.phenotype || "").trim() !== target
            );
            const nextVariantsBySet = { ...this.credibleSetsState.variantsBySet };
            const selectedIds = (this.credibleSetsState.selectedIds || []).filter(
                (selectionKey) => {
                    const meta = nextVariantsBySet[selectionKey]?.meta;
                    const parsed = parseCredibleSetSelectionKey(selectionKey);
                    const entryPhenotype =
                        meta?.phenotype || parsed.phenotype || "";
                    if (String(entryPhenotype).trim() !== target) {
                        return true;
                    }
                    delete nextVariantsBySet[selectionKey];
                    return false;
                }
            );

            this.credibleSetsState = {
                ...this.credibleSetsState,
                available,
                selectedIds,
                variantsBySet: nextVariantsBySet,
            };
        },
        onRemoveCredibleSet(selectionKey) {
            if (!selectionKey) {
                return;
            }
            const nextVariantsBySet = { ...this.credibleSetsState.variantsBySet };
            delete nextVariantsBySet[selectionKey];
            this.credibleSetsState = {
                ...this.credibleSetsState,
                selectedIds: this.credibleSetsState.selectedIds.filter(
                    (id) => id !== selectionKey
                ),
                variantsBySet: nextVariantsBySet,
                variantsError: null,
            };
        },
        async loadGenesTrack(session) {
            const token = ++this.genesRequestToken;
            this.genesState = {
                ...emptyGenesState(this.genesState?.selectedTypes),
                loading: true,
            };

            try {
                const data = await fetchGenesTrackData(
                    session.region,
                    GP2_GENOME_BUILD,
                    this.bioIndexHostFor("genes")
                );
                if (token !== this.genesRequestToken) {
                    return false;
                }
                this.genesState = {
                    ...this.genesState,
                    ready: true,
                    loading: false,
                    error: data.length ? null : "No genes found for this locus.",
                    data: data.length ? data : null,
                    selectedTypes: normalizeSelectedGeneTypes(
                        this.genesState.selectedTypes
                    ),
                };
                this.lastGenesRegion = cloneGenomicRegion(session.region);
                return true;
            } catch (error) {
                if (token !== this.genesRequestToken) {
                    return false;
                }
                console.warn("GP2 Browser genes track load failed", error);
                this.genesState = {
                    ...emptyGenesState(this.genesState?.selectedTypes),
                    error: "Failed to load genes track for this locus.",
                };
                return false;
            }
        },
        async loadPlotOverlays(session, rows) {
            const token = ++this.plotOverlaysRequestToken;
            this.plotOverlaysState = {
                ...emptyPlotOverlaysState(),
                loading: true,
            };

            let recombData = null;
            let overlayError = null;

            try {
                recombData = await fetchRecombinationRate(session.region);
            } catch (error) {
                console.warn("GP2 Browser plot overlays load failed", error);
                overlayError = "Recombination overlay could not be loaded for this locus.";
            }

            if (token !== this.plotOverlaysRequestToken) {
                return;
            }

            const leadRow = pickLeadVariantRow(rows);
            this.plotOverlaysState = {
                ready: true,
                loading: false,
                error: overlayError,
                recombData,
                refVariant: rowToLdVariant(leadRow),
                refVariantUserSet: false,
            };
        },
        async refreshRegionLdScores(rows, activeRegion, isCancelled = () => false) {
            if (!rows?.length || !activeRegion || !this.searchSession) {
                return false;
            }

            const primary = primaryAssociationAncestry(this.searchSession);
            const userPinnedRef = Boolean(this.plotOverlaysState.refVariantUserSet);
            const preferredRefRow = resolveLdReferenceRow(rows, {
                refVariant: this.plotOverlaysState.refVariant,
                refVariantUserSet: userPinnedRef,
            });
            if (!preferredRefRow) {
                this.associationsState = {
                    ...this.associationsState,
                    ldLoading: false,
                    ldError: "LD scores could not be loaded for the extended region.",
                };
                return false;
            }

            this.associationsState = {
                ...this.associationsState,
                ldLoading: true,
                ldError: null,
            };

            try {
                const ancestries = [
                    primary,
                    ...(this.associationsState.selectedAncestries || []),
                ].filter((code, index, all) => code && all.indexOf(code) === index);

                let enrichedRows = [];
                let resolvedRefVariant = rowToLdVariant(preferredRefRow);

                // Enrich KP ancestry series and GWAS-CE overlay separately.
                const kpRows = filterAssociationRowsByProject(
                    rows,
                    VKS_ASSOCIATION_PROJECT_KP
                );
                const ceRows = filterAssociationRowsByProject(
                    rows,
                    VKS_ASSOCIATION_PROJECT_GWAS_CE
                );

                for (const ancestry of ancestries) {
                    if (isCancelled()) {
                        return false;
                    }
                    const seriesRows = kpRows.filter(
                        (row) => associationRowAncestry(row, primary) === ancestry
                    );
                    if (!seriesRows.length) {
                        continue;
                    }
                    const ancestrySession = {
                        ...this.searchSession,
                        ancestry: ancestry === "Mixed" ? null : ancestry,
                        region: activeRegion,
                    };
                    if (ancestry === primary) {
                        ancestrySession.ancestry = this.searchSession.ancestry;
                    }

                    let seriesWithLd;
                    if (userPinnedRef) {
                        seriesWithLd = await enrichAssociationRowsWithLdScoresForRef(
                            seriesRows,
                            ancestrySession,
                            preferredRefRow,
                            activeRegion
                        );
                    } else {
                        const ldResult = await enrichAssociationRowsWithLdScores(
                            seriesRows,
                            ancestrySession,
                            activeRegion
                        );
                        seriesWithLd = ldResult.rows;
                        if (ldResult.refVariant) {
                            resolvedRefVariant = ldResult.refVariant;
                        }
                    }
                    enrichedRows = enrichedRows.concat(seriesWithLd);
                }

                if (ceRows.length) {
                    if (isCancelled()) {
                        return false;
                    }
                    if (userPinnedRef) {
                        enrichedRows = enrichedRows.concat(
                            await enrichAssociationRowsWithLdScoresForRef(
                                ceRows,
                                { ...this.searchSession, region: activeRegion },
                                preferredRefRow,
                                activeRegion
                            )
                        );
                    } else {
                        const ceLd = await enrichAssociationRowsWithLdScores(
                            ceRows,
                            { ...this.searchSession, region: activeRegion },
                            activeRegion
                        );
                        enrichedRows = enrichedRows.concat(ceLd.rows);
                        if (!resolvedRefVariant && ceLd.refVariant) {
                            resolvedRefVariant = ceLd.refVariant;
                        }
                    }
                }

                if (isCancelled()) {
                    return false;
                }

                enrichedRows = mergeAssociationRowsByVariantAndAncestry(
                    [],
                    enrichedRows,
                    primary
                );
                const ldAvailable = enrichedRows.some(
                    (row) => row.LDS != null && !Number.isNaN(row.LDS)
                );
                this.associationsState = {
                    ...this.associationsState,
                    ldLoading: false,
                    ldError: ldAvailable
                        ? null
                        : "LD scores could not be loaded for the extended region.",
                    rows: filterAssociationRowsInRegion(enrichedRows, activeRegion),
                };
                this.plotOverlaysState = {
                    ...this.plotOverlaysState,
                    refVariant: resolvedRefVariant,
                };
                return ldAvailable;
            } catch (ldError) {
                if (isCancelled()) {
                    return false;
                }
                console.warn("GP2 Browser LD score fetch failed", ldError);
                this.associationsState = {
                    ...this.associationsState,
                    ldLoading: false,
                    ldError: "LD scores could not be loaded for the extended region.",
                };
                return false;
            }
        },
        async loadAssociationsSectionForSession(session) {
            const token = ++this.associationsRequestToken;
            const recombToken = ++this.plotOverlaysRequestToken;
            this.associationsState = emptyAssociationsState();

            const host = this.bioIndexHostFor("associations");
            if (!host) {
                this.associationsState = {
                    ...emptyAssociationsState(),
                    error: "BioIndex host is not available.",
                };
                this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.FAILED);
                this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.FAILED);
                this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.FAILED);
                return false;
            }

            let formattedRows = [];
            let associationFailed = false;

            this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.LOADING);
            try {
                const result = await fetchAssociations(session, host, this.projectId);
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                const kpRows = formatAssociationRows(result.rows, session, {
                    project: VKS_ASSOCIATION_PROJECT_KP,
                });
                const ceRows = await this.fetchFormattedGwasCeAssociationRows(session);
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                formattedRows = mergeAssociationRowsByVariantAndAncestry(
                    kpRows,
                    ceRows,
                    primaryAssociationAncestry(session)
                );
                if (!this.dataRegion) {
                    this.dataRegion = cloneGenomicRegion(session.region);
                }
                this.lastAssociationsRegion = cloneGenomicRegion(session.region);
                this.associationsState = {
                    ...this.associationsState,
                    error: null,
                    ldError: null,
                    rows: formattedRows,
                    index: result.index,
                    query: result.q,
                };
                this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.DONE);
            } catch (error) {
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                const ceRows = await this.fetchFormattedGwasCeAssociationRows(session);
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                if (ceRows.length) {
                    formattedRows = ceRows;
                    associationFailed = false;
                    if (!this.dataRegion) {
                        this.dataRegion = cloneGenomicRegion(session.region);
                    }
                    this.lastAssociationsRegion = cloneGenomicRegion(session.region);
                    this.associationsState = {
                        ...this.associationsState,
                        error: null,
                        ldError: null,
                        rows: formattedRows,
                        index: null,
                        query: null,
                    };
                    this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.DONE);
                    console.warn(
                        "GP2 Browser KP associations load failed; showing GWAS-CE only",
                        error
                    );
                } else {
                    associationFailed = true;
                    console.warn("GP2 Browser associations load failed", error);
                    this.associationsState = {
                        ...emptyAssociationsState(),
                        error: "Failed to load associations. Please try again.",
                    };
                    this.setRegionLoadStep(
                        "associations",
                        VKS_REGION_LOAD_STATUS.FAILED
                    );
                }
            }

            if (token !== this.associationsRequestToken) {
                return false;
            }

            this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.LOADING);
            try {
                const recombData = await fetchRecombinationRate(session.region);
                if (recombToken === this.plotOverlaysRequestToken) {
                    const leadRow =
                        pickLeadVariantRow(
                            filterAssociationRowsByProject(
                                formattedRows,
                                VKS_ASSOCIATION_PROJECT_KP
                            )
                        ) || pickLeadVariantRow(formattedRows);
                    this.plotOverlaysState = {
                        ready: true,
                        loading: false,
                        error: null,
                        recombData,
                        refVariant: rowToLdVariant(leadRow),
                        refVariantUserSet: false,
                    };
                    this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.DONE);
                }
            } catch (error) {
                if (recombToken === this.plotOverlaysRequestToken) {
                    console.warn("GP2 Browser plot overlays load failed", error);
                    this.plotOverlaysState = {
                        ...emptyPlotOverlaysState(),
                        ready: true,
                        error: "Recombination overlay could not be loaded for this locus.",
                    };
                    this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.FAILED);
                }
            }

            if (!associationFailed && formattedRows.length) {
                this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                try {
                    const ldResult = await this.enrichAssociationRowsByProject(
                        formattedRows,
                        session
                    );
                    const rowsWithLd = ldResult.rows;
                    if (token !== this.associationsRequestToken) {
                        return false;
                    }
                    const ldAvailable = rowsWithLd.some(
                        (row) => row.LDS != null && !Number.isNaN(row.LDS)
                    );
                    this.associationsState = {
                        ...this.associationsState,
                        ldError: ldAvailable
                            ? null
                            : "LD scores could not be loaded for this locus.",
                        rows: rowsWithLd,
                    };
                    const resolvedRef =
                        ldResult.refVariant ||
                        rowToLdVariant(pickLeadVariantRow(rowsWithLd));
                    this.plotOverlaysState = {
                        ...this.plotOverlaysState,
                        refVariant: resolvedRef,
                    };
                    if (this.viewingCenterBp == null) {
                        const refPos = extractVariantPositionBp(resolvedRef);
                        if (refPos != null) {
                            this.viewingCenterBp = clampViewingCenterBp(
                                refPos,
                                session.region,
                                this.viewingRegionHalfWidthBp
                            );
                        }
                    }
                    this.setRegionLoadStep(
                        "ld",
                        ldAvailable
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                } catch (ldError) {
                    if (token !== this.associationsRequestToken) {
                        return false;
                    }
                    console.warn("GP2 Browser LD score fetch failed", ldError);
                    this.associationsState = {
                        ...this.associationsState,
                        ldError: "LD scores could not be loaded for this locus.",
                    };
                    this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.FAILED);
                }
            } else {
                this.setRegionLoadStep(
                    "ld",
                    associationFailed
                        ? VKS_REGION_LOAD_STATUS.FAILED
                        : VKS_REGION_LOAD_STATUS.DONE
                );
            }

            if (!associationFailed) {
                this.probeAncestryAssociationAvailabilityForSession(session);
                this.lastZoomedAssociationsRegion = null;
                this.refetchAssociationsForZoomedView();
            }
            this.loadPendingAssociationExtras();
            return !associationFailed;
        },
        async loadInitialSearchData(session) {
            const token = ++this.associationsRequestToken;
            ++this.genesRequestToken;
            ++this.plotOverlaysRequestToken;
            ++this.credibleSetsRequestToken;
            ++this.globalEnrichmentRequestToken;

            if (this.regionLoadDismissTimer) {
                clearTimeout(this.regionLoadDismissTimer);
                this.regionLoadDismissTimer = null;
            }
            const associationsOnly = projectAssociationsOnly(this.projectId);
            const loadAssoc = this.isSectionChecked("associations");
            const loadGenes = !associationsOnly && this.isSectionChecked("genes");
            const loadCs =
                !associationsOnly && this.isSectionChecked("credible-sets");
            const loadGe =
                !associationsOnly && this.isSectionChecked("global-enrichment");
            const loadV2g =
                !associationsOnly &&
                this.isSectionChecked("variant-to-gene-links") &&
                !loadGe;
            const loadS2g =
                !associationsOnly && this.isSectionChecked("snp2gene-links");

            const initialStepIds = [
                ...(loadAssoc ? ["associations", "recomb", "ld"] : []),
                ...(loadGenes ? ["genes"] : []),
                ...(loadCs ? ["credibleSets"] : []),
                ...(loadGe ? ["globalEnrichment"] : []),
                ...(loadV2g ? ["geneLinks"] : []),
                ...(loadS2g ? ["snp2geneLinks"] : []),
            ];

            this.regionLoadProgress = startGp2RegionLoadProgress(initialStepIds);
            this.associationsState = emptyAssociationsState();

            if (!initialStepIds.length) {
                this.closeRegionLoadProgressIfSettled();
                return;
            }

            const host = this.bioIndexHostFor("associations");
            if (loadAssoc && !host) {
                this.associationsState = {
                    ...emptyAssociationsState(),
                    error: "BioIndex host is not available.",
                };
                for (const step of this.regionLoadProgress.steps) {
                    this.setRegionLoadStep(step.id, VKS_REGION_LOAD_STATUS.FAILED);
                }
                this.closeRegionLoadProgressIfSettled();
                return;
            }

            let formattedRows = [];
            let associationFailed = false;

            if (loadAssoc) {
                this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.LOADING);
                try {
                    const result = await fetchAssociations(session, host, this.projectId);
                    if (token !== this.associationsRequestToken) {
                        return;
                    }

                    const kpRows = formatAssociationRows(result.rows, session, {
                        project: VKS_ASSOCIATION_PROJECT_KP,
                    });
                    const ceRows = await this.fetchFormattedGwasCeAssociationRows(session);
                    if (token !== this.associationsRequestToken) {
                        return;
                    }

                    formattedRows = mergeAssociationRowsByVariantAndAncestry(
                        kpRows,
                        ceRows,
                        primaryAssociationAncestry(session)
                    );
                    if (!this.dataRegion) {
                        this.dataRegion = cloneGenomicRegion(session.region);
                    }
                    this.lastAssociationsRegion = cloneGenomicRegion(session.region);
                    this.associationsState = {
                        ...this.associationsState,
                        error: null,
                        ldError: null,
                        rows: formattedRows,
                        index: result.index,
                        query: result.q,
                    };
                    this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.DONE);
                } catch (error) {
                    if (token !== this.associationsRequestToken) {
                        return;
                    }
                    // KP failed — still try CE overlay so the page is not empty.
                    const ceRows = await this.fetchFormattedGwasCeAssociationRows(session);
                    if (token !== this.associationsRequestToken) {
                        return;
                    }
                    if (ceRows.length) {
                        formattedRows = ceRows;
                        associationFailed = false;
                        if (!this.dataRegion) {
                            this.dataRegion = cloneGenomicRegion(session.region);
                        }
                        this.lastAssociationsRegion = cloneGenomicRegion(session.region);
                        this.associationsState = {
                            ...this.associationsState,
                            error: null,
                            ldError: null,
                            rows: formattedRows,
                            index: null,
                            query: null,
                        };
                        this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.DONE);
                        console.warn("GP2 Browser KP associations load failed; showing GWAS-CE only", error);
                    } else {
                        associationFailed = true;
                        console.warn("GP2 Browser associations load failed", error);
                        this.associationsState = {
                            ...emptyAssociationsState(),
                            error: "Failed to load associations. Please try again.",
                        };
                        this.setRegionLoadStep("associations", VKS_REGION_LOAD_STATUS.FAILED);
                    }
                }
            }

            if (token !== this.associationsRequestToken) {
                return;
            }

            if (projectAssociationsOnly(this.projectId)) {
                if (loadAssoc) {
                    this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.LOADING);
                    try {
                        const recombData = await fetchRecombinationRate(session.region);
                        if (token !== this.associationsRequestToken) {
                            return;
                        }
                        const leadRow =
                            pickLeadVariantRow(
                                filterAssociationRowsByProject(
                                    formattedRows,
                                    VKS_ASSOCIATION_PROJECT_KP
                                )
                            ) || pickLeadVariantRow(formattedRows);
                        this.plotOverlaysState = {
                            ready: true,
                            loading: false,
                            error: null,
                            recombData,
                            refVariant: rowToLdVariant(leadRow),
                            refVariantUserSet: false,
                        };
                        this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.DONE);
                    } catch (error) {
                        if (token !== this.associationsRequestToken) {
                            return;
                        }
                        console.warn("GP2 Browser plot overlays load failed", error);
                        this.plotOverlaysState = {
                            ...emptyPlotOverlaysState(),
                            ready: true,
                            error: "Recombination overlay could not be loaded for this locus.",
                        };
                        this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.FAILED);
                    }

                    if (!associationFailed && formattedRows.length) {
                        this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                        try {
                            const ldResult = await this.enrichAssociationRowsByProject(
                                formattedRows,
                                session
                            );
                            const rowsWithLd = ldResult.rows;
                            if (token !== this.associationsRequestToken) {
                                return;
                            }
                            const ldAvailable = rowsWithLd.some(
                                (row) => row.LDS != null && !Number.isNaN(row.LDS)
                            );
                            this.associationsState = {
                                ...this.associationsState,
                                rows: rowsWithLd,
                                ldLoading: false,
                                ldError: ldAvailable
                                    ? null
                                    : "LD scores could not be loaded for this locus.",
                            };
                            if (ldResult.refVariant) {
                                this.plotOverlaysState = {
                                    ...this.plotOverlaysState,
                                    refVariant: ldResult.refVariant,
                                };
                            }
                            this.setRegionLoadStep(
                                "ld",
                                ldAvailable
                                    ? VKS_REGION_LOAD_STATUS.DONE
                                    : VKS_REGION_LOAD_STATUS.FAILED
                            );
                        } catch (error) {
                            if (token !== this.associationsRequestToken) {
                                return;
                            }
                            console.warn("GP2 Browser LD enrich failed", error);
                            this.associationsState = {
                                ...this.associationsState,
                                ldLoading: false,
                                ldError: "LD scores could not be loaded for this locus.",
                            };
                            this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.FAILED);
                        }
                    } else {
                        this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.DONE);
                    }
                }

                this.closeRegionLoadProgressIfSettled();
                return;
            }

            const genesToken = ++this.genesRequestToken;
            const recombToken = ++this.plotOverlaysRequestToken;
            const globalEnrichmentToken = ++this.globalEnrichmentRequestToken;

            if (loadGenes) {
                this.setRegionLoadStep("genes", VKS_REGION_LOAD_STATUS.LOADING);
            }
            if (loadAssoc) {
                this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.LOADING);
            }
            if (loadCs) {
                this.setRegionLoadStep("credibleSets", VKS_REGION_LOAD_STATUS.LOADING);
            }
            if (loadGe) {
                this.setRegionLoadStep("globalEnrichment", VKS_REGION_LOAD_STATUS.LOADING);
            }
            if (loadV2g) {
                this.setRegionLoadStep("geneLinks", VKS_REGION_LOAD_STATUS.LOADING);
            }
            if (loadS2g) {
                this.setRegionLoadStep("snp2geneLinks", VKS_REGION_LOAD_STATUS.LOADING);
            }

            await Promise.all([
                (async () => {
                    if (!loadGenes) {
                        return;
                    }
                    try {
                        const data = await fetchGenesTrackData(
                            session.region,
                            GP2_GENOME_BUILD,
                            this.bioIndexHostFor("genes")
                        );
                        if (genesToken !== this.genesRequestToken) {
                            return;
                        }
                        this.genesState = {
                            ...this.genesState,
                            ready: true,
                            loading: false,
                            error: data.length ? null : "No genes found for this locus.",
                            data: data.length ? data : null,
                            selectedTypes: normalizeSelectedGeneTypes(
                                this.genesState.selectedTypes
                            ),
                        };
                        this.lastGenesRegion = cloneGenomicRegion(session.region);
                        this.setRegionLoadStep("genes", VKS_REGION_LOAD_STATUS.DONE);
                    } catch (error) {
                        if (genesToken !== this.genesRequestToken) {
                            return;
                        }
                        console.warn("GP2 Browser genes track load failed", error);
                        this.genesState = {
                            ...emptyGenesState(this.genesState?.selectedTypes),
                            error: "Failed to load genes track for this locus.",
                        };
                        this.setRegionLoadStep("genes", VKS_REGION_LOAD_STATUS.FAILED);
                    }
                })(),
                (async () => {
                    if (!loadAssoc) {
                        return;
                    }
                    try {
                        const recombData = await fetchRecombinationRate(session.region);
                        if (recombToken !== this.plotOverlaysRequestToken) {
                            return;
                        }
                        const leadRow = pickLeadVariantRow(formattedRows);
                        this.plotOverlaysState = {
                            ready: true,
                            loading: false,
                            error: null,
                            recombData,
                            refVariant: rowToLdVariant(leadRow),
                            refVariantUserSet: false,
                        };
                        this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.DONE);
                    } catch (error) {
                        if (recombToken !== this.plotOverlaysRequestToken) {
                            return;
                        }
                        console.warn("GP2 Browser plot overlays load failed", error);
                        this.plotOverlaysState = {
                            ...emptyPlotOverlaysState(),
                            ready: true,
                            error: "Recombination overlay could not be loaded for this locus.",
                        };
                        this.setRegionLoadStep("recomb", VKS_REGION_LOAD_STATUS.FAILED);
                    }
                })(),
                (async () => {
                    if (!loadCs) {
                        return;
                    }
                    const ok = await this.loadCredibleSetsList(session);
                    if (ok) {
                        this.setRegionLoadStep(
                            "credibleSets",
                            VKS_REGION_LOAD_STATUS.DONE
                        );
                    } else if (this.credibleSetsState?.listError) {
                        this.setRegionLoadStep(
                            "credibleSets",
                            VKS_REGION_LOAD_STATUS.FAILED
                        );
                    }
                })(),
                (async () => {
                    if (!loadGe) {
                        return;
                    }
                    await this.loadGlobalEnrichmentForSession(session, {
                        offerClassifyAction: true,
                        requestToken: globalEnrichmentToken,
                    });
                })(),
                (async () => {
                    if (!loadV2g) {
                        return;
                    }
                    const ok = await this.loadV2gTissueCatalogForSession(session);
                    this.setRegionLoadStep(
                        "geneLinks",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
                (async () => {
                    if (!loadS2g) {
                        return;
                    }
                    const ok = await this.onS2gLoad();
                    this.setRegionLoadStep(
                        "snp2geneLinks",
                        ok
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                })(),
            ]);

            if (token !== this.associationsRequestToken) {
                return;
            }

            if (loadAssoc) {
                if (!associationFailed && formattedRows.length) {
                    this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                    try {
                        const ldResult = await this.enrichAssociationRowsByProject(
                            formattedRows,
                            session
                        );
                        const rowsWithLd = ldResult.rows;
                        if (token !== this.associationsRequestToken) {
                            return;
                        }
                        const ldAvailable = rowsWithLd.some(
                            (row) => row.LDS != null && !Number.isNaN(row.LDS)
                        );
                        this.associationsState = {
                            ...this.associationsState,
                            ldError: ldAvailable
                                ? null
                                : "LD scores could not be loaded for this locus.",
                            rows: rowsWithLd,
                        };
                        const resolvedRef =
                            ldResult.refVariant ||
                            rowToLdVariant(pickLeadVariantRow(rowsWithLd));
                        this.plotOverlaysState = {
                            ...this.plotOverlaysState,
                            refVariant: resolvedRef,
                        };
                        if (this.viewingCenterBp == null) {
                            const refPos = extractVariantPositionBp(resolvedRef);
                            if (refPos != null) {
                                this.viewingCenterBp = clampViewingCenterBp(
                                    refPos,
                                    session.region,
                                    this.viewingRegionHalfWidthBp
                                );
                            }
                        }
                        this.setRegionLoadStep(
                            "ld",
                            ldAvailable
                                ? VKS_REGION_LOAD_STATUS.DONE
                                : VKS_REGION_LOAD_STATUS.FAILED
                        );
                    } catch (ldError) {
                        if (token !== this.associationsRequestToken) {
                            return;
                        }
                        console.warn("GP2 Browser LD score fetch failed", ldError);
                        this.associationsState = {
                            ...this.associationsState,
                            ldLoading: false,
                            ldError: "LD scores could not be loaded for this locus.",
                        };
                        this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.FAILED);
                    }
                } else {
                    this.setRegionLoadStep(
                        "ld",
                        associationFailed
                            ? VKS_REGION_LOAD_STATUS.FAILED
                            : VKS_REGION_LOAD_STATUS.DONE
                    );
                }
            }

            this.closeRegionLoadProgressIfSettled();
            if (loadAssoc) {
                if (!associationFailed) {
                    this.probeAncestryAssociationAvailabilityForSession(session);
                    this.lastZoomedAssociationsRegion = null;
                    this.refetchAssociationsForZoomedView();
                }
                // Primary page data is loaded; now fetch URL/session sub-ancestries
                // and additive phenotypes.
                this.loadPendingAssociationExtras();
            }
        },
        async loadPendingAssociationExtras() {
            const pendingAncestries = parseSubAncestriesParam(
                this.pendingSubAncestries,
                primaryAssociationAncestry(this.searchSession)
            );
            // Consume ancestry queue up front so later URL sync does not re-apply.
            this.pendingSubAncestries = [];

            const pendingPhenotypeNames = parsePhenotypesParam(
                this.pendingPhenotypes
            ).filter((name) => name !== this.searchSession?.phenotype?.name);
            const showProgress =
                pendingPhenotypeNames.length > 0 || pendingAncestries.length > 0;

            if (showProgress) {
                this.beginAdditivePhenotypeLoadProgress();
            }

            let associationsOk = true;
            try {
                // Additive phenotypes first (primary ancestry), then apply URL
                // sub-ancestries to every phenotype on the canvas.
                await this.loadPendingPhenotypes({
                    syncUrl: false,
                    showProgress: false,
                });
                await this.loadPendingSubAncestriesForPhenotypes(pendingAncestries, {
                    syncUrl: false,
                });
            } catch (error) {
                associationsOk = false;
                console.warn("GP2 Browser pending association extras failed", error);
            }

            if (showProgress) {
                this.finishAdditivePhenotypeLoadProgress({
                    associationsOk,
                    ldOk: associationsOk,
                });
            }

            if (this.searchSession) {
                this.syncUrlSearchParams(this.searchSession);
            }
        },
        associationPhenotypesOnCanvas() {
            const phenotypes = [];
            const primary = this.searchSession?.phenotype;
            const primaryName = String(primary?.name || "").trim();
            if (primaryName) {
                phenotypes.push(primary);
            }
            (this.associationsState.selectedPhenotypes || []).forEach((entry) => {
                const name = typeof entry === "string" ? entry : entry?.name;
                const trimmed = String(name || "").trim();
                if (!trimmed || trimmed === primaryName) {
                    return;
                }
                if (phenotypes.some((item) => item?.name === trimmed)) {
                    return;
                }
                phenotypes.push(
                    typeof entry === "string"
                        ? { name: entry }
                        : {
                              name: entry.name,
                              description: entry.description || null,
                          }
                );
            });
            return phenotypes;
        },
        async loadPendingSubAncestries({ syncUrl = true } = {}) {
            const pending = parseSubAncestriesParam(
                this.pendingSubAncestries,
                primaryAssociationAncestry(this.searchSession)
            );
            this.pendingSubAncestries = [];
            await this.loadPendingSubAncestriesForPhenotypes(pending, { syncUrl });
        },
        async loadPendingSubAncestriesForPhenotypes(
            pendingAncestries = [],
            { syncUrl = true } = {}
        ) {
            const pending = parseSubAncestriesParam(
                pendingAncestries,
                primaryAssociationAncestry(this.searchSession)
            );
            if (!pending.length || !this.searchSession) {
                if (syncUrl) {
                    this.syncUrlSearchParams(this.searchSession);
                }
                return;
            }

            const token = this.associationsRequestToken;
            const phenotypes = this.associationPhenotypesOnCanvas();
            for (const ancestry of pending) {
                for (const phenotype of phenotypes) {
                    if (token !== this.associationsRequestToken) {
                        return;
                    }
                    await this.loadAssociationAncestrySeries(ancestry, phenotype);
                }
            }
            if (syncUrl && token === this.associationsRequestToken) {
                this.syncUrlSearchParams(this.searchSession);
            }
        },
        async loadPendingPhenotypes({ syncUrl = true, showProgress = false } = {}) {
            const pending = parsePhenotypesParam(this.pendingPhenotypes).filter(
                (name) => name !== this.searchSession?.phenotype?.name
            );
            this.pendingPhenotypes = [];
            if (!pending.length || !this.searchSession) {
                if (syncUrl) {
                    this.syncUrlSearchParams(this.searchSession);
                }
                return;
            }

            const token = this.associationsRequestToken;
            for (const name of pending) {
                if (token !== this.associationsRequestToken) {
                    return;
                }
                if (
                    (this.associationsState.selectedPhenotypes || []).some(
                        (entry) => entry?.name === name
                    )
                ) {
                    continue;
                }
                const phenotype =
                    (this.phenotypes || this.phenotypesInUse || []).find(
                        (entry) => entry.name === name
                    ) || { name, description: null };
                await this.loadAssociationPhenotypeSeries(phenotype, {
                    syncUrl: false,
                    showProgress,
                });
            }
            if (syncUrl && token === this.associationsRequestToken) {
                this.syncUrlSearchParams(this.searchSession);
            }
        },
        async probeAncestryAssociationAvailabilityForSession(session) {
            if (!session?.phenotype) {
                return;
            }
            await this.probeAncestryAssociationAvailabilityForPhenotype(
                session.phenotype,
                session
            );
        },
        async probeAncestryAssociationAvailabilityForPhenotype(
            phenotype,
            session = null
        ) {
            const host = this.bioIndexHostFor("ancestry-associations");
            const token = this.associationsRequestToken;
            const phenotypeName = String(phenotype?.name || "").trim();
            const baseSession = session || this.searchSession;
            if (!phenotypeName || !baseSession?.region || !host) {
                return;
            }

            const primaryName = String(baseSession?.phenotype?.name || "").trim();
            const isPrimary = phenotypeName === primaryName;

            this.associationsState = {
                ...this.associationsState,
                ancestryAvailabilityLoading: isPrimary
                    ? true
                    : this.associationsState.ancestryAvailabilityLoading,
                ancestryAvailabilityError: isPrimary
                    ? null
                    : this.associationsState.ancestryAvailabilityError,
                ancestryAvailabilityLoadingByPhenotype: {
                    ...this.associationsState.ancestryAvailabilityLoadingByPhenotype,
                    [phenotypeName]: true,
                },
                ancestryAvailabilityErrorByPhenotype: {
                    ...this.associationsState.ancestryAvailabilityErrorByPhenotype,
                    [phenotypeName]: null,
                },
            };

            try {
                const probeSession = {
                    ...baseSession,
                    phenotype,
                    region: this.dataRegion || baseSession.region,
                };
                const availability = await probeAncestryAssociationAvailability(
                    probeSession,
                    host,
                    this.projectId
                );
                if (token !== this.associationsRequestToken) {
                    return;
                }
                this.associationsState = {
                    ...this.associationsState,
                    ancestryAvailability: isPrimary
                        ? availability
                        : this.associationsState.ancestryAvailability,
                    ancestryAvailabilityLoading: isPrimary
                        ? false
                        : this.associationsState.ancestryAvailabilityLoading,
                    ancestryAvailabilityError: isPrimary
                        ? null
                        : this.associationsState.ancestryAvailabilityError,
                    ancestryAvailabilityByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityByPhenotype,
                        [phenotypeName]: availability,
                    },
                    ancestryAvailabilityLoadingByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityLoadingByPhenotype,
                        [phenotypeName]: false,
                    },
                    ancestryAvailabilityErrorByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityErrorByPhenotype,
                        [phenotypeName]: null,
                    },
                };
            } catch (error) {
                if (token !== this.associationsRequestToken) {
                    return;
                }
                console.warn(
                    `GP2 Browser ancestry availability probe failed for ${phenotypeName}`,
                    error
                );
                this.associationsState = {
                    ...this.associationsState,
                    ancestryAvailability: isPrimary
                        ? []
                        : this.associationsState.ancestryAvailability,
                    ancestryAvailabilityLoading: isPrimary
                        ? false
                        : this.associationsState.ancestryAvailabilityLoading,
                    ancestryAvailabilityError: isPrimary
                        ? "Could not check ancestry-specific association availability."
                        : this.associationsState.ancestryAvailabilityError,
                    ancestryAvailabilityByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityByPhenotype,
                        [phenotypeName]: [],
                    },
                    ancestryAvailabilityLoadingByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityLoadingByPhenotype,
                        [phenotypeName]: false,
                    },
                    ancestryAvailabilityErrorByPhenotype: {
                        ...this.associationsState.ancestryAvailabilityErrorByPhenotype,
                        [phenotypeName]:
                            "Could not check ancestry-specific association availability.",
                    },
                };
            }
        },
        resolveAssociationPhenotype(phenotypeName) {
            const primary = this.searchSession?.phenotype;
            const primaryName = String(primary?.name || "").trim();
            const target = String(phenotypeName || "").trim() || primaryName;
            if (!target || target === primaryName) {
                return primary || null;
            }
            const entry = (this.associationsState.selectedPhenotypes || []).find(
                (item) => String(item?.name || "").trim() === target
            );
            return entry
                ? {
                      name: entry.name,
                      description: entry.description || null,
                  }
                : { name: target };
        },
        selectedAncestriesForPhenotype(phenotypeName) {
            const primaryName = String(this.searchSession?.phenotype?.name || "").trim();
            const target = String(phenotypeName || "").trim() || primaryName;
            if (!target || target === primaryName) {
                return this.associationsState.selectedAncestries || [];
            }
            const entry = (this.associationsState.selectedPhenotypes || []).find(
                (item) => String(item?.name || "").trim() === target
            );
            return entry?.selectedAncestries || [];
        },
        async onToggleAssociationAncestry(payload) {
            const ancestry =
                typeof payload === "string" ? payload : payload?.ancestry;
            const phenotypeName =
                typeof payload === "string" || !payload?.phenotype
                    ? this.searchSession?.phenotype?.name
                    : payload.phenotype;
            const primaryAncestry = primaryAssociationAncestry(this.searchSession);
            if (!ancestry || ancestry === primaryAncestry || !this.searchSession) {
                return;
            }

            const phenotype = this.resolveAssociationPhenotype(phenotypeName);
            const resolvedName = String(phenotype?.name || "").trim();
            const primaryName = String(this.searchSession?.phenotype?.name || "").trim();
            const isPrimary = !resolvedName || resolvedName === primaryName;
            const selected = this.selectedAncestriesForPhenotype(resolvedName);

            if (selected.includes(ancestry)) {
                const nextRows = this.associationsState.rows.filter((row) => {
                    if (associationRowAncestry(row, primaryAncestry) !== ancestry) {
                        return true;
                    }
                    const rowPhenotype = associationRowPhenotype(row, primaryName);
                    if (isPrimary) {
                        return rowPhenotype && rowPhenotype !== primaryName;
                    }
                    return rowPhenotype !== resolvedName;
                });

                if (isPrimary) {
                    this.associationsState = {
                        ...this.associationsState,
                        selectedAncestries: selected.filter(
                            (code) => code !== ancestry
                        ),
                        rows: nextRows,
                        ancestrySeriesErrors: {
                            ...this.associationsState.ancestrySeriesErrors,
                            [ancestrySeriesLoadingKey(primaryName, ancestry)]: null,
                            [ancestry]: null,
                        },
                    };
                    this.removeCredibleSetsForAncestry(ancestry);
                } else {
                    this.associationsState = {
                        ...this.associationsState,
                        selectedPhenotypes: (
                            this.associationsState.selectedPhenotypes || []
                        ).map((entry) =>
                            entry?.name === resolvedName
                                ? {
                                      ...entry,
                                      selectedAncestries: (
                                          entry.selectedAncestries || []
                                      ).filter((code) => code !== ancestry),
                                  }
                                : entry
                        ),
                        rows: nextRows,
                        ancestrySeriesErrors: {
                            ...this.associationsState.ancestrySeriesErrors,
                            [ancestrySeriesLoadingKey(resolvedName, ancestry)]: null,
                        },
                    };
                    this.removeCredibleSetsForAncestry(ancestry, resolvedName);
                }
                this.syncUrlSearchParams(this.searchSession);
                return;
            }

            await this.loadAssociationAncestrySeries(ancestry, phenotype);
            this.syncUrlSearchParams(this.searchSession);
        },
        async loadAssociationAncestrySeries(ancestry, phenotype = null) {
            const primaryAncestry = primaryAssociationAncestry(this.searchSession);
            const phenotypeObj =
                phenotype || this.searchSession?.phenotype || null;
            const phenotypeName = String(phenotypeObj?.name || "").trim();
            const primaryName = String(
                this.searchSession?.phenotype?.name || ""
            ).trim();
            const isPrimary = !phenotypeName || phenotypeName === primaryName;
            if (!ancestry || ancestry === primaryAncestry || !this.searchSession) {
                return false;
            }
            if (this.selectedAncestriesForPhenotype(phenotypeName).includes(ancestry)) {
                return true;
            }

            const host = this.bioIndexHostFor("ancestry-associations");
            if (!host) {
                return false;
            }

            const token = this.associationsRequestToken;
            const loadingKey = ancestrySeriesLoadingKey(
                isPrimary ? primaryName : phenotypeName,
                ancestry
            );
            const selected = this.selectedAncestriesForPhenotype(phenotypeName);
            this.associationsState = {
                ...this.associationsState,
                ancestrySeriesLoading: {
                    ...this.associationsState.ancestrySeriesLoading,
                    [loadingKey]: true,
                    ...(isPrimary ? { [ancestry]: true } : {}),
                },
                ancestrySeriesErrors: {
                    ...this.associationsState.ancestrySeriesErrors,
                    [loadingKey]: null,
                    ...(isPrimary ? { [ancestry]: null } : {}),
                },
            };

            try {
                const ancestrySession = {
                    ...this.searchSession,
                    phenotype: phenotypeObj,
                    ancestry,
                    region: this.dataRegion || this.searchSession.region,
                };
                const result = await fetchAssociations(
                    ancestrySession,
                    host,
                    this.projectId
                );
                if (token !== this.associationsRequestToken) {
                    return false;
                }

                let formattedRows = formatAssociationRows(result.rows, ancestrySession, {
                    project: VKS_ASSOCIATION_PROJECT_KP,
                });
                try {
                    const refRow = resolveLdReferenceRow(
                        filterAssociationRowsByProject(
                            this.associationsState.rows,
                            VKS_ASSOCIATION_PROJECT_KP
                        ),
                        {
                            refVariant: this.plotOverlaysState?.refVariant,
                            refVariantUserSet: this.plotOverlaysState?.refVariantUserSet,
                        }
                    );
                    if (refRow) {
                        formattedRows = await enrichAssociationRowsWithLdScoresForRef(
                            formattedRows,
                            ancestrySession,
                            refRow,
                            ancestrySession.region
                        );
                    } else {
                        const ldResult = await enrichAssociationRowsWithLdScores(
                            formattedRows,
                            ancestrySession
                        );
                        formattedRows = ldResult.rows;
                    }
                } catch (ldError) {
                    console.warn(`GP2 Browser ${ancestry} LD enrich failed`, ldError);
                }

                if (token !== this.associationsRequestToken) {
                    return false;
                }

                const mergedRows = mergeAssociationRowsByVariantAndAncestry(
                    this.associationsState.rows,
                    formattedRows,
                    primaryAncestry
                );
                const emptyMessage = formattedRows.length
                    ? null
                    : "No associations returned for this ancestry.";

                if (isPrimary) {
                    this.associationsState = {
                        ...this.associationsState,
                        selectedAncestries: [...selected, ancestry],
                        rows: mergedRows,
                        ancestrySeriesLoading: {
                            ...this.associationsState.ancestrySeriesLoading,
                            [loadingKey]: false,
                            [ancestry]: false,
                        },
                        ancestrySeriesErrors: {
                            ...this.associationsState.ancestrySeriesErrors,
                            [loadingKey]: emptyMessage,
                            [ancestry]: emptyMessage,
                        },
                    };
                    if (this.isSectionChecked("credible-sets")) {
                        await this.mergeCredibleSetsForAncestry(ancestry);
                    } else {
                        this.lastCredibleSetsListRegion = null;
                    }
                    this.offerUnderstudiedForAncestry(ancestry);
                } else {
                    this.associationsState = {
                        ...this.associationsState,
                        selectedPhenotypes: (
                            this.associationsState.selectedPhenotypes || []
                        ).map((entry) =>
                            entry?.name === phenotypeName
                                ? {
                                      ...entry,
                                      selectedAncestries: [
                                          ...(entry.selectedAncestries || []),
                                          ancestry,
                                      ],
                                  }
                                : entry
                        ),
                        rows: mergedRows,
                        ancestrySeriesLoading: {
                            ...this.associationsState.ancestrySeriesLoading,
                            [loadingKey]: false,
                        },
                        ancestrySeriesErrors: {
                            ...this.associationsState.ancestrySeriesErrors,
                            [loadingKey]: emptyMessage,
                        },
                    };
                    if (this.isSectionChecked("credible-sets")) {
                        await this.mergeCredibleSetsForPhenotypeAncestry(
                            phenotypeObj,
                            ancestry
                        );
                    } else {
                        this.lastCredibleSetsListRegion = null;
                    }
                }
                this.lastZoomedAssociationsRegion = null;
                this.refetchAssociationsForZoomedView();
                return true;
            } catch (error) {
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                console.warn(`GP2 Browser ${ancestry} associations load failed`, error);
                this.associationsState = {
                    ...this.associationsState,
                    ancestrySeriesLoading: {
                        ...this.associationsState.ancestrySeriesLoading,
                        [loadingKey]: false,
                        ...(isPrimary ? { [ancestry]: false } : {}),
                    },
                    ancestrySeriesErrors: {
                        ...this.associationsState.ancestrySeriesErrors,
                        [loadingKey]: "Failed to load associations for this ancestry.",
                        ...(isPrimary
                            ? {
                                  [ancestry]:
                                      "Failed to load associations for this ancestry.",
                              }
                            : {}),
                    },
                };
                return false;
            }
        },
        async loadAssociationPhenotypeSeries(
            phenotype,
            { syncUrl = true, showProgress = true } = {}
        ) {
            const primary = this.searchSession?.phenotype;
            const phenotypeName = String(phenotype?.name || "").trim();
            if (!phenotypeName || !this.searchSession) {
                return false;
            }
            if (phenotypeName === String(primary?.name || "").trim()) {
                return false;
            }
            const selected = this.associationsState.selectedPhenotypes || [];
            if (selected.some((entry) => entry?.name === phenotypeName)) {
                return true;
            }

            const host = this.bioIndexHostFor("associations");
            if (!host) {
                return false;
            }

            const token = this.associationsRequestToken;
            const primaryAncestry = primaryAssociationAncestry(this.searchSession);
            const ownsProgress = Boolean(showProgress);
            if (ownsProgress) {
                this.beginAdditivePhenotypeLoadProgress();
            }

            this.associationsState = {
                ...this.associationsState,
                phenotypeSeriesLoading: {
                    ...this.associationsState.phenotypeSeriesLoading,
                    [phenotypeName]: true,
                },
                phenotypeSeriesErrors: {
                    ...this.associationsState.phenotypeSeriesErrors,
                    [phenotypeName]: null,
                },
            };

            let associationsOk = false;
            let ldOk = true;
            try {
                if (ownsProgress) {
                    this.setRegionLoadStep(
                        "associations",
                        VKS_REGION_LOAD_STATUS.LOADING
                    );
                }
                const phenotypeSession = {
                    ...this.searchSession,
                    phenotype,
                    ancestry: primaryAncestry,
                    region: this.dataRegion || this.searchSession.region,
                };
                const result = await fetchAssociations(
                    phenotypeSession,
                    host,
                    this.projectId
                );
                if (token !== this.associationsRequestToken) {
                    return false;
                }

                let formattedRows = formatAssociationRows(result.rows, phenotypeSession, {
                    project: VKS_ASSOCIATION_PROJECT_KP,
                });
                if (ownsProgress) {
                    this.setRegionLoadStep(
                        "associations",
                        VKS_REGION_LOAD_STATUS.DONE
                    );
                    this.setRegionLoadStep("ld", VKS_REGION_LOAD_STATUS.LOADING);
                }
                try {
                    const refRow = resolveLdReferenceRow(
                        filterAssociationRowsByProject(
                            this.associationsState.rows,
                            VKS_ASSOCIATION_PROJECT_KP
                        ),
                        {
                            refVariant: this.plotOverlaysState?.refVariant,
                            refVariantUserSet: this.plotOverlaysState?.refVariantUserSet,
                        }
                    );
                    if (refRow) {
                        formattedRows = await enrichAssociationRowsWithLdScoresForRef(
                            formattedRows,
                            phenotypeSession,
                            refRow,
                            phenotypeSession.region
                        );
                    } else {
                        const ldResult = await enrichAssociationRowsWithLdScores(
                            formattedRows,
                            phenotypeSession
                        );
                        formattedRows = ldResult.rows;
                    }
                } catch (ldError) {
                    ldOk = false;
                    console.warn(
                        `GP2 Browser ${phenotypeName} LD enrich failed`,
                        ldError
                    );
                }

                if (token !== this.associationsRequestToken) {
                    return false;
                }

                const mergedRows = mergeAssociationRowsByVariantAndAncestry(
                    this.associationsState.rows,
                    formattedRows,
                    primaryAncestry
                );
                const phenotypeEntry = {
                    name: phenotypeName,
                    description: phenotype.description || null,
                    selectedAncestries: [],
                };
                this.associationsState = {
                    ...this.associationsState,
                    selectedPhenotypes: [...selected, phenotypeEntry],
                    rows: mergedRows,
                    phenotypeSeriesLoading: {
                        ...this.associationsState.phenotypeSeriesLoading,
                        [phenotypeName]: false,
                    },
                    phenotypeSeriesErrors: {
                        ...this.associationsState.phenotypeSeriesErrors,
                        [phenotypeName]: formattedRows.length
                            ? null
                            : "No associations returned for this phenotype.",
                    },
                };
                this.probeAncestryAssociationAvailabilityForPhenotype(
                    phenotype,
                    this.searchSession
                );
                this.lastZoomedAssociationsRegion = null;
                this.refetchAssociationsForZoomedView();
                associationsOk = true;
                const loadCs =
                    !projectAssociationsOnly(this.projectId) &&
                    this.isSectionChecked("credible-sets");
                const loadGe =
                    !projectAssociationsOnly(this.projectId) &&
                    (this.isSectionChecked("global-enrichment") ||
                        this.isSectionChecked("variant-to-gene-links"));
                if (ownsProgress) {
                    this.setRegionLoadStep(
                        "ld",
                        ldOk
                            ? VKS_REGION_LOAD_STATUS.DONE
                            : VKS_REGION_LOAD_STATUS.FAILED
                    );
                    if (loadCs) {
                        this.setRegionLoadStep(
                            "credibleSets",
                            VKS_REGION_LOAD_STATUS.LOADING
                        );
                    }
                }
                let credibleSetsOk = true;
                if (loadCs) {
                    try {
                        credibleSetsOk = await this.mergeCredibleSetsForPhenotype(
                            phenotype
                        );
                    } catch (csError) {
                        credibleSetsOk = false;
                        console.warn(
                            `GP2 Browser ${phenotypeName} credible sets merge failed`,
                            csError
                        );
                    }
                } else {
                    this.lastCredibleSetsListRegion = null;
                }
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                if (ownsProgress) {
                    if (loadCs) {
                        this.setRegionLoadStep(
                            "credibleSets",
                            credibleSetsOk
                                ? VKS_REGION_LOAD_STATUS.DONE
                                : VKS_REGION_LOAD_STATUS.FAILED
                        );
                    }
                    if (loadGe) {
                        this.setRegionLoadStep(
                            "globalEnrichment",
                            VKS_REGION_LOAD_STATUS.LOADING
                        );
                    }
                }
                let globalEnrichmentOk = true;
                if (loadGe) {
                    try {
                        globalEnrichmentOk = await this.mergeGlobalEnrichmentForPhenotype(
                            phenotype
                        );
                    } catch (geError) {
                        globalEnrichmentOk = false;
                        console.warn(
                            `GP2 Browser ${phenotypeName} global enrichment merge failed`,
                            geError
                        );
                    }
                } else {
                    this.lastGlobalEnrichmentRegion = null;
                    this.lastV2gCatalogRegion = null;
                }
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                if (ownsProgress) {
                    if (loadGe) {
                        this.setRegionLoadStep(
                            "globalEnrichment",
                            globalEnrichmentOk
                                ? VKS_REGION_LOAD_STATUS.DONE
                                : VKS_REGION_LOAD_STATUS.FAILED
                        );
                    }
                    this.finishAdditivePhenotypeLoadProgress({
                        associationsOk: true,
                        ldOk,
                        credibleSetsOk,
                        globalEnrichmentOk,
                    });
                }
                if (syncUrl) {
                    this.syncUrlSearchParams(this.searchSession);
                }
                return true;
            } catch (error) {
                if (token !== this.associationsRequestToken) {
                    return false;
                }
                console.warn(
                    `GP2 Browser ${phenotypeName} associations load failed`,
                    error
                );
                this.associationsState = {
                    ...this.associationsState,
                    phenotypeSeriesLoading: {
                        ...this.associationsState.phenotypeSeriesLoading,
                        [phenotypeName]: false,
                    },
                    phenotypeSeriesErrors: {
                        ...this.associationsState.phenotypeSeriesErrors,
                        [phenotypeName]:
                            "Failed to load associations for this phenotype.",
                    },
                };
                if (ownsProgress) {
                    this.finishAdditivePhenotypeLoadProgress({
                        associationsOk: false,
                        ldOk: false,
                        credibleSetsOk: false,
                        globalEnrichmentOk: false,
                    });
                }
                return false;
            }
        },
        onRemoveAssociationPhenotype(phenotypeName) {
            const target = String(phenotypeName || "").trim();
            if (!target || !this.searchSession?.phenotype) {
                return;
            }

            const primaryName = String(this.searchSession.phenotype.name || "").trim();
            const selected = [...(this.associationsState.selectedPhenotypes || [])];
            const totalCount = (primaryName ? 1 : 0) + selected.length;
            if (totalCount <= 1) {
                return;
            }

            const primaryAncestry = primaryAssociationAncestry(this.searchSession);

            if (target === primaryName) {
                const [nextEntry, ...rest] = selected;
                if (!nextEntry?.name) {
                    return;
                }
                const nextPhenotype =
                    (this.phenotypes || this.phenotypesInUse || []).find(
                        (entry) => entry.name === nextEntry.name
                    ) || {
                        name: nextEntry.name,
                        description: nextEntry.description || null,
                    };

                // Drop association rows for the removed primary phenotype.
                const nextRows = (this.associationsState.rows || []).filter((row) => {
                    const rowPhenotype = associationRowPhenotype(row, primaryName);
                    return rowPhenotype !== primaryName;
                });

                this.searchSession = {
                    ...this.searchSession,
                    phenotype: nextPhenotype,
                };
                this.associationsState = {
                    ...this.associationsState,
                    rows: nextRows,
                    selectedPhenotypes: rest,
                    // Ancestry series belonged to the previous primary track.
                    selectedAncestries: [],
                    ancestryAvailability:
                        this.associationsState.ancestryAvailabilityByPhenotype?.[
                            nextEntry.name
                        ] || [],
                };
                this.probeAncestryAssociationAvailabilityForPhenotype(
                    nextPhenotype,
                    this.searchSession
                );
                this.removeCredibleSetsForPhenotype(primaryName);
                this.removeGlobalEnrichmentForPhenotype(primaryName);
            } else {
                const nextRows = (this.associationsState.rows || []).filter((row) => {
                    const rowPhenotype = associationRowPhenotype(row, primaryName);
                    return rowPhenotype !== target;
                });
                const {
                    [target]: _availability,
                    ...availabilityByPhenotype
                } = this.associationsState.ancestryAvailabilityByPhenotype || {};
                const {
                    [target]: _loading,
                    ...availabilityLoadingByPhenotype
                } = this.associationsState.ancestryAvailabilityLoadingByPhenotype || {};
                const {
                    [target]: _error,
                    ...availabilityErrorByPhenotype
                } = this.associationsState.ancestryAvailabilityErrorByPhenotype || {};

                this.associationsState = {
                    ...this.associationsState,
                    rows: nextRows,
                    selectedPhenotypes: selected.filter(
                        (entry) => entry?.name !== target
                    ),
                    ancestryAvailabilityByPhenotype: availabilityByPhenotype,
                    ancestryAvailabilityLoadingByPhenotype:
                        availabilityLoadingByPhenotype,
                    ancestryAvailabilityErrorByPhenotype:
                        availabilityErrorByPhenotype,
                };
                this.removeCredibleSetsForPhenotype(target);
                this.removeGlobalEnrichmentForPhenotype(target);
            }

            this.syncUrlSearchParams(this.searchSession);
        },
        applyUrlSearchParams() {
            if (this.canvasActive || this.searchSession) {
                return;
            }

            const params = this.utilsBox?.keyParams;
            if (!params?.region) {
                return;
            }

            // GWAS-CE needs a token that is never stored in the URL — prefills welcome only.
            if (isGwasCeProject(this.projectId) || isGwasCeProject(params.project)) {
                this.welcomeInitialValues = {
                    phenotype: params.phenotype || "",
                    ancestry: params.ancestry || "Mixed",
                    geneOrVariantQuery: params.region,
                    gwasCeToken: "",
                };
                return;
            }

            if (!params?.phenotype) {
                return;
            }

            const region = parseRegionParam(params.region);
            if (!region) {
                return;
            }

            const phenotypeNames = parsePhenotypesParam(params.phenotype);
            const primaryName = phenotypeNames[0] || String(params.phenotype).trim();
            const additionalPhenotypes = phenotypeNames.slice(1);
            const phenotype = (this.phenotypes || []).find(
                (entry) => entry.name === primaryName
            );
            if (!phenotype) {
                this.welcomeInitialValues = {
                    phenotype: primaryName || params.phenotype,
                    ancestry: params.ancestry || "",
                    geneOrVariantQuery: params.region,
                };
                return;
            }

            this.onStartSearch(
                {
                    phenotype,
                    ancestry: params.ancestry || null,
                    region,
                    regionLabel: formatRegion(region),
                    geneOrVariantQuery: params.region,
                    regionExpandBp: null,
                },
                {
                    subAncestries: parseSubAncestriesParam(
                        params.sub_ancestries,
                        params.ancestry || "Mixed"
                    ),
                    additionalPhenotypes,
                }
            );
        },
        syncUrlSearchParams(session) {
            if (!this.utilsBox?.keyParams || !session?.phenotype || !session?.regionLabel) {
                return;
            }

            const phenotypeParam =
                formatPhenotypesParam(
                    session.phenotype.name,
                    this.associationsState?.selectedPhenotypes || []
                ) || session.phenotype.name;

            const nextParams = {
                phenotype: phenotypeParam,
                region: session.regionLabel,
                project: this.projectId || undefined,
            };
            if (session.ancestry) {
                nextParams.ancestry = session.ancestry;
            } else {
                nextParams.ancestry = undefined;
            }

            const selected = [
                ...(this.associationsState?.selectedAncestries || []),
            ];
            (this.associationsState?.selectedPhenotypes || []).forEach((entry) => {
                (entry?.selectedAncestries || []).forEach((code) => {
                    if (code && !selected.includes(code)) {
                        selected.push(code);
                    }
                });
            });
            const pending = this.pendingSubAncestries || [];
            const subAncestries = parseSubAncestriesParam(
                [...selected, ...pending],
                session.ancestry || "Mixed"
            );
            const subParam = formatSubAncestriesParam(subAncestries);
            nextParams.sub_ancestries = subParam || undefined;

            this.utilsBox.keyParams.set(nextParams);
        },
        onAssociationsFiltersIndexUpdate(filtersIndex) {
            this.associationsState = {
                ...this.associationsState,
                filtersIndex,
            };
        },
        onCredibleSetsPanelFiltersUpdate(panelFilters) {
            this.credibleSetsState = {
                ...this.credibleSetsState,
                panelFilters: cloneCredibleSetsPanelFilters(panelFilters),
            };
        },
        onGenesSelectedTypesUpdate(selectedTypes) {
            this.genesState = {
                ...this.genesState,
                selectedTypes: normalizeSelectedGeneTypes(selectedTypes),
            };
        },
        async refreshAssistantLlmHealth() {
            const health = await fetchInteractiveLlmHealth();
            this.assistantState = {
                ...this.assistantState,
                llmAvailable: Boolean(health?.llm_available),
            };
        },
        onAssistantActiveTabUpdate(activeTab) {
            this.assistantState = {
                ...this.assistantState,
                activeTab,
            };
        },
        onAssistantExecuteAll() {
            const steps = this.assistantState?.plan?.steps || [];
            const next = steps.find(
                (step) => (this.assistantState.stepStates || {})[step.id] !== "done"
            );
            if (next) {
                this.runAssistantAction(next.actionId, {
                    auto: false,
                    stepId: next.id,
                });
            }
        },
        onAssistantExecuteStep(stepId) {
            const step = (this.assistantState?.plan?.steps || []).find(
                (item) => item.id === stepId
            );
            if (!step) {
                return;
            }
            this.runAssistantAction(step.actionId, { auto: false, stepId: step.id });
        },
        onExecuteCatalogAction(actionId) {
            const action = findAssistantAction(actionId);
            if (!action?.runnable || !this.searchSession) {
                return;
            }
            const stepId = this.researchActionStepId(actionId);
            const plan = createAssistantPlan(
                [
                    {
                        id: stepId,
                        actionId,
                        label: action.label,
                    },
                ],
                { executeLabel: "Execute" }
            );
            const clearedEntries = clearAssistantResultEntries(
                this.assistantState.threadEntries
            );
            this.aiAssistantOpen = true;
            this.assistantState = {
                ...this.assistantState,
                activeTab: "request",
                executing: false,
                executionProgressLabel: "",
                cs2ctStarPrompt: null,
                understudiedStarPrompt: null,
                plan,
                stepStates: { [stepId]: "pending" },
                threadEntries: appendAssistantEntries(clearedEntries, [
                    createUserMessage(action.label),
                    createAssistantMessage(`Running “${action.label}”.`),
                ]),
            };
            this.runAssistantAction(actionId, { auto: false, stepId });
        },
        onAssistantPlanRequest({ text } = {}) {
            const requestText = String(text || "").trim();
            if (!requestText) {
                return;
            }

            const matches = matchVksAssistantRequest(requestText);
            const clearedEntries = clearAssistantResultEntries(
                this.assistantState.threadEntries
            );
            this.aiAssistantOpen = true;
            this.assistantState = {
                ...this.assistantState,
                activeTab: "request",
                cs2ctStarPrompt: null,
                understudiedStarPrompt: null,
                threadEntries: appendAssistantEntries(clearedEntries, [
                    createUserMessage(requestText),
                ]),
            };

            if (!matches.length) {
                this.assistantState = {
                    ...this.assistantState,
                    plan: null,
                    stepStates: {},
                    threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                        createAssistantMessage(
                            "I could not match that to a GP2 Browser action yet. Browse the Actions tab for supported requests, or try phrasing like “Classify tissues by phenotype relevance”, “Find understudied bottom-line variants in this locus”, or “Open Global enrich.”",
                            { isClarify: true }
                        ),
                    ]),
                };
                return;
            }

            if (
                matches.length === 1 &&
                matches[0].id !== "filter_ge_relevance" &&
                matches[0].id !== "find_understudied_bottom_line" &&
                matches[0].id !== "find_genetic_correlations"
            ) {
                const action = matches[0];
                this.assistantState = {
                    ...this.assistantState,
                    plan: null,
                    stepStates: {},
                    threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                        createAssistantMessage(`Running “${action.label}”.`),
                    ]),
                };
                this.runAssistantAction(action.id, { auto: false });
                return;
            }

            const plan = createAssistantPlan(
                matches.map((action) => ({
                    id: `step-${action.id}`,
                    actionId: action.id,
                    label: action.label,
                })),
                { executeLabel: matches.length > 1 ? "Execute all" : "Execute" }
            );
            const stepStates = {};
            (plan?.steps || []).forEach((step) => {
                stepStates[step.id] = "pending";
            });
            this.assistantState = {
                ...this.assistantState,
                plan,
                stepStates,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantMessage(
                        matches.length === 1
                            ? `Matched “${matches[0].label}”. Run Execute below when you are ready.`
                            : `Matched ${matches.length} actions. Review the steps below, then Execute.`
                    ),
                ]),
            };
        },
        markAssistantStepState(stepId, status) {
            if (!stepId) {
                return;
            }
            this.assistantState = {
                ...this.assistantState,
                stepStates: {
                    ...(this.assistantState.stepStates || {}),
                    [stepId]: status,
                },
            };
        },
        applyAssistantZoomStep(direction) {
            const step = 10;
            const delta = direction < 0 ? -step : step;
            const current = sliderValueFromZoom(this.regionZoom, this.regionZoomOut);
            let next = Math.max(
                VKS_REGION_ZOOM_SLIDER_MIN,
                Math.min(VKS_REGION_ZOOM_SLIDER_MAX, current + delta)
            );
            if (next < 0 && this.zoomOutAtLimit) {
                next = Math.max(next, current < 0 ? current : 0);
            }
            const { regionZoom, regionZoomOut } = zoomFromSliderValue(next);
            this.onRegionZoomUpdate(regionZoom);
            this.onRegionZoomOutUpdate(regionZoomOut);
            this.onRegionZoomSliderCommit();
        },
        async runAssistantAction(actionId, { auto = false, requestToken, silent = false, stepId } = {}) {
            if (actionId === "export_session") {
                this.exportSession();
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "import_session") {
                this.openSessionImport();
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "reset_search") {
                this.resetSearch();
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "zoom_in") {
                this.applyAssistantZoomStep(1);
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "zoom_out") {
                this.applyAssistantZoomStep(-1);
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "toggle_data_table") {
                this.dataTableOpen = !this.dataTableOpen;
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "open_getting_around") {
                this.workspaceGuideOpen = true;
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "open_global_enrichment") {
                this.openDrawerId = "global-enrichment";
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "open_associations") {
                this.openDrawerId = "associations";
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "open_credible_sets") {
                this.openDrawerId = "credible-sets";
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "open_genes") {
                this.openDrawerId = "genes";
                this.markAssistantStepState(stepId, "done");
                return;
            }
            if (actionId === "find_understudied_bottom_line") {
                await this.runFindUnderstudiedBottomLineAction({
                    auto,
                    silent,
                    stepId,
                });
                return;
            }
            if (actionId === "find_genetic_correlations") {
                await this.runFindGeneticCorrelationsAction({
                    silent,
                    stepId,
                });
                return;
            }
            if (actionId !== "filter_ge_relevance") {
                return;
            }

            const session = this.searchSession;
            const catalog = this.globalEnrichmentState?.catalog;
            if (
                !session ||
                (!catalog?.annotations?.length && !catalog?.tissues?.length)
            ) {
                return;
            }

            const geToken = requestToken ?? this.globalEnrichmentRequestToken;
            const actionToken = ++this.assistantActionToken;
            const runningLabel = buildGeRelevanceRunningMessage();
            const annotationLabels = catalog.annotations || [];
            this.markAssistantStepState(stepId, "running");

            if (!silent) {
                this.aiAssistantOpen = true;
                this.assistantState = {
                    ...this.assistantState,
                    activeTab: "request",
                    executing: true,
                    executionProgressLabel: runningLabel,
                    cs2ctStarPrompt: null,
                    understudiedStarPrompt: null,
                    threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                        createAssistantStatusMessage(
                            buildGeRelevanceIntroMessage(session, catalog),
                            { pending: true, isStepResult: true }
                        ),
                    ]),
                };
            }

            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                llmRelevance: buildGeLlmLoadingState(annotationLabels),
            };

            try {
                let availableSets = this.credibleSetsState?.available || [];
                if (!availableSets.length) {
                    await this.loadCredibleSetsList(session, {
                        preserveSelection: true,
                    });
                    if (
                        actionToken !== this.assistantActionToken ||
                        geToken !== this.globalEnrichmentRequestToken
                    ) {
                        return;
                    }
                    availableSets = this.credibleSetsState?.available || [];
                }

                const classification = await runCs2ctTissueClassification({
                    session,
                    credibleSets: availableSets,
                    host: this.bioIndexHostFor("c2ct-credible-set"),
                    catalogTissues: catalog.tissues || [],
                    catalogAnnotations: annotationLabels,
                    associationRows: this.associationsState?.rows || [],
                    region: this.dataRegion || session.region,
                });
                if (
                    actionToken !== this.assistantActionToken ||
                    geToken !== this.globalEnrichmentRequestToken
                ) {
                    return;
                }

                const llmRelevance = {
                    loading: false,
                    error: null,
                    llmUsed: true,
                    tissueOnly: true,
                    filterComplete: true,
                    relevantAnnotations: annotationLabels,
                    relevantTissues: classification.relevantTissues || [],
                    relevantTissuesByAnnotation:
                        classification.relevantTissuesByAnnotation || {},
                    rationaleById: {},
                    source: "c2ct-credible-set",
                };

                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    llmRelevance,
                    enabledMutedAnnotations: [],
                    enabledMutedAnnotationTissues: {},
                    disabledAnnotationTissues: {},
                };

                this.markAssistantStepState(stepId, "done");

                const starOptions = classification.starOptions || [];
                const cs2ctStarPrompt = starOptions.length
                    ? {
                          message: buildCs2ctStarPromptMessage(starOptions),
                          options: starOptions,
                      }
                    : null;

                if (!silent) {
                    const report = buildGeRelevanceReportMessage({
                        session,
                        catalog,
                        llmRelevance,
                        annoData: this.globalEnrichmentState?.annoData || {},
                        geRows: this.globalEnrichmentState?.geRows || [],
                        classification,
                    });
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        cs2ctStarPrompt,
                        understudiedStarPrompt: null,
                        threadEntries: replacePendingAssistantEntry(
                            this.assistantState.threadEntries,
                            report
                        ),
                    };
                    if (!cs2ctStarPrompt) {
                        this.offerRemainingResearchAction("filter_ge_relevance");
                    }
                } else {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        cs2ctStarPrompt,
                        understudiedStarPrompt: null,
                    };
                    if (!cs2ctStarPrompt) {
                        this.offerRemainingResearchAction("filter_ge_relevance");
                    }
                }
            } catch (error) {
                if (
                    actionToken !== this.assistantActionToken ||
                    geToken !== this.globalEnrichmentRequestToken
                ) {
                    return;
                }
                console.warn("GP2 Browser CS2CT tissue classification failed", error);
                const errorMessage =
                    error?.message ||
                    "CS2CT tissue filtering is unavailable; using enrichment p-value filtering only.";
                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    llmRelevance: buildGeLlmRelevanceShowAllState(annotationLabels, {
                        error: errorMessage,
                    }),
                };
                this.markAssistantStepState(stepId, "error");
                if (!silent) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        cs2ctStarPrompt: null,
                        understudiedStarPrompt: null,
                        threadEntries: replacePendingAssistantEntry(
                            this.assistantState.threadEntries,
                            `${errorMessage} Annotation tracks show tissues with enrichment p < 0.5 for each annotation.`,
                            { isClarify: true }
                        ),
                    };
                    this.offerRemainingResearchAction("filter_ge_relevance");
                } else if (this.assistantState.executing) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        cs2ctStarPrompt: null,
                        understudiedStarPrompt: null,
                    };
                    this.offerRemainingResearchAction("filter_ge_relevance");
                }
            }
        },
        listUnderstudiedTargetAncestries() {
            const primary = primaryAssociationAncestry(this.searchSession) || "Mixed";
            const selected = this.associationsState?.selectedAncestries || [];
            const codes = [];
            const add = (ancestry) => {
                if (ancestry && !codes.includes(ancestry)) {
                    codes.push(ancestry);
                }
            };

            add(primary);
            selected.forEach(add);

            // Also include ancestries present in loaded association rows
            // (covers cases where series data is present but selection lists lag).
            (this.associationsState?.rows || []).forEach((row) => {
                add(associationRowAncestry(row, primary));
            });

            return codes.length ? codes : ["Mixed"];
        },
        async runFindUnderstudiedBottomLineAction({
            silent = false,
            stepId,
        } = {}) {
            const baseSession = this.searchSession;
            if (!baseSession?.phenotype?.name || !baseSession?.region) {
                return;
            }

            const ancestries = this.listUnderstudiedTargetAncestries();
            const actionToken = ++this.assistantActionToken;
            const resolvedStepId =
                stepId || this.researchActionStepId("find_understudied_bottom_line");
            this.markAssistantStepState(resolvedStepId, "running");

            const host =
                this.bioIndexHostFor("global-associations") ||
                this.bioIndexHostFor("associations");
            if (!host) {
                if (!silent) {
                    this.aiAssistantOpen = true;
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        understudiedStarPrompt: null,
                        understudiedAncestry: null,
                        threadEntries: appendAssistantEntries(
                            this.assistantState.threadEntries,
                            [
                                createAssistantStepMessage(
                                    "BioIndex host is not available.",
                                    { isClarify: true }
                                ),
                            ]
                        ),
                    };
                }
                this.markAssistantStepState(resolvedStepId, "error");
                this.offerRemainingResearchAction("find_understudied_bottom_line");
                return;
            }

            const allStarRows = [];
            let hadError = false;

            if (!silent) {
                this.aiAssistantOpen = true;
                this.assistantState = {
                    ...this.assistantState,
                    activeTab: "request",
                    executing: true,
                    cs2ctStarPrompt: null,
                    understudiedStarPrompt: null,
                };
            }

            try {
                for (let index = 0; index < ancestries.length; index += 1) {
                    if (actionToken !== this.assistantActionToken) {
                        return;
                    }
                    const ancestry = ancestries[index];
                    const session = { ...baseSession, ancestry };
                    const intro = buildUnderstudiedIntroMessage(session);
                    const runningLabel = buildUnderstudiedRunningMessage();

                    if (!silent) {
                        this.assistantState = {
                            ...this.assistantState,
                            executing: true,
                            executionProgressLabel: runningLabel,
                            threadEntries: appendAssistantEntries(
                                this.assistantState.threadEntries,
                                [
                                    createAssistantStatusMessage(intro, {
                                        pending: true,
                                        isStepResult: true,
                                    }),
                                ]
                            ),
                        };
                    }

                    try {
                        const { rows } = await fetchGlobalAssociations(session, host, {
                            limit: 1000,
                        });
                        if (actionToken !== this.assistantActionToken) {
                            return;
                        }

                        const matched = filterUnderstudiedBottomLineInRegion(
                            rows,
                            session.region
                        );
                        matched.forEach((row) => {
                            const starRow = {
                                ...row,
                                ancestry,
                            };
                            allStarRows.push(starRow);
                        });

                        if (!silent) {
                            const report = matched.length
                                ? buildUnderstudiedReportMessage(session, matched)
                                : buildUnderstudiedNoneFoundMessage(session);
                            this.assistantState = {
                                ...this.assistantState,
                                threadEntries: replacePendingAssistantEntry(
                                    this.assistantState.threadEntries,
                                    report
                                ),
                            };
                        }
                    } catch (error) {
                        if (actionToken !== this.assistantActionToken) {
                            return;
                        }
                        hadError = true;
                        console.warn(
                            `GP2 Browser understudied bottom-line search failed (${ancestry})`,
                            error
                        );
                        const errorMessage =
                            error?.message ||
                            `Could not load phenotype-wide associations for ${ancestry}.`;
                        if (!silent) {
                            this.assistantState = {
                                ...this.assistantState,
                                threadEntries: replacePendingAssistantEntry(
                                    this.assistantState.threadEntries,
                                    errorMessage,
                                    { isClarify: true }
                                ),
                            };
                        }
                    }
                }

                if (actionToken !== this.assistantActionToken) {
                    return;
                }

                const understudiedStarPrompt = buildUnderstudiedStarPrompt(allStarRows);
                this.markAssistantStepState(
                    resolvedStepId,
                    hadError && !allStarRows.length ? "error" : "done"
                );

                this.assistantState = {
                    ...this.assistantState,
                    executing: false,
                    executionProgressLabel: "",
                    understudiedStarPrompt: silent ? null : understudiedStarPrompt,
                    understudiedAncestry: null,
                };

                if (!silent && !understudiedStarPrompt) {
                    this.offerRemainingResearchAction("find_understudied_bottom_line");
                }
            } catch (error) {
                if (actionToken !== this.assistantActionToken) {
                    return;
                }
                console.warn(
                    "GP2 Browser understudied bottom-line search failed",
                    error
                );
                const errorMessage =
                    error?.message ||
                    "Could not load phenotype-wide associations for understudied bottom-line variants.";
                this.markAssistantStepState(resolvedStepId, "error");
                if (!silent) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        understudiedStarPrompt: null,
                        understudiedAncestry: null,
                        threadEntries: replacePendingAssistantEntry(
                            this.assistantState.threadEntries,
                            errorMessage,
                            { isClarify: true }
                        ),
                    };
                    this.offerRemainingResearchAction("find_understudied_bottom_line");
                } else if (this.assistantState.executing) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        understudiedStarPrompt: null,
                        understudiedAncestry: null,
                    };
                    this.offerRemainingResearchAction("find_understudied_bottom_line");
                }
            }
        },
        onConfirmCs2ctStar(selectedCredibleSetIds = []) {
            const prompt = this.assistantState?.cs2ctStarPrompt;
            const options = prompt?.options || [];
            if (!options.length) {
                this.onDismissCs2ctStar();
                return;
            }

            const selected = new Set(
                (Array.isArray(selectedCredibleSetIds)
                    ? selectedCredibleSetIds
                    : []
                ).map(String)
            );
            let starred = [...(this.plotMarkersState.starredVariants || [])];
            let added = 0;

            options.forEach((option) => {
                if (!selected.has(String(option.credibleSetId))) {
                    return;
                }
                (option.variants || []).forEach((row) => {
                    if (isVariantStarred(starred, row?.["Variant ID"])) {
                        return;
                    }
                    const entry = createStarredVariant(row, "cs2ct");
                    if (entry) {
                        starred.push(entry);
                        added += 1;
                    }
                });
            });

            this.plotMarkersState = {
                ...this.plotMarkersState,
                starredVariants: starred,
            };
            this.assistantState = {
                ...this.assistantState,
                cs2ctStarPrompt: null,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantStepMessage(
                        added
                            ? `Starred ${added} overlap lead SNP${added === 1 ? "" : "s"} from the selected credible sets.`
                            : "No new overlap lead SNPs were starred (they may already be starred)."
                    ),
                ]),
            };
            this.offerRemainingResearchAction("filter_ge_relevance");
        },
        onDismissCs2ctStar() {
            if (!this.assistantState?.cs2ctStarPrompt) {
                return;
            }
            this.assistantState = {
                ...this.assistantState,
                cs2ctStarPrompt: null,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantStepMessage("Skipped starring CS2CT overlap lead SNPs."),
                ]),
            };
            this.offerRemainingResearchAction("filter_ge_relevance");
        },
        onConfirmUnderstudiedStar() {
            const prompt = this.assistantState?.understudiedStarPrompt;
            const variants = prompt?.variants || [];
            if (!variants.length) {
                this.onDismissUnderstudiedStar();
                return;
            }

            let starred = [...(this.plotMarkersState.starredVariants || [])];
            let added = 0;
            variants.forEach((row) => {
                if (isVariantStarred(starred, row?.["Variant ID"])) {
                    return;
                }
                const entry = createStarredVariant(row, "understudied-bottom-line");
                if (entry) {
                    starred.push(entry);
                    added += 1;
                }
            });

            this.plotMarkersState = {
                ...this.plotMarkersState,
                starredVariants: starred,
            };
            this.assistantState = {
                ...this.assistantState,
                understudiedStarPrompt: null,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantStepMessage(
                        added
                            ? `Starred ${added} understudied bottom-line variant${
                                  added === 1 ? "" : "s"
                              }.`
                            : "No new understudied bottom-line variants were starred (they may already be starred)."
                    ),
                ]),
            };
            this.offerRemainingResearchAction("find_understudied_bottom_line");
        },
        onDismissUnderstudiedStar() {
            if (!this.assistantState?.understudiedStarPrompt) {
                return;
            }
            this.assistantState = {
                ...this.assistantState,
                understudiedStarPrompt: null,
                threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                    createAssistantStepMessage(
                        "Skipped starring understudied bottom-line variants."
                    ),
                ]),
            };
            this.offerRemainingResearchAction("find_understudied_bottom_line");
        },
        phenotypeMapForCorrelations() {
            const map = {};
            (this.phenotypes || []).forEach((phenotype) => {
                if (phenotype?.name) {
                    map[phenotype.name] = phenotype;
                }
            });
            return map;
        },
        async runFindGeneticCorrelationsAction({ silent = false, stepId } = {}) {
            const session = this.searchSession;
            if (!session?.phenotype?.name) {
                return;
            }

            const ancestries = this.listUnderstudiedTargetAncestries();
            const actionToken = ++this.assistantActionToken;
            const resolvedStepId =
                stepId || this.researchActionStepId("find_genetic_correlations");
            const runningLabel = buildGeneticCorrelationRunningMessage();
            this.markAssistantStepState(resolvedStepId, "running");

            const host =
                this.bioIndexHostFor("genetic-correlation") ||
                this.bioIndexHostFor("associations");
            if (!host) {
                this.markAssistantStepState(resolvedStepId, "error");
                if (!silent) {
                    this.aiAssistantOpen = true;
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        threadEntries: appendAssistantEntries(
                            this.assistantState.threadEntries,
                            [
                                createAssistantStepMessage(
                                    "BioIndex host is not available.",
                                    { isClarify: true }
                                ),
                            ]
                        ),
                    };
                }
                return;
            }

            if (!silent) {
                this.aiAssistantOpen = true;
                this.assistantState = {
                    ...this.assistantState,
                    activeTab: "request",
                    executing: true,
                    executionProgressLabel: runningLabel,
                    cs2ctStarPrompt: null,
                    understudiedStarPrompt: null,
                    threadEntries: appendAssistantEntries(this.assistantState.threadEntries, [
                        createAssistantStatusMessage(
                            buildGeneticCorrelationIntroMessage(session, ancestries),
                            { pending: true, isStepResult: true }
                        ),
                    ]),
                };
            }

            const phenotypeMap = this.phenotypeMapForCorrelations();
            const groups = [];

            try {
                for (const ancestry of ancestries) {
                    if (actionToken !== this.assistantActionToken) {
                        return;
                    }
                    try {
                        const { rows } = await fetchGeneticCorrelation(
                            session.phenotype.name,
                            ancestry,
                            host
                        );
                        if (actionToken !== this.assistantActionToken) {
                            return;
                        }
                        const filtered = filterSignificantGeneticCorrelations(rows, {
                            phenotypeMap,
                        });
                        groups.push(
                            buildGeneticCorrelationPhenotypeGroups({
                                ancestry,
                                rows: filtered,
                                phenotypeMap,
                            })
                        );
                    } catch (error) {
                        console.warn(
                            `GP2 Browser genetic correlation failed (${ancestry})`,
                            error
                        );
                        groups.push({
                            ancestry,
                            phenotypes: [],
                            error: error?.message || "Failed to load genetic correlations.",
                        });
                    }
                }

                if (actionToken !== this.assistantActionToken) {
                    return;
                }

                const result = buildGeneticCorrelationResultEntry({
                    session,
                    groups,
                });
                const resultText = String(result.text || "").replace(
                    /Variant Sifter/gi,
                    "GP2 Browser"
                );
                this.markAssistantStepState(resolvedStepId, "done");
                if (!silent) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        threadEntries: replacePendingAssistantEntry(
                            this.assistantState.threadEntries,
                            resultText,
                            { phenotypeGroups: result.phenotypeGroups }
                        ),
                    };
                } else {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                    };
                }
            } catch (error) {
                if (actionToken !== this.assistantActionToken) {
                    return;
                }
                console.warn("GP2 Browser genetic correlation search failed", error);
                this.markAssistantStepState(resolvedStepId, "error");
                if (!silent) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                        threadEntries: replacePendingAssistantEntry(
                            this.assistantState.threadEntries,
                            error?.message ||
                                "Could not load genetically correlated phenotypes.",
                            { isClarify: true }
                        ),
                    };
                } else if (this.assistantState.executing) {
                    this.assistantState = {
                        ...this.assistantState,
                        executing: false,
                        executionProgressLabel: "",
                    };
                }
            }
        },
        onOpenCorrelatedPhenotype(phenotype) {
            if (!phenotype?.name || !this.searchSession?.regionLabel) {
                return;
            }
            const url = new URL(window.location.href);
            url.searchParams.set("phenotype", phenotype.name);
            url.searchParams.set("region", this.searchSession.regionLabel);
            if (this.projectId) {
                url.searchParams.set("project", this.projectId);
            } else {
                url.searchParams.delete("project");
            }
            const ancestry = phenotype.ancestry || "Mixed";
            if (ancestry && ancestry !== "Mixed") {
                url.searchParams.set("ancestry", ancestry);
            } else {
                url.searchParams.set("ancestry", "Mixed");
            }
            url.searchParams.delete("sub_ancestries");
            window.open(url.toString(), "_blank", "noopener,noreferrer");
        },
        onGeSelectedAnnotationsUpdate(selectedAnnotations) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedAnnotations: [...selectedAnnotations],
            };
        },
        onSelectGePlotTissue({
            annotation,
            tissue,
            selected = true,
            selectedAnnotations,
            selectedTissues,
            enabledMutedAnnotationTissues,
            disabledAnnotationTissues,
        } = {}) {
            if (!annotation || !tissue) {
                return;
            }
            const nextMap = setSelectedTissuesForAnnotation(
                this.globalEnrichmentState?.selectedTissuesByAnnotation,
                annotation,
                selected
                    ? [
                          ...new Set([
                              ...selectedTissuesForAnnotation(
                                  this.globalEnrichmentState
                                      ?.selectedTissuesByAnnotation,
                                  annotation
                              ),
                              tissue,
                          ]),
                      ]
                    : selectedTissuesForAnnotation(
                          this.globalEnrichmentState?.selectedTissuesByAnnotation,
                          annotation
                      ).filter((item) => item !== tissue)
            );
            // Prefer explicit list from the plot handler when provided.
            const resolvedMap = Array.isArray(selectedTissues)
                ? setSelectedTissuesForAnnotation(
                      this.globalEnrichmentState?.selectedTissuesByAnnotation,
                      annotation,
                      selectedTissues
                  )
                : nextMap;
            const nextActive = selected
                ? annotation
                : this.globalEnrichmentState.activeAnnotation || annotation;
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedAnnotations: Array.isArray(selectedAnnotations)
                    ? [...selectedAnnotations]
                    : this.globalEnrichmentState.selectedAnnotations,
                enabledMutedAnnotationTissues: {
                    ...(enabledMutedAnnotationTissues ||
                        this.globalEnrichmentState.enabledMutedAnnotationTissues ||
                        {}),
                },
                disabledAnnotationTissues: {
                    ...(disabledAnnotationTissues ||
                        this.globalEnrichmentState.disabledAnnotationTissues ||
                        {}),
                },
                activeAnnotation: nextActive,
                selectedTissuesByAnnotation: resolvedMap,
                selectedTissues: selectedTissuesForAnnotation(
                    resolvedMap,
                    nextActive
                ),
            };
        },
        onGeEnabledMutedAnnotationsUpdate(enabledMutedAnnotations) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                enabledMutedAnnotations: [...enabledMutedAnnotations],
            };
        },
        onGeEnabledMutedAnnotationTissuesUpdate(enabledMutedAnnotationTissues) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                enabledMutedAnnotationTissues: {
                    ...(enabledMutedAnnotationTissues || {}),
                },
            };
        },
        onGeDisabledAnnotationTissuesUpdate(disabledAnnotationTissues) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                disabledAnnotationTissues: {
                    ...(disabledAnnotationTissues || {}),
                },
            };
        },
        onGeTissueTrackSortUpdate(tissueTrackSort) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                tissueTrackSort: normalizeGeTissueTrackSort(tissueTrackSort),
            };
        },
        onGeTrackPValueMaxUpdate(geTrackPValueMax) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                geTrackPValueMax: normalizeGeTrackPValueMax(geTrackPValueMax),
                enabledMutedAnnotationTissues: {},
                disabledAnnotationTissues: {},
            };
        },
        onGeSelectedMethodsUpdate(selectedMethods) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedMethods: normalizeGeFilterStringList(selectedMethods),
            };
        },
        onGeSelectedSourcesUpdate(selectedSources) {
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedSources: normalizeGeFilterStringList(selectedSources),
            };
        },
        pruneV2gDeselections(tissueData, deselectedMethods, deselectedGenes) {
            const methods = new Set(collectMethodsFromTissueData(tissueData));
            const genes = new Set(collectGenesFromTissueData(tissueData));
            return {
                deselectedMethods: (deselectedMethods || []).filter((method) =>
                    methods.has(method)
                ),
                deselectedGenes: (deselectedGenes || []).filter((gene) => genes.has(gene)),
            };
        },
        async onV2gSelectedTissuesUpdate(selectedTissues) {
            const nextSelected = Array.isArray(selectedTissues)
                ? [...new Set(selectedTissues.filter(Boolean))]
                : [];
            const prevSelected = this.v2gState?.selectedTissues || [];
            const prevData = { ...(this.v2gState?.tissueData || {}) };
            const removed = prevSelected.filter((tissue) => !nextSelected.includes(tissue));
            removed.forEach((tissue) => {
                delete prevData[tissue];
            });

            const pruned = this.pruneV2gDeselections(
                prevData,
                this.v2gState?.deselectedMethods,
                this.v2gState?.deselectedGenes
            );

            this.v2gState = {
                ...this.v2gState,
                selectedTissues: nextSelected,
                tissueData: prevData,
                tissueErrors: Object.fromEntries(
                    Object.entries(this.v2gState?.tissueErrors || {}).filter(([tissue]) =>
                        nextSelected.includes(tissue)
                    )
                ),
                error: null,
                ...pruned,
            };

            const toFetch = nextSelected.filter((tissue) => !Array.isArray(prevData[tissue]));
            if (!toFetch.length) {
                return;
            }

            const host = this.bioIndexHostFor("gene-links");
            const region = this.dataRegion || this.searchSession?.region;
            if (!host || !region) {
                this.v2gState = {
                    ...this.v2gState,
                    error: host
                        ? "No region is available for gene-links."
                        : "BioIndex host is not available.",
                    selectedTissues: nextSelected.filter((tissue) =>
                        Array.isArray(this.v2gState.tissueData?.[tissue])
                    ),
                };
                return;
            }

            const token = ++this.v2gRequestToken;
            for (const tissue of toFetch) {
                if (token !== this.v2gRequestToken) {
                    return;
                }
                this.v2gState = {
                    ...this.v2gState,
                    loadingTissue: tissue,
                    error: null,
                };
                try {
                    const rows = await fetchGeneLinks(tissue, region, host);
                    if (token !== this.v2gRequestToken) {
                        return;
                    }
                    if (!rows.length) {
                        const tissueErrors = {
                            ...(this.v2gState.tissueErrors || {}),
                            [tissue]: `${tissue} has no linked genes.`,
                        };
                        const selectedWithoutEmpty = (
                            this.v2gState.selectedTissues || []
                        ).filter((entry) => entry !== tissue);
                        this.v2gState = {
                            ...this.v2gState,
                            loadingTissue: null,
                            selectedTissues: selectedWithoutEmpty,
                            tissueErrors,
                            error: tissueErrors[tissue],
                        };
                        continue;
                    }
                    const tissueData = {
                        ...(this.v2gState.tissueData || {}),
                        [tissue]: rows,
                    };
                    const nextPruned = this.pruneV2gDeselections(
                        tissueData,
                        this.v2gState.deselectedMethods,
                        this.v2gState.deselectedGenes
                    );
                    this.v2gState = {
                        ...this.v2gState,
                        loadingTissue: null,
                        tissueData,
                        error: null,
                        ...nextPruned,
                    };
                } catch (error) {
                    if (token !== this.v2gRequestToken) {
                        return;
                    }
                    console.warn("GP2 Browser gene-links fetch failed", tissue, error);
                    const selectedWithoutFailed = (this.v2gState.selectedTissues || []).filter(
                        (entry) => entry !== tissue
                    );
                    this.v2gState = {
                        ...this.v2gState,
                        loadingTissue: null,
                        selectedTissues: selectedWithoutFailed,
                        error: `Failed to load gene links for ${tissue}.`,
                    };
                }
            }

            if (token === this.v2gRequestToken) {
                this.v2gState = {
                    ...this.v2gState,
                    loadingTissue: null,
                };
            }
        },
        onV2gDeselectedMethodsUpdate(deselectedMethods) {
            this.v2gState = {
                ...this.v2gState,
                deselectedMethods: Array.isArray(deselectedMethods)
                    ? [...deselectedMethods]
                    : [],
            };
        },
        onV2gDeselectedGenesUpdate(deselectedGenes) {
            this.v2gState = {
                ...this.v2gState,
                deselectedGenes: Array.isArray(deselectedGenes) ? [...deselectedGenes] : [],
            };
        },
        onV2gDeselectedTissuesUpdate(deselectedTissues) {
            this.v2gState = {
                ...this.v2gState,
                deselectedTissues: Array.isArray(deselectedTissues)
                    ? [...deselectedTissues]
                    : [],
            };
        },
        onV2gDeselectedBiosamplesUpdate(deselectedBiosamples) {
            this.v2gState = {
                ...this.v2gState,
                deselectedBiosamples: Array.isArray(deselectedBiosamples)
                    ? [...deselectedBiosamples]
                    : [],
            };
        },
        onV2gViewModeUpdate(viewMode) {
            this.v2gState = {
                ...this.v2gState,
                viewMode: normalizeV2gViewMode(viewMode),
            };
        },
        async onS2gLoad() {
            const region =
                this.dataRegion || this.viewRegion || this.searchSession?.region;
            if (!region) {
                return false;
            }
            const host = this.bioIndexHostFor("variant-links");
            const token = ++this.s2gRequestToken;
            this.s2gState = {
                ...this.s2gState,
                loadingTissue: VKS_S2G_TISSUE_LABEL,
                error: null,
            };
            try {
                const rows = await fetchVariantLinks(region, host);
                if (token !== this.s2gRequestToken) {
                    return false;
                }
                this.lastS2gRegion = cloneGenomicRegion(region);
                if (!rows.length) {
                    this.s2gState = {
                        ...emptyS2gState(),
                        error: "No SNP-to-gene links found in this region.",
                    };
                    return true;
                }
                const tissueData = buildS2gTissueData(rows);
                const methods = collectMethodsFromTissueData(tissueData);
                const genes = collectGenesFromTissueData(tissueData);
                const deselectedMethods = (this.s2gState.deselectedMethods || []).filter(
                    (method) => methods.includes(method)
                );
                const deselectedGenes = (this.s2gState.deselectedGenes || []).filter((gene) =>
                    genes.includes(gene)
                );
                this.s2gState = {
                    ...this.s2gState,
                    loadingTissue: null,
                    tissueData,
                    selectedTissues: [VKS_S2G_TISSUE_LABEL],
                    error: null,
                    tissueErrors: {},
                    deselectedMethods,
                    deselectedGenes,
                    viewMode: "tracks",
                };
                return true;
            } catch (error) {
                if (token !== this.s2gRequestToken) {
                    return false;
                }
                console.warn("GP2 Browser variant-links fetch failed", error);
                this.s2gState = {
                    ...this.s2gState,
                    loadingTissue: null,
                    error: "Failed to load SNP-to-gene links for this region.",
                };
                return false;
            }
        },
        onS2gClear() {
            this.s2gRequestToken += 1;
            this.lastS2gRegion = null;
            this.s2gState = emptyS2gState();
        },
        onS2gDeselectedMethodsUpdate(deselectedMethods) {
            this.s2gState = {
                ...this.s2gState,
                deselectedMethods: Array.isArray(deselectedMethods)
                    ? [...deselectedMethods]
                    : [],
            };
        },
        onS2gDeselectedGenesUpdate(deselectedGenes) {
            this.s2gState = {
                ...this.s2gState,
                deselectedGenes: Array.isArray(deselectedGenes) ? [...deselectedGenes] : [],
            };
        },
        onGeSelectedBiosamplesUpdate(selectedBiosamples) {
            const next = normalizeGeSelectedBiosamples(selectedBiosamples);
            const prev = this.globalEnrichmentState?.selectedBiosamples || [];
            if (
                next.length === prev.length &&
                next.every((item, index) => item === prev[index])
            ) {
                return;
            }
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedBiosamples: next,
            };
        },
        onGeActiveAnnotationUpdate(activeAnnotation) {
            const nextAnnotation = activeAnnotation || null;
            if (this.globalEnrichmentState?.activeAnnotation === nextAnnotation) {
                return;
            }
            const tissueMap = resolveSelectedTissuesByAnnotation({
                selectedTissuesByAnnotation:
                    this.globalEnrichmentState?.selectedTissuesByAnnotation,
                selectedTissues: this.globalEnrichmentState?.selectedTissues,
                activeAnnotation: this.globalEnrichmentState?.activeAnnotation,
            });
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                activeAnnotation: nextAnnotation,
                selectedTissuesByAnnotation: tissueMap,
                selectedTissues: selectedTissuesForAnnotation(
                    tissueMap,
                    nextAnnotation
                ),
                selectedBiosamples: [],
                biosampleLoading: false,
            };
        },
        onGeSelectedTissuesUpdate(selectedTissues) {
            const next = Array.isArray(selectedTissues)
                ? [...new Set(selectedTissues.filter(Boolean))]
                : [];
            const annotation = this.globalEnrichmentState?.activeAnnotation || null;
            const prev = selectedTissuesForAnnotation(
                this.globalEnrichmentState?.selectedTissuesByAnnotation,
                annotation
            );
            if (
                next.length === prev.length &&
                next.every((item, index) => item === prev[index])
            ) {
                return;
            }
            const nextMap = setSelectedTissuesForAnnotation(
                this.globalEnrichmentState?.selectedTissuesByAnnotation,
                annotation,
                next
            );
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                selectedTissuesByAnnotation: nextMap,
                selectedTissues: next,
            };
        },
        onGeBiosampleTissueRegionsUpdate(payload = {}) {
            const previous = this.globalEnrichmentState?.biosampleRegionsByAnnotation;
            const next = upsertGeBiosampleTissueRegions(previous, payload);
            const annotation = payload?.annotation;
            const tissue = payload?.tissue;
            const prevEntry = annotation && tissue ? previous?.[annotation]?.[tissue] : null;
            const nextEntry = annotation && tissue ? next?.[annotation]?.[tissue] : null;
            if (
                prevEntry &&
                nextEntry &&
                prevEntry.regionKey === nextEntry.regionKey &&
                prevEntry.rows?.length === nextEntry.rows?.length
            ) {
                return;
            }
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                biosampleRegionsByAnnotation: next,
            };
        },
        onGeBiosampleLoadingUpdate(isLoading) {
            const next = Boolean(isLoading);
            if (Boolean(this.globalEnrichmentState?.biosampleLoading) === next) {
                return;
            }
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                biosampleLoading: next,
            };
        },
        onRemoveMappingCategory(categoryId) {
            const parsed = parseMappingCategoryId(categoryId);
            if (!parsed) {
                return;
            }

            const currentIds = normalizeMappingState(this.mappingState)
                .selectedCategoryIds;
            if (currentIds.includes(categoryId)) {
                this.onMappingStateUpdate({
                    ...normalizeMappingState(this.mappingState),
                    selectedCategoryIds: currentIds.filter(
                        (id) => id !== categoryId
                    ),
                });
            }

            if (parsed.source === "credible-sets") {
                this.onRemoveCredibleSet(parsed.selectionKey);
                return;
            }

            if (parsed.source === "global-enrichment") {
                const prevMap = resolveSelectedTissuesByAnnotation({
                    selectedTissuesByAnnotation:
                        this.globalEnrichmentState?.selectedTissuesByAnnotation,
                    selectedTissues: this.globalEnrichmentState?.selectedTissues,
                    activeAnnotation: this.globalEnrichmentState?.activeAnnotation,
                });
                const current = selectedTissuesForAnnotation(
                    prevMap,
                    parsed.annotation
                );
                if (!current.includes(parsed.tissue)) {
                    return;
                }
                const nextTissues = current.filter(
                    (tissue) => tissue !== parsed.tissue
                );
                const nextMap = setSelectedTissuesForAnnotation(
                    prevMap,
                    parsed.annotation,
                    nextTissues
                );
                const activeAnnotation =
                    this.globalEnrichmentState?.activeAnnotation || null;
                const tissuePrefix = `${parsed.tissue}:::`;
                const nextBiosamples = (
                    this.globalEnrichmentState?.selectedBiosamples || []
                ).filter((key) => !String(key).startsWith(tissuePrefix));
                this.globalEnrichmentState = {
                    ...this.globalEnrichmentState,
                    selectedTissuesByAnnotation: nextMap,
                    selectedTissues: selectedTissuesForAnnotation(
                        nextMap,
                        activeAnnotation
                    ),
                    selectedBiosamples: normalizeGeSelectedBiosamples(
                        nextBiosamples
                    ),
                };
                return;
            }

            if (parsed.source === "biosamples") {
                const prev =
                    this.globalEnrichmentState?.selectedBiosamples || [];
                const next = prev.filter(
                    (key) => key !== parsed.selectionKey
                );
                if (next.length === prev.length) {
                    return;
                }
                this.onGeSelectedBiosamplesUpdate(next);
                return;
            }

            if (parsed.source === "variant-to-gene-links") {
                const next = normalizeV2gSelectedLinks(
                    this.v2gState?.selectedLinks
                ).filter((key) => key !== parsed.selectionKey);
                this.onV2gSelectedLinksUpdate(next);
                return;
            }

            if (parsed.source === "snp2gene-links") {
                const next = normalizeV2gSelectedLinks(
                    this.s2gState?.selectedLinks
                ).filter((key) => key !== parsed.selectionKey);
                this.onS2gSelectedLinksUpdate(next);
            }
        },
        onMappingStateUpdate(mappingState) {
            const next = normalizeMappingState(mappingState);
            const prev = normalizeMappingState(this.mappingState);
            if (
                next.mappingMode === prev.mappingMode &&
                next.selectedCategoryIds.length === prev.selectedCategoryIds.length &&
                next.selectedCategoryIds.every(
                    (id, index) => id === prev.selectedCategoryIds[index]
                )
            ) {
                return;
            }
            this.mappingState = next;
            if (normalizeWorkspaceMappingFilter(this.workspaceMappingFilter)) {
                if (!next.selectedCategoryIds.length) {
                    this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
                    return;
                }
                this.applyWorkspaceMappingFilter({ quiet: true });
            }
        },
        onWorkspaceFilterActiveUpdate(active) {
            if (active) {
                this.applyWorkspaceMappingFilter({ quiet: false });
                return;
            }
            this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
        },
        applyWorkspaceMappingFilter({ quiet = false } = {}) {
            // Layer 3 only: build a derived filter snapshot from current mapping
            // selections + association rows. Does not mutate loaded data or chip/track
            // selections (layers 1 and 2).
            if (!this.mappingState?.selectedCategoryIds?.length) {
                this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
                if (!quiet) {
                    window.alert(
                        "Select one or more mapping categories before filtering the workspace."
                    );
                }
                return;
            }
            const categories = collectMappingCategories({
                credibleSetsState: this.credibleSetsState,
                globalEnrichmentState: this.globalEnrichmentState,
                v2gState: this.v2gState,
                s2gState: this.s2gState,
                region: this.searchSession?.region || null,
                phenotype: this.searchSession?.phenotype?.name || "",
                ancestry: this.searchSession?.ancestry || "Mixed",
            });
            const filter = buildWorkspaceMappingFilter(
                applyAssociationsFilters(
                    this.associationsState.rows || [],
                    this.associationsState.filtersIndex
                ),
                {
                    mappingCategories: categories,
                    selectedCategoryIds: this.mappingState.selectedCategoryIds,
                    mappingMode: this.mappingState.mappingMode,
                }
            );
            if (!filter) {
                this.workspaceMappingFilter = emptyWorkspaceMappingFilter();
                if (!quiet) {
                    window.alert(
                        "No mapped variants matched the selected categories."
                    );
                }
                return;
            }
            this.workspaceMappingFilter = filter;
        },
        onV2gSelectedLinksUpdate(selectedLinks) {
            const next = normalizeV2gSelectedLinks(selectedLinks);
            const prev = normalizeV2gSelectedLinks(this.v2gState?.selectedLinks);
            if (
                next.length === prev.length &&
                next.every((key, index) => key === prev[index])
            ) {
                return;
            }
            this.v2gState = {
                ...this.v2gState,
                selectedLinks: next,
            };
        },
        onS2gSelectedLinksUpdate(selectedLinks) {
            const next = normalizeV2gSelectedLinks(selectedLinks);
            const prev = normalizeV2gSelectedLinks(this.s2gState?.selectedLinks);
            if (
                next.length === prev.length &&
                next.every((key, index) => key === prev[index])
            ) {
                return;
            }
            this.s2gState = {
                ...this.s2gState,
                selectedLinks: next,
            };
        },
        onGeBiosampleFilterOptionsUpdate(options = {}) {
            const nextMethods = [
                ...new Set([
                    ...(this.globalEnrichmentState?.biosampleMethodOptions || []),
                    ...(Array.isArray(options.methods) ? options.methods : []),
                ]),
            ].sort();
            const nextSources = [
                ...new Set([
                    ...(this.globalEnrichmentState?.biosampleSourceOptions || []),
                    ...(Array.isArray(options.sources) ? options.sources : []),
                ]),
            ].sort();
            const prevMethods = this.globalEnrichmentState?.biosampleMethodOptions || [];
            const prevSources = this.globalEnrichmentState?.biosampleSourceOptions || [];
            if (
                nextMethods.length === prevMethods.length &&
                nextSources.length === prevSources.length &&
                nextMethods.every((item, index) => item === prevMethods[index]) &&
                nextSources.every((item, index) => item === prevSources[index])
            ) {
                return;
            }
            this.globalEnrichmentState = {
                ...this.globalEnrichmentState,
                biosampleMethodOptions: nextMethods,
                biosampleSourceOptions: nextSources,
            };
        },
    },
});
</script>

<style scoped>
.kp-variant-sifter-workspace {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--cfde-border, #e6e1d6);
    border-radius: 12px;
    background: #ffffff;
    position: relative;
}

.vks-header {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--cfde-border, #e6e1d6);
    background: var(--cfde-header-bg, #f6f5f2);
    z-index: 20;
    border-radius: 11px 11px 0 0;
}

.vks-header.is-pinned {
    border-radius: 0;
    box-shadow: 0 2px 12px rgba(20, 22, 30, 0.12);
}

.vks-header-spacer {
    flex-shrink: 0;
}

.vks-header-start {
    display: flex;
    align-items: center;
    gap: 24px;
    min-width: 0;
    flex-shrink: 0;
    justify-self: start;
}

.vks-header-controls,
.vks-header >>> .vks-viewport-controls {
    grid-column: 3;
    justify-self: end;
    margin-left: 0;
    flex-shrink: 0;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.vks-brand {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-shrink: 0;
}

.vks-mark {
    font-weight: 800;
    letter-spacing: 0.04em;
    color: var(--cfde-orange, #e07b39);
    font-size: 1.05rem;
}

.vks-title {
    font-weight: 600;
    color: var(--cfde-blue, #2c5c97);
    font-size: 1.05rem;
}

.vks-main {
    display: flex;
    align-items: flex-start;
    position: relative;
    background: #ffffff;
    border-radius: 0 0 11px 11px;
}

.vks-drawer-rail-slot {
    flex: 0 0 var(--vks-drawer-tab-width, 30px);
    width: var(--vks-drawer-tab-width, 30px);
    align-self: stretch;
}

.vks-stage {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    background: #ffffff;
}

.vks-session-import-input {
    display: none;
}

.kp-variant-sifter-workspace >>> table.research-data-table {
    border-top: solid 1px #ddd;
    border-right: solid 1px #ddd;
    border-collapse: inherit;
    text-align: center;
    clear: both;
}

.kp-variant-sifter-workspace >>> .research-data-table > thead > tr > th {
    background-color: #eeeeee;
    border: none !important;
    border-left: solid 1px #ddd !important;
    border-bottom: solid 2px #ccc !important;
    font-size: 13px;
    white-space: nowrap;
}

.kp-variant-sifter-workspace >>> .research-data-table > thead > tr > th.sortable-th {
    color: #007bff;
}

.kp-variant-sifter-workspace >>> .research-data-table > thead > tr > th.sortable-th:hover {
    color: #004bcf;
    cursor: pointer;
}

.kp-variant-sifter-workspace >>> .research-data-table td {
    border: none !important;
    border-left: solid 1px #eee !important;
    border-bottom: solid 1px #ddd !important;
    font-size: 13px;
    height: 27px;
    vertical-align: middle;
    position: relative;
}

.kp-variant-sifter-workspace >>> .research-data-table td a {
    color: #007bff;
}

.kp-variant-sifter-workspace >>> .research-data-table td a:hover {
    color: #004bcf;
}

.kp-variant-sifter-workspace >>> .byor-tooltip {
    position: relative;
    overflow: visible;
}

.kp-variant-sifter-workspace >>> .byor-tooltip .tooltiptext {
    visibility: hidden;
    width: 220px;
    background-color: #333;
    color: #fff;
    text-align: left;
    border-radius: 4px;
    padding: 8px 10px;
    position: absolute;
    z-index: 100;
    top: calc(100% + 4px);
    left: 0;
    bottom: auto;
    margin-left: 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    white-space: normal;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.kp-variant-sifter-workspace >>> .byor-tooltip:hover .tooltiptext {
    visibility: visible;
}
</style>
